import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Badge,
  Flex,
  Icon,
  Image,
  IconButton,
  Spinner,
  Tabs,
  Input,
  NativeSelect,
  Textarea,
  SimpleGrid,
  Table,
  Alert,
  Link,
  useDisclosure,
  Checkbox,
  Slider,
  Dialog,
  Portal,
  Separator,
  Field,
  List,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { toast } from '@/components/ui/toaster';
import { TabList, Tab, TabPanels, TabPanel, Thead, Tbody, Tr, Th, Td, SliderTrack, SliderThumb, SliderFilledTrack, SliderRoot, tabsControlledValue, tabsOnValueChange, Switch} from '@/components/ui/chakra-v2-compat';
import { InputGroup, InputLeftElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import { IsolateCheckboxesFromField } from '@/components/ui/multi-checkbox-field';
import {
  FaArrowLeft, FaSave, FaUsers, FaUserPlus, FaMusic, FaMagic, FaWhatsapp, FaFilePdf, FaImage,
  FaLink, FaCopy, FaCheck, FaTimes, FaQuestion, FaBus, FaPlus, FaTrash, FaEdit,
  FaShieldAlt,
  FaExternalLinkAlt, FaEnvelope, FaCalendarAlt, FaMapMarkerAlt, FaClock, FaExclamationTriangle,
  FaFolder, FaEye, FaGripVertical, FaList, FaTh, FaThList, FaSync, FaPhone, FaAddressBook, FaSearch,
  FaInstagram, FaMobileAlt, FaUserCheck,
} from 'react-icons/fa';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import FormacionEditor from './FormacionEditor.jsx';
import FormacionViewer from './FormacionViewer.jsx';
import ActuacionMapPreview from './ActuacionMapPreview.jsx';
import ActuacionDatosFormFields from './ActuacionDatosFormFields.jsx';
import AutobusInfoAccordion from './AutobusInfoAccordion.jsx';
import PasarListaModal from './PasarListaModal.jsx';
import { actuacionesAPI, instrumentosAPI, bandasAPI, contactosAPI, ensayosAPI } from '../services/api';
import { formatMusicoInstrumentosText, primeraFamiliaMusico } from '../utils/musicoInstrumentos';
import { formatActuacionHorario, labelTipoActuacion } from '../constants/tipoActuacion.js';
import { useTiposFormacionCatalogo } from '../hooks/useTiposFormacionCatalogo.js';
import { buildActuacionDatosPayload } from '../utils/actuacionPayload.js';
import { openWhatsappChat } from '../utils/whatsappLink.js';
import {
  drawActuacionCartelRrss,
  defaultRrssCartelOpts,
  RRSS_CARTEL_FORMATOS,
  slugifyFilename,
} from '../utils/actuacionCartelRrss.js';
const ESTADO_LABELS = { BORRADOR: 'Borrador', ENCUESTA_ABIERTA: 'Encuesta abierta', EN_PREPARACION: 'En preparación', LISTA: 'Lista', ARCHIVADA: 'Archivada' };
const ESTADO_COLORS = { BORRADOR: 'gray', ENCUESTA_ABIERTA: 'yellow', EN_PREPARACION: 'blue', LISTA: 'green', ARCHIVADA: 'red' };
const ENSAYO_ESTADO_CONFIG = {
  PROGRAMADO: { label: 'Programado', color: 'blue' },
  REALIZADO: { label: 'Realizado', color: 'green' },
  CANCELADO: { label: 'Cancelado', color: 'red' },
};

function formatEnsayoFechaCorta(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function mergeEnsayosAsociados(act) {
  if (!act) return [];
  const map = new Map();
  for (const ensayo of act.ensayosVinculados || []) {
    map.set(ensayo.id, { ...ensayo, vinculoDirecto: true });
  }
  for (const grupo of act.gruposEnsayo || []) {
    for (const ensayo of grupo.ensayos || []) {
      if (map.has(ensayo.id)) continue;
      map.set(ensayo.id, {
        ...ensayo,
        vinculoDirecto: false,
        grupo: ensayo.grupo || { id: grupo.id, nombre: grupo.nombre, color: grupo.color },
      });
    }
  }
  return [...map.values()].sort((a, b) => {
    const da = a.fecha ? new Date(a.fecha).getTime() : 0;
    const db = b.fecha ? new Date(b.fecha).getTime() : 0;
    if (da !== db) return da - db;
    return String(a.horaInicio || '').localeCompare(String(b.horaInicio || ''), 'es');
  });
}

const ACT_TAB = {
  FICHA: 0,
  ENSAYOS: 1,
  ASISTENCIA: 2,
  REFUERZOS: 3,
  FORMACION: 4,
  REPERTORIO: 5,
};

function EnsayosPreparacionPanel({ ensayosAsociados, cardBg, infoBg, sectionHeaderBg, navigate }) {
  if (!ensayosAsociados.length) {
    return (
      <Card.Root bg={cardBg} shadow="md" rounded="xl">
        <Card.Body textAlign="center" py={10}>
          <Icon as={FaCalendarAlt} boxSize={10} color="gray.300" mb={3} />
          <Heading size="sm" color="gray.500" mb={2}>Sin ensayos de preparación</Heading>
          <Text fontSize="sm" color="gray.400" mb={4} maxW="md" mx="auto">
            Vincula ensayos a esta actuación desde la sección Ensayos, o asócialos a través de un grupo de ensayos.
          </Text>
          <Button size="sm" colorPalette="purple" onClick={() => navigate('/banda/ensayos')}>
            Ir a Ensayos
          </Button>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Card.Root rounded="xl" shadow="sm" overflow="hidden" bg={cardBg}>
      <Box bg={sectionHeaderBg} px={4} py={2}>
        <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
          <HStack gap={2}>
            <Icon as={FaCalendarAlt} color="white" boxSize={4} />
            <Text color="white" fontWeight="700" fontSize="sm">
              Ensayos de preparación
            </Text>
            <Badge bg="whiteAlpha.300" color="white" fontSize="xs" px={2} rounded="full">
              {ensayosAsociados.length}
            </Badge>
          </HStack>
          <Button
            size="xs"
            bg="whiteAlpha.300"
            color="white"
            _hover={{ bg: 'whiteAlpha.500' }}
            onClick={() => navigate('/banda/ensayos', { state: { openEnsayoId: ensayosAsociados[0]?.id } })}
          >
            Ir a Ensayos
          </Button>
        </Flex>
      </Box>
      <Card.Body p={0}>
        <VStack gap={0} align="stretch">{ensayosAsociados.map((ensayo) => {
            const estadoConf = ENSAYO_ESTADO_CONFIG[ensayo.estado] ?? ENSAYO_ESTADO_CONFIG.PROGRAMADO;
            const grupo = ensayo.grupo;
            const grupoColor = grupo?.color ?? '#6B46C1';
            const lugar = (ensayo.lugar || ensayo.sede?.nombre || '').trim();
            return (
              <Flex
                key={ensayo.id}
                align="flex-start"
                px={4}
                py={3}
                gap={3}
                _hover={{ bg: infoBg }}
                transition="background 0.15s"
                cursor="pointer"
                onClick={() => navigate('/banda/ensayos', { state: { openEnsayoId: ensayo.id } })}
              >
                <Box
                  w={8}
                  h={8}
                  rounded="full"
                  bg="purple.100"
                  border="2px solid"
                  borderColor="purple.300"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  fontSize="md"
                  _dark={{ bg: 'purple.900', borderColor: 'purple.600' }}
                >
                  🎵
                </Box>
                <Box flex={1} minW={0}>
                  <HStack gap={2} flexWrap="wrap" mb={1}>
                    <Text fontSize="sm" fontWeight="700" lineClamp={1}>
                      {formatEnsayoFechaCorta(ensayo.fecha)}
                    </Text>
                    <HStack gap={1}>
                      <Icon as={FaClock} color="gray.400" boxSize={3} />
                      <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                        {ensayo.horaInicio}{ensayo.horaFin ? ` – ${ensayo.horaFin}` : ''}
                      </Text>
                    </HStack>
                  </HStack>
                  <HStack gap={2} flexWrap="wrap">
                    <Badge colorPalette={estadoConf.color} fontSize="2xs" variant="subtle">
                      {estadoConf.label}
                    </Badge>
                    {ensayo.vinculoDirecto ? (
                      <Badge colorPalette="orange" fontSize="2xs" variant="subtle">Vinculado</Badge>
                    ) : null}
                    {grupo ? (
                      <HStack gap={1}>
                        <Box w={2} h={2} rounded="full" bg={grupoColor} flexShrink={0} />
                        <Text fontSize="xs" color="gray.500" lineClamp={1}>{grupo.nombre}</Text>
                      </HStack>
                    ) : null}
                    {ensayo.destacado ? (
                      <Badge colorPalette="yellow" fontSize="2xs" variant="subtle">Destacado</Badge>
                    ) : null}
                    {!ensayo.obligatorio ? (
                      <Badge colorPalette="gray" fontSize="2xs" variant="subtle">Opcional</Badge>
                    ) : null}
                    {ensayo.tipo === 'EXTRAORDINARIO' ? (
                      <Badge colorPalette="purple" fontSize="2xs" variant="subtle">Extraordinario</Badge>
                    ) : null}
                  </HStack>
                  {lugar ? (
                    <HStack gap={1} mt={1}>
                      <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3} />
                      <Text fontSize="xs" color="gray.500" lineClamp={1}>{lugar}</Text>
                    </HStack>
                  ) : null}
                </Box>
              </Flex>
            );
          })}</VStack>
      </Card.Body>
    </Card.Root>
  );
}
const FAMILIA_LABELS = { VIENTO_METAL: 'V. Metal', VIENTO_MADERA: 'V. Madera', PERCUSION: 'Percusión', CUERDA: 'Cuerda', VOZ: 'Voz', OTRO: 'Otro' };
const FAMILIA_COLORS = { VIENTO_METAL: 'yellow', VIENTO_MADERA: 'green', PERCUSION: 'red', CUERDA: 'blue', VOZ: 'purple', OTRO: 'gray' };

const PASAR_LISTA_ACTUACION_STATUSES = [
  { key: 'ASISTE', label: 'Asiste', color: 'green', icon: FaCheck },
  { key: 'NO_ASISTE', label: 'No asiste', color: 'red', icon: FaTimes },
  { key: 'PENDIENTE', label: 'Pendiente', color: 'gray', icon: FaQuestion },
  { key: 'JUSTIFICADO', label: 'Justificado', color: 'blue', icon: FaShieldAlt },
];

function respuestaEstadoEffective(r) {
  if (r?.estado) return r.estado;
  if (r?.asiste === true) return 'ASISTE';
  if (r?.asiste === false) return 'NO_ASISTE';
  return 'PENDIENTE';
}

function asisteFromEstado(estado) {
  if (estado === 'ASISTE') return true;
  if (estado === 'NO_ASISTE') return false;
  return null;
}

function escapeHtml(s) {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Origen del backend sin `/api`, para URLs de ficheros servidos en `/uploads/`. */
function getApiOriginForStatic() {
  const base = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const s = String(base).replace(/\/api\/?$/, '');
  return s || 'http://localhost:3000';
}

function resolveCartelUrlForPdf(url) {
  if (url == null || url === '') return '';
  const u = String(url).trim();
  if (/^https?:\/\//i.test(u)) return u;
  const origin = getApiOriginForStatic();
  if (u.startsWith('/')) return `${origin}${u}`;
  return `${origin}/uploads/${u}`;
}

function fetchImageAsDataUrlForPdf(imageUrl) {
  const abs = resolveCartelUrlForPdf(imageUrl);
  // Para exportaciones (PDF/PNG) necesitamos poder leer el blob del avatar/cartel.
  // Si el backend sirve el fichero con sesión/cookie, `omit` lo rompe (se queda en 401 y no se dibuja).
  return fetch(abs, { mode: 'cors', credentials: 'include' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    })
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve(String(r.result || ''));
          r.onerror = () => reject(new Error('FileReader'));
          r.readAsDataURL(blob);
        }),
    );
}

/** El portapapeles del navegador solo admite PNG de forma fiable (JPEG/WebP fallan en Chrome/Edge). */
const CLIPBOARD_IMAGE_MAX_DIM = 1400;

function imgElementToPngBlob(img, maxDim = CLIPBOARD_IMAGE_MAX_DIM) {
  const nw = img.naturalWidth || img.width;
  const nh = img.naturalHeight || img.height;
  if (!nw || !nh) throw new Error('invalid image dimensions');
  const scale = Math.min(1, maxDim / Math.max(nw, nh));
  const w = Math.max(1, Math.round(nw * scale));
  const h = Math.max(1, Math.round(nh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas');
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) => {
    canvas.toBlob((png) => (png ? resolve(png) : reject(new Error('toBlob'))), 'image/png', 0.9);
  });
}

async function urlToPngBlob(url, maxDim = CLIPBOARD_IMAGE_MAX_DIM) {
  const img = new Image();
  img.src = url;
  await img.decode();
  return imgElementToPngBlob(img, maxDim);
}

async function blobToPngBlob(blob, maxDim = CLIPBOARD_IMAGE_MAX_DIM) {
  const url = URL.createObjectURL(blob);
  try {
    return await urlToPngBlob(url, maxDim);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function ensurePngBlob(blob) {
  if (!blob) throw new Error('no blob');
  if (blob.type === 'image/png') return blobToPngBlob(blob);
  return blobToPngBlob(blob);
}

function resolveImgEl(ref) {
  const node = ref?.current;
  if (!node) return null;
  if (node.tagName === 'IMG') return node;
  return node.querySelector?.('img') || null;
}

async function buildPngForClipboard({ imgRef, blob, url }) {
  const img = resolveImgEl(imgRef);
  if (img?.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
    try {
      return await imgElementToPngBlob(img);
    } catch (e) {
      console.warn('imgElementToPngBlob failed', e);
    }
  }
  if (blob) {
    try {
      return await blobToPngBlob(blob);
    } catch (e) {
      console.warn('blobToPngBlob failed', e);
    }
  }
  if (url) {
    return urlToPngBlob(url);
  }
  throw new Error('no image source');
}

async function writePngClipboard(pngBlob) {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    throw new Error('clipboard unsupported');
  }
  // En modales a veces hace falta recuperar el foco para que el gesto de usuario cuente.
  window.focus();

  const attempts = [
    () => navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]),
    () => navigator.clipboard.write([new ClipboardItem({ 'image/png': Promise.resolve(pngBlob) })]),
    () => navigator.clipboard.write([new ClipboardItem({ 'image/png': Promise.resolve(pngBlob.slice(0, pngBlob.size, 'image/png')) })]),
  ];

  let lastErr;
  for (const attempt of attempts) {
    try {
      await attempt();
      return;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('clipboard write failed');
}

async function copyPreviewImgToClipboard({ imgRef, blob, url }) {
  const png = await buildPngForClipboard({ imgRef, blob, url });
  await writePngClipboard(png);
}

async function copyPngToClipboard(pngBlobOrUrl) {
  const png = typeof pngBlobOrUrl === 'string'
    ? await urlToPngBlob(pngBlobOrUrl)
    : await ensurePngBlob(pngBlobOrUrl);
  await writePngClipboard(png);
}

async function copyTextAndPngToClipboard(text, pngBlobOrUrl) {
  if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
    throw new Error('clipboard unsupported');
  }
  const png = typeof pngBlobOrUrl === 'string'
    ? await urlToPngBlob(pngBlobOrUrl)
    : await ensurePngBlob(pngBlobOrUrl);
  window.focus();
  await navigator.clipboard.write([
    new ClipboardItem({
      'text/plain': Promise.resolve(new Blob([text], { type: 'text/plain' })),
      'image/png': Promise.resolve(png),
    }),
  ]);
}

function downloadBlob(blob, filename) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

/** Enlace de búsqueda en Google Maps (coordenadas o texto de dirección). */
function buildGoogleMapsSearchUrl({ lat, lng, address }) {
  const latNum = lat != null ? Number(lat) : null;
  const lngNum = lng != null ? Number(lng) : null;
  const hasCoords = latNum != null && lngNum != null && !Number.isNaN(latNum) && !Number.isNaN(lngNum);
  const addr = (address || '').trim();
  if (hasCoords) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latNum},${lngNum}`)}`;
  }
  if (addr) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  }
  return null;
}

function appendUbicacionWaLines(lines, emojiLabel, ubicacionText, lat, lng) {
  const text = (ubicacionText || '').trim();
  if (!text) return;
  lines.push(`${emojiLabel} ${text}`);
  const mapsUrl = buildGoogleMapsSearchUrl({ lat, lng, address: text });
  if (mapsUrl) lines.push(`🗺️ ${mapsUrl}`);
}

/** Grupo legacy: una carpeta con array de obras (se aplana al cargar). */
function isCarpetaPartiturasGroup(r) {
  return r && r.tipo === 'carpeta_partituras' && r.nombreCarpeta && Array.isArray(r.obras);
}

function isObraCarpetaLine(r) {
  return r && r.tipo === 'obra_carpeta' && r.titulo;
}

function repLineId() {
  return globalThis.crypto?.randomUUID?.() || `rep-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Lista plana: manuales `{ id, titulo, autor?, enlace? }` y obras de carpeta `{ id, tipo:'obra_carpeta', titulo, autor?, nombreCarpeta, carpetaId }`. */
function normalizeRepertorio(rep) {
  const out = [];
  for (const r of rep || []) {
    if (isCarpetaPartiturasGroup(r)) {
      const sorted = [...r.obras].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      for (const row of sorted) {
        const tit = row.obra?.titulo ?? row.titulo ?? 'Sin título';
        const aut = row.obra?.autor ?? row.autor ?? null;
        const enl = row.obra?.enlace ?? row.enlace ?? null;
        const oid = row.obra?.id ?? row.obraId ?? null;
        out.push({
          id: repLineId(),
          tipo: 'obra_carpeta',
          titulo: tit,
          autor: aut,
          nombreCarpeta: r.nombreCarpeta,
          carpetaId: r.carpetaId,
          ...(oid ? { obraId: oid } : {}),
          ...(enl ? { enlace: enl } : {}),
        });
      }
    } else if (isObraCarpetaLine(r)) {
      out.push({
        ...r,
        id: r.id || repLineId(),
        titulo: r.titulo,
        autor: r.autor ?? null,
        nombreCarpeta: r.nombreCarpeta,
        carpetaId: r.carpetaId,
      });
    } else if (r?.titulo) {
      const manual = {
        id: r.id || repLineId(),
        titulo: r.titulo,
        ...(r.obraId ? { obraId: r.obraId } : {}),
        ...(r.enlace ? { enlace: r.enlace } : {}),
      };
      const aut = r.autor != null && String(r.autor).trim() ? String(r.autor).trim() : null;
      if (aut) manual.autor = aut;
      out.push(manual);
    }
  }
  return out;
}

function repertorioNeedsNormalize(rep) {
  const arr = rep || [];
  if (arr.some(isCarpetaPartiturasGroup)) return true;
  return arr.some((x) => x && (x.titulo || x.tipo === 'obra_carpeta') && !x.id);
}

function carpetaIdsEnRepertorio(repertorio) {
  const flat = normalizeRepertorio(repertorio || []);
  return new Set(
    flat.filter(isObraCarpetaLine).map((r) => r.carpetaId).filter(Boolean),
  );
}

function buildRepertorioHtml(repertorio) {
  const items = normalizeRepertorio(repertorio || []);
  if (!items.length) return '';
  const parts = items.map((r) => {
    if (isObraCarpetaLine(r)) {
      // Mismo orden que en WhatsApp (buildRepertorioWhatsappLines): título, autor, carpeta.
      const sub = r.autor ? ` <span style="color:#666">(${escapeHtml(r.autor)})</span>` : '';
      const folder = r.nombreCarpeta
        ? ` <span style="display:inline-block;margin-left:6px;padding:2px 8px;border-radius:10px;background:#ede9fe;color:#553c9a;font-size:11px;font-weight:600">${escapeHtml(r.nombreCarpeta)}</span>`
        : '';
      return `<li><strong>${escapeHtml(r.titulo || '')}</strong>${sub}${folder}</li>`;
    }
    const sub = r.autor ? ` <span style="color:#666">(${escapeHtml(r.autor)})</span>` : '';
    const t = escapeHtml(r.titulo || '');
    const e = r.enlace ? ` — <a href="${escapeHtml(r.enlace)}">${escapeHtml(r.enlace)}</a>` : '';
    return `<li><strong>${t}</strong>${sub}${e}</li>`;
  });
  return `<h2>Repertorio</h2><ol style="padding-left:20px">${parts.join('')}</ol>`;
}

function buildRepertorioCompartidoHtml(share, plainPwd, incluirEnExport = true) {
  if (!incluirEnExport) return '';
  if (!share?.shareUrl) {
    if (share?.hasPassword) {
      return '<h2>Partituras (repertorio compartido Cadencya)</h2><p><em>Enlace disponible en la app Cadencya (repertorio de partituras).</em></p>';
    }
    return '';
  }
  let h = `<h2>Partituras (repertorio compartido Cadencya)</h2><p><a href="${escapeHtml(share.shareUrl)}">${escapeHtml(share.shareUrl)}</a></p>`;
  if (plainPwd) {
    h += `<p><strong>Contraseña del enlace:</strong> ${escapeHtml(plainPwd)}</p>`;
  } else if (share.canRevealSharePassword) {
    h +=
      '<p><em>La contraseña del enlace puedes verla en Cadencya (Repertorio partituras → Ver contraseña) con tu contraseña de cuenta.</em></p>';
  }
  return h;
}

function buildRepertorioWhatsappLines(repertorio) {
  const items = normalizeRepertorio(repertorio || []);
  if (!items.length) return '';
  const lines = [];
  lines.push('🎼 *Repertorio:*');
  items.forEach((r, idx) => {
    if (isObraCarpetaLine(r)) {
      // Mismo orden que en PDF (buildRepertorioHtml): título, autor, etiqueta de carpeta.
      const au = r.autor ? ` (${r.autor})` : '';
      const carp = r.nombreCarpeta ? ` [${r.nombreCarpeta}]` : '';
      lines.push(`${idx + 1}. ${r.titulo || ''}${au}${carp}`);
    } else {
      const au = r.autor ? ` (${r.autor})` : '';
      lines.push(`${idx + 1}. ${r.titulo || ''}${au}${r.enlace ? `\n   ${r.enlace}` : ''}`);
    }
  });
  return `${lines.join('\n')}\n`;
}

function buildRepertorioCompartidoWhatsapp(share, plainPwd, incluirEnExport = true) {
  if (!incluirEnExport) return '';
  if (!share?.shareUrl) {
    if (share?.hasPassword) return '📄 *Partituras Cadencya:* enlace en la app (repertorio compartido).\n';
    return '';
  }
  let t = `📄 *Partituras (repertorio compartido Cadencya):*\n${share.shareUrl}\n`;
  if (plainPwd) t += `🔑 *Contraseña del enlace:* ${plainPwd}\n`;
  else if (share.canRevealSharePassword) {
    t += '_(Contraseña del enlace: consúltala en Cadencya con tu clave de cuenta.)_\n';
  }
  return t;
}

// ── Formation canvas helpers – WhatsApp image sharing ────────────────────────
function _waRRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function _waDrawCell(ctx, x, y, w, h, musico, bg) {
  ctx.fillStyle = bg;
  _waRRect(ctx, x, y, w, h, 4);
  ctx.fill();
  if (musico) {
    const fs = Math.max(7, Math.min(10, h / 3.5));
    ctx.fillStyle = '#fff';
    ctx.font = `600 ${fs}px "Segoe UI",sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    const ap = musico.apellidos ? ` ${musico.apellidos[0]}.` : '';
    ctx.fillText(`${musico.nombre || ''}${ap}`, x + w / 2, y + h * 0.55);
    const ia = musico.instrumentosAsignados?.[0];
    const inm = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '';
    if (inm) {
      ctx.font = `${Math.max(6, fs - 2)}px "Segoe UI",sans-serif`;
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText(inm.length > 10 ? inm.slice(0, 9) + '\u2026' : inm, x + w / 2, y + h * 0.8);
    }
  }
}

function generateFormacionCanvas(fd) {
  if (!fd) return null;
  const isGrid = (fd.modo || 'GRID') === 'GRID';
  const isCalle = fd.subtipoLibre === 'calle';
  const W = 700, H = isGrid ? 420 : 480;
  const DPR = typeof window !== 'undefined' && window.devicePixelRatio >= 2 ? 2 : 1;
  const canvas = document.createElement('canvas');
  canvas.width = W * DPR;
  canvas.height = H * DPR;
  const ctx = canvas.getContext('2d');
  ctx.scale(DPR, DPR);

  // Background
  if (!isGrid && !isCalle) {
    ctx.fillStyle = '#13103A';
  } else if (!isGrid) {
    ctx.fillStyle = '#F5F0E8';
  } else {
    const bgGrad = ctx.createLinearGradient(0, 0, W, H);
    bgGrad.addColorStop(0, '#F9F5FF');
    bgGrad.addColorStop(1, '#EDE9FE');
    ctx.fillStyle = bgGrad;
  }
  ctx.fillRect(0, 0, W, H);

  if (!isGrid) {
    if (isCalle) {
      const sw = H * 0.2;
      ctx.fillStyle = '#E2D9C8'; ctx.fillRect(0, 0, W, sw); ctx.fillRect(0, H - sw, W, sw);
      ctx.fillStyle = '#D4CCBC'; ctx.fillRect(0, sw, W, H - sw * 2);
      ctx.strokeStyle = '#9B8F6F'; ctx.lineWidth = 1.5;
      [sw, H - sw].forEach(yy => { ctx.beginPath(); ctx.moveTo(0, yy); ctx.lineTo(W, yy); ctx.stroke(); });
      ctx.strokeStyle = '#F6D860'; ctx.lineWidth = 2; ctx.setLineDash([20, 12]);
      ctx.beginPath(); ctx.moveTo(0, H / 2); ctx.lineTo(W, H / 2); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#F6D860';
      const ax = W - 50;
      ctx.beginPath(); ctx.moveTo(ax + 20, H / 2); ctx.lineTo(ax, H / 2 - 10);
      ctx.lineTo(ax + 5, H / 2); ctx.lineTo(ax, H / 2 + 10); ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#9B8F6F'; ctx.font = '9px "Segoe UI",sans-serif';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText('ACERA', 10, sw / 2); ctx.fillText('ACERA', 10, H - sw / 2);
    } else {
      const cx = W / 2, cy = H * 0.82;
      [H * 0.82, H * 0.66, H * 0.5].forEach((r, i) => {
        ctx.strokeStyle = ['#553C9A', '#6B46C1', '#9F7AEA'][i]; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(cx, cy, r, Math.PI, 0); ctx.stroke();
      });
      const sr = H * 0.25;
      const sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, sr);
      sg.addColorStop(0, '#3D2A8A'); sg.addColorStop(1, '#2D1B69');
      ctx.fillStyle = sg; ctx.beginPath(); ctx.arc(cx, cy, sr, Math.PI, 0); ctx.fill();
      ctx.strokeStyle = '#B794F4'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(cx, cy, sr, Math.PI, 0); ctx.stroke();
      ctx.fillStyle = '#E9D8FD'; ctx.font = 'bold 11px "Segoe UI",sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('ESCENARIO', cx, cy - sr * 0.45);
    }
  }

  // Title bar
  ctx.fillStyle = isGrid ? '#6B46C1' : 'rgba(107,70,193,0.92)';
  ctx.fillRect(0, 0, W, 26);
  ctx.fillStyle = '#fff'; ctx.font = '600 11px "Segoe UI",sans-serif';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText(fd.nombre || 'Formaci\xF3n', W / 2, 13);

  if (isGrid) {
    const GAP = 3, PAD = 14;
    const cols = fd.columnas || 6, filas = fd.filas || 4;
    let xtraBefore = 0, xtraAfter = 0;
    if (fd.tieneFilaExtra) {
      if (fd.filaExtraPos === 'ANTES_DIRECTOR' || fd.filaExtraPos === 'ANTES') xtraBefore++;
      else xtraAfter++;
    }
    if (fd.tieneDirector) xtraBefore++;
    const totalR = filas + xtraBefore + xtraAfter;
    const cW = Math.min(75, Math.floor((W - PAD * 2 - GAP * (cols - 1)) / cols));
    const cH = Math.min(46, Math.floor((H - 30 - PAD * 2 - GAP * (totalR - 1)) / totalR));
    const gW = cols * (cW + GAP) - GAP;
    const sX = (W - gW) / 2, sY = 30 + PAD;
    const pm = {};
    (fd.posiciones || []).forEach(p => {
      if (p.esDirector) pm['dir'] = p;
      else if (p.esFilaExtra) pm[`ex_${p.columna ?? 0}`] = p;
      else pm[`${p.fila}_${p.columna}`] = p;
    });
    let ri = 0;
    if (fd.tieneFilaExtra && (fd.filaExtraPos === 'ANTES_DIRECTOR' || fd.filaExtraPos === 'ANTES')) {
      const nc = fd.filaExtraCeldas || 3, ew = nc * (cW + GAP) - GAP, ex = (W - ew) / 2;
      for (let c = 0; c < nc; c++) {
        const p = pm[`ex_${c}`];
        _waDrawCell(ctx, ex + c * (cW + GAP), sY + ri * (cH + GAP), cW, cH, p?.musico, p?.musico ? '#B7791F' : '#F6E05E');
      }
      ri++;
    }
    if (fd.tieneDirector) {
      const dp = pm['dir'], dx = (W - cW) / 2, dy = sY + ri * (cH + GAP);
      ctx.fillStyle = '#D69E2E'; _waRRect(ctx, dx, dy, cW, cH, 4); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.font = '600 9px "Segoe UI",sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(dp?.musico
        ? `${dp.musico.nombre || ''}${dp.musico.apellidos ? ` ${dp.musico.apellidos[0]}.` : ''}`
        : 'Director', dx + cW / 2, dy + cH / 2);
      ri++;
    }
    for (let r = 0; r < filas; r++) {
      for (let c = 0; c < cols; c++) {
        const p = pm[`${r}_${c}`];
        const px2 = sX + c * (cW + GAP), py2 = sY + ri * (cH + GAP);
        if (p?.musico) {
          _waDrawCell(ctx, px2, py2, cW, cH, p.musico, '#6B46C1');
        } else {
          ctx.strokeStyle = '#C4B5FD'; ctx.lineWidth = 1;
          _waRRect(ctx, px2 + 0.5, py2 + 0.5, cW - 1, cH - 1, 4); ctx.stroke();
        }
      }
      ri++;
    }
    if (fd.tieneFilaExtra && fd.filaExtraPos !== 'ANTES_DIRECTOR' && fd.filaExtraPos !== 'ANTES') {
      const nc = fd.filaExtraCeldas || 3, ew = nc * (cW + GAP) - GAP, ex = (W - ew) / 2;
      for (let c = 0; c < nc; c++) {
        const p = pm[`ex_${c}`];
        _waDrawCell(ctx, ex + c * (cW + GAP), sY + ri * (cH + GAP), cW, cH, p?.musico, p?.musico ? '#B7791F' : '#F6E05E');
      }
    }
  } else {
    (fd.posiciones || []).forEach(p => {
      if (!p.musico) return;
      const px2 = (p.x / 100) * W, py2 = (p.y / 100) * H, R = 17;
      ctx.fillStyle = isCalle ? '#6B46C1' : 'rgba(107,70,193,0.88)';
      ctx.beginPath(); ctx.arc(px2, py2, R, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#C4B5FD'; ctx.lineWidth = 1.5; ctx.stroke();
      const ap = p.musico.apellidos ? ` ${p.musico.apellidos[0]}.` : '';
      ctx.fillStyle = '#fff'; ctx.font = 'bold 7px "Segoe UI",sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`${p.musico.nombre || ''}${ap}`, px2, py2 - 2);
      const ia = p.musico.instrumentosAsignados?.[0];
      const inm = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '';
      if (inm) {
        ctx.font = '6px "Segoe UI",sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fillText(inm.length > 8 ? inm.slice(0, 7) + '\u2026' : inm, px2, py2 + 6);
      }
    });
  }

  return canvas;
}

function SortableRepertorioRow({ item, onRemove, readOnly }) {
  const id = item.id;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: id || `missing-${item.titulo}`,
    disabled: !id || readOnly,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
  };
  const isObra = isObraCarpetaLine(item);
  return (
    <Flex
      ref={setNodeRef}
      style={style}
      align="flex-start"
      gap={2}
      py={2}
      px={3}
      rounded="lg"
      borderWidth="1px"
      borderColor="gray.100"
      bg="gray.50"
      shadow={isDragging ? 'md' : 'none'}
    >
      {!readOnly ? (
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Arrastrar para reordenar"
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
          mt={0.5}
          flexShrink={0}
          {...attributes}
          {...listeners}><FaGripVertical /></IconButton>
      ) : (
        <Box w="32px" flexShrink={0} />
      )}
      <Box minW={0} flex={1}>
        <HStack align="flex-start" gap={2} flexWrap="wrap">
          <Text fontSize="sm" fontWeight="600" lineClamp={3} flex={1} minW={0}>
            {item.titulo}
          </Text>
          {isObra && item.nombreCarpeta ? (
            <Badge colorPalette="purple" flexShrink={0} fontSize="10px" borderRadius="md" display="inline-flex" alignItems="center" gap="4px">
              <Icon as={FaFolder} boxSize="9px" />
              {item.nombreCarpeta}
            </Badge>
          ) : null}
        </HStack>
        {item.autor ? (
          <Text fontSize="xs" color="gray.600" mt={1}>
            {item.autor}
          </Text>
        ) : null}
        {item.enlace ? (
          <Link
            href={item.enlace}
            fontSize="xs"
            color="purple.500"
            display="block"
            mt={1}
            lineClamp={1}
            target='_blank'
            rel='noopener noreferrer'>
            {item.enlace}
          </Link>
        ) : null}
      </Box>
      {!readOnly ? (
        <IconButton
          size="xs"
          variant="ghost"
          colorPalette="red"
          onClick={() => id && onRemove(id)}
          aria-label="Eliminar"
          flexShrink={0}><FaTrash /></IconButton>
      ) : null}
    </Flex>
  );
}

export default function ActuacionDetalle({
  actuacionId,
  onBack,
  toast,
  plantillas,
  hayMusicos = true,
  readOnly = false,
}) {
  const isReadOnly = Boolean(readOnly);
  const navigate = useNavigate();
  const { tipos: tiposFormacionCatalogo } = useTiposFormacionCatalogo();
  const sinPlantillasFormacion = !(plantillas || []).length;
  const sinMusicosEnBanda = !hayMusicos;
  const [act, setAct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editingRespuestaId, setEditingRespuestaId] = useState(null);
  const [editingRespuestaValues, setEditingRespuestaValues] = useState({ asiste: null, estado: null, necesitaAutobus: null });
  const [savingRespuesta, setSavingRespuesta] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [instrumentos, setInstrumentos] = useState([]);
  const { open: isRefOpen, onOpen: onRefOpen, onClose: onRefClose } = useDisclosure();
  const { open: isWaOpen, onOpen: onWaOpen, onClose: onWaClose } = useDisclosure();
  const { open: isPdfOpen, onOpen: onPdfOpen, onClose: onPdfClose } = useDisclosure();
  const { open: isRrssCartelOpen, onOpen: onRrssCartelOpen, onClose: onRrssCartelClose } = useDisclosure();
  const { open: isPasarListaOpen, onOpen: onPasarListaOpen, onClose: onPasarListaClose } = useDisclosure();
  const [rrssCartelStep, setRrssCartelStep] = useState('formato'); // 'formato' | 'secciones' | 'colores' | 'preview'
  const [rrssCartelFormato, setRrssCartelFormato] = useState('feed');
  const [rrssCartelOpts, setRrssCartelOpts] = useState(() => defaultRrssCartelOpts());
  const [rrssCartelGen, setRrssCartelGen] = useState(false);
  const [rrssCartelPreviewUrl, setRrssCartelPreviewUrl] = useState('');
  const [pdfSecciones, setPdfSecciones] = useState({
    cartel: true,
    infoGeneral: true,
    autobus: true,
    observaciones: true,
    repertorio: true,
    partiturasCompartidas: true,
    enlaceManual: true,
    formacion: false,
    musicosConfirmados: false,
    noAsisten: false,
    refuerzos: false,
  });
  const [pdfGenerando, setPdfGenerando] = useState(false);
  const [waTarget, setWaTarget] = useState(null); // { contacto, ref, mensaje }
  const [waMensaje, setWaMensaje] = useState('');
  // ── Share actuación (WhatsApp multi-step) ─────────────────────────────────
  const { open: isShareWaOpen, onOpen: onShareWaOpen, onClose: onShareWaClose } = useDisclosure();
  const [shareWaStep, setShareWaStep] = useState('formato'); // 'formato' | 'secciones' | 'preview'
  const [shareWaFormato, setShareWaFormato] = useState(null); // 'texto' | 'pdf'
  const [shareWaSecciones, setShareWaSecciones] = useState({
    infoBasica: true, horarios: true, autobus: true, observaciones: false, cartel: false,
    repertorio: true, partiturasEnlace: true, asistencia: false, formacion: false, refuerzos: false,
  });
  const [shareWaMensaje, setShareWaMensaje] = useState('');
  const [shareWaFormacionImgUrl, setShareWaFormacionImgUrl] = useState(null);
  const [shareWaFormacionBlob, setShareWaFormacionBlob] = useState(null);
  const [shareWaCartelImgUrl, setShareWaCartelImgUrl] = useState(null);
  const [shareWaCartelBlob, setShareWaCartelBlob] = useState(null);
  const [shareWaCopied, setShareWaCopied] = useState(false);
  const [shareWaCopyNotice, setShareWaCopyNotice] = useState(null); // { status, title, description }
  const [shareWaGenerando, setShareWaGenerando] = useState(false);
  const shareWaCartelImgRef = useRef(null);
  const shareWaFormacionImgRef = useRef(null);
  const closeShareWaModal = useCallback(() => {
    if (shareWaCartelImgUrl) URL.revokeObjectURL(shareWaCartelImgUrl);
    setShareWaCartelImgUrl(null);
    setShareWaCartelBlob(null);
    setShareWaCopied(false);
    setShareWaCopyNotice(null);
    onShareWaClose();
  }, [shareWaCartelImgUrl, onShareWaClose]);
  const [refForm, setRefForm] = useState({ nombre: '', instrumentoId: '', telefono: '', email: '', notas: '', confirmado: false });
  // Posicion vacante que se quiere cubrir (interno, no visible en el formulario)
  const [refPosicionVacanteId, setRefPosicionVacanteId] = useState('');
  // Agenda de refuerzos para selección
  const [agendaRefuerzos, setAgendaRefuerzos] = useState([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [refBusqueda, setRefBusqueda] = useState('');
  const [refModo, setRefModo] = useState('seleccionar'); // 'seleccionar' | 'nuevo'
  const [refContactoSeleccionado, setRefContactoSeleccionado] = useState(null);
  const [repItem, setRepItem] = useState({ titulo: '', autor: '', enlace: '' });
  const [repertorioShare, setRepertorioShare] = useState(null);
  const [sharePwdPlain, setSharePwdPlain] = useState('');
  /** Si es false, PDF y WhatsApp no incluyen enlace ni contraseña del repertorio compartido Cadencya. */
  const [incluirRepertorioCompartidoEnExport, setIncluirRepertorioCompartidoEnExport] = useState(true);
  const [revealAccountPwd, setRevealAccountPwd] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const [carpetasPartituras, setCarpetasPartituras] = useState([]);
  const [carpetasLoading, setCarpetasLoading] = useState(false);
  const [carpetaIdsSeleccion, setCarpetaIdsSeleccion] = useState([]);
  const { open: isCarpetasOpen, onOpen: onCarpetasOpen, onClose: onCarpetasClose } = useDisclosure();
  const { open: isImportObraOpen, onOpen: onImportObraOpen, onClose: onImportObraClose } = useDisclosure();
  const { open: isClearRepertorioOpen, onOpen: onClearRepertorioOpen, onClose: onClearRepertorioClose } = useDisclosure();
  const cancelClearRepertorioRef = useRef(null);
  const [obrasArchivo, setObrasArchivo] = useState([]);
  const [obrasArchivoLoading, setObrasArchivoLoading] = useState(false);
  const [obraArchivoQuery, setObraArchivoQuery] = useState('');
  const [selectedObraArchivoId, setSelectedObraArchivoId] = useState('');
  const { open: isRevealPwdOpen, onOpen: onRevealPwdOpen, onClose: onRevealPwdClose } = useDisclosure();
  const { open: isCartelOpen, onOpen: onCartelOpen, onClose: onCartelClose } = useDisclosure();
  /** Índice de pestaña principal (0=Ficha … 5=Repertorio); controlado para no saltar al refrescar tras guardar. */
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const cardBg = useColorModeValue('white', 'gray.800');
  const infoBg = useColorModeValue('purple.50', 'purple.900');
  const sectionHeaderBg = useColorModeValue('purple.500', 'purple.700');
  const ubicSuggestBorder = useColorModeValue('gray.200', 'gray.600');
  const [ubicLlegadaDistinta, setUbicLlegadaDistinta] = useState(false);
  /** Incrementar al pulsar "Editar" para reiniciar flags de autocompletado de ubicación en el formulario compartido. */
  const [datosFormSearchResetKey, setDatosFormSearchResetKey] = useState(0);
  const cartelInputRef = useRef(null);
  const [cartelUploading, setCartelUploading] = useState(false);

  // ── Filtros y ordenación de la tabla de asistencia ─────────────────────────
  const [filtroNombre,     setFiltroNombre]     = useState('');
  const [filtroFamilia,    setFiltroFamilia]    = useState('');
  const [filtroAsistencia, setFiltroAsistencia] = useState('');
  const [filtroAutobus,    setFiltroAutobus]    = useState('');
  const [ordenAsistencia,  setOrdenAsistencia]  = useState('nombre');

  // ── Baja asistencia a ensayos vinculados ─────────────────────────────────
  const [bajaAsistenciaIds,    setBajaAsistenciaIds]    = useState([]);
  const [bajaAsistenciaDetalle, setBajaAsistenciaDetalle] = useState([]);

  // ── Pestaña Refuerzos ──────────────────────────────────────────────────────
  const [vacantes, setVacantes] = useState(null);
  const [vacantesLoading, setVacantesLoading] = useState(false);
  const [vacantesVista, setVacantesVista] = useState('mosaico'); // 'lista' | 'mosaico'

  // ── Pestaña Formación ──────────────────────────────────────────────────────
  const [formacionData, setFormacionData] = useState(null);
  const [formacionLoading, setFormacionLoading] = useState(false);
  const [savingPlantilla, setSavingPlantilla] = useState(false);

  const loadBajaAsistencia = useCallback(async () => {
    if (!actuacionId) return;
    try {
      const res = await ensayosAPI.getBajaAsistencia(actuacionId);
      setBajaAsistenciaIds(res.data?.musicoIds ?? []);
      setBajaAsistenciaDetalle(res.data?.detalle ?? []);
    } catch {
      // No hay ensayos vinculados o el endpoint no está disponible: silencioso
    }
  }, [actuacionId]);

  const loadFormacion = useCallback(async () => {
    if (!actuacionId) return;
    setFormacionLoading(true);
    try {
      const res = await actuacionesAPI.getFormacionConAsistencia(actuacionId);
      setFormacionData(res.data);
    } catch {
      setFormacionData(null);
    } finally {
      setFormacionLoading(false);
    }
  }, [actuacionId]);

  const loadVacantes = useCallback(async () => {
    if (!actuacionId) return;
    setVacantesLoading(true);
    try {
      const res = await actuacionesAPI.getVacantes(actuacionId);
      setVacantes(res.data);
    } catch {
      setVacantes(null);
    } finally {
      setVacantesLoading(false);
    }
  }, [actuacionId]);

  const handleSelectPlantilla = useCallback(async (plantillaId) => {
    setSavingPlantilla(true);
    try {
      await actuacionesAPI.update(actuacionId, { plantillaId: plantillaId || null });
      setAct((prev) => ({ ...prev, plantillaId: plantillaId || null }));
      if (plantillaId) {
        const res = await actuacionesAPI.getFormacionConAsistencia(actuacionId);
        setFormacionData(res.data);
      } else {
        setFormacionData(null);
      }
      toast({ title: 'Formación actualizada', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'Error al guardar formación', status: 'error' });
    } finally {
      setSavingPlantilla(false);
    }
  }, [actuacionId, toast]);

  const baseUrl = window.location.origin;

  // ── Lista filtrada y ordenada de respuestas de asistencia ──────────────────
  const FAMILIA_SORT_ORDER = { VIENTO_METAL: 0, VIENTO_MADERA: 1, PERCUSION: 2, CUERDA: 3, VOZ: 4, OTRO: 5 };
  const ASISTENCIA_SORT_VAL = { true: 0, null: 1, false: 2 };

  const respuestasFiltradas = useMemo(() => {
    const all = act?.encuesta?.respuestas || [];
    let list = all.filter(r => {
      const nombreCompleto = `${r.musico.nombre} ${r.musico.apellidos || ''}`.toLowerCase();
      if (filtroNombre && !nombreCompleto.includes(filtroNombre.toLowerCase())) return false;
      if (filtroFamilia && primeraFamiliaMusico(r.musico) !== filtroFamilia) return false;
      if (filtroAsistencia === 'si'  && r.asiste !== true)  return false;
      if (filtroAsistencia === 'no'  && r.asiste !== false) return false;
      if (filtroAsistencia === 'sin' && r.asiste !== null)  return false;
      if (filtroAutobus === 'si'  && r.necesitaAutobus !== true)  return false;
      if (filtroAutobus === 'no'  && r.necesitaAutobus !== false) return false;
      if (filtroAutobus === 'sin' && r.necesitaAutobus !== null)  return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (ordenAsistencia === 'familia') {
        const fa = FAMILIA_SORT_ORDER[primeraFamiliaMusico(a.musico) || 'OTRO'] ?? 5;
        const fb = FAMILIA_SORT_ORDER[primeraFamiliaMusico(b.musico) || 'OTRO'] ?? 5;
        if (fa !== fb) return fa - fb;
      }
      if (ordenAsistencia === 'asistencia') {
        const sa = ASISTENCIA_SORT_VAL[String(a.asiste)] ?? 1;
        const sb = ASISTENCIA_SORT_VAL[String(b.asiste)] ?? 1;
        if (sa !== sb) return sa - sb;
      }
      return `${a.musico.nombre} ${a.musico.apellidos || ''}`.localeCompare(
        `${b.musico.nombre} ${b.musico.apellidos || ''}`, 'es',
      );
    });
    return list;
  }, [act?.encuesta?.respuestas, filtroNombre, filtroFamilia, filtroAsistencia, filtroAutobus, ordenAsistencia]);

  useEffect(() => {
    setMainTabIndex(0);
  }, [actuacionId]);

  useEffect(() => {
    if (isReadOnly) setEditMode(false);
  }, [isReadOnly, actuacionId]);

  const fetchActuacion = useCallback(async () => {
    try {
      setLoading(true);
      const [res, instRes, shareRes] = await Promise.all([
        actuacionesAPI.getById(actuacionId),
        instrumentosAPI.getAll(),
        bandasAPI.getRepertorioShare().catch(() => ({ data: null })),
      ]);
      let actData = res.data;
      const rawRep = actData.repertorio;
      if (repertorioNeedsNormalize(rawRep) && !isReadOnly) {
        try {
          const flat = normalizeRepertorio(rawRep || []);
          await actuacionesAPI.update(actuacionId, { repertorio: flat });
          const res2 = await actuacionesAPI.getById(actuacionId);
          actData = {
            ...res2.data,
            repertorio: normalizeRepertorio(res2.data.repertorio || []),
          };
        } catch (e) {
          console.error(e);
          toast({
            title: 'No se pudo guardar el repertorio migrado',
            description: 'Se muestra en lista plana; revisa permisos o inténtalo de nuevo.',
            status: 'warning',
            duration: 6000,
          });
          actData = { ...actData, repertorio: normalizeRepertorio(rawRep || []) };
        }
      } else {
        actData = { ...actData, repertorio: normalizeRepertorio(actData.repertorio || []) };
      }
      setAct(actData);
      setInstrumentos(instRes.data);
      setRepertorioShare(shareRes?.data || null);
      setSharePwdPlain('');
      setEditForm({
        nombre: actData.nombre,
        descripcion: actData.descripcion || '',
        fecha: actData.fecha ? actData.fecha.split('T')[0] : '',
        horaInicio: actData.horaInicio || '',
        horaFin: actData.horaFin || '',
        ubicacion: actData.ubicacion || '',
        lat: actData.lat != null && !Number.isNaN(Number(actData.lat)) ? Number(actData.lat) : null,
        lng: actData.lng != null && !Number.isNaN(Number(actData.lng)) ? Number(actData.lng) : null,
        ubicacionFin: actData.ubicacionFin || '',
        latFin: actData.latFin != null && !Number.isNaN(Number(actData.latFin)) ? Number(actData.latFin) : null,
        lngFin: actData.lngFin != null && !Number.isNaN(Number(actData.lngFin)) ? Number(actData.lngFin) : null,
        uniforme: actData.uniforme || '',
        tipo: actData.tipo,
        plantillaId: actData.plantillaId || '',
        enlacePartituras: actData.enlacePartituras || '',
        observaciones: actData.observaciones || '',
        necesidadAutobus: actData.necesidadAutobus || false,
        autobusUbicacion: actData.autobusUbicacion || '',
        autobusHoraSalida: actData.autobusHoraSalida || '',
        autobusLat:
          actData.autobusLat != null && !Number.isNaN(Number(actData.autobusLat))
            ? Number(actData.autobusLat)
            : null,
        autobusLng:
          actData.autobusLng != null && !Number.isNaN(Number(actData.autobusLng))
            ? Number(actData.autobusLng)
            : null,
      });
      setUbicLlegadaDistinta(Boolean(actData.ubicacionFin?.trim() || actData.latFin != null));
    } catch (err) { console.error(err); toast({ title: 'Error cargando datos', status: 'error' }); }
    finally { setLoading(false); }
  }, [actuacionId, toast, isReadOnly]);

  useEffect(() => { fetchActuacion(); }, [fetchActuacion]);

  useEffect(() => {
    if (isReadOnly) setShowEditor(false);
  }, [isReadOnly]);

  const handleCartelFileChange = async (e) => {
    if (isReadOnly) return;
    const file = e.target.files?.[0];
    if (cartelInputRef.current) cartelInputRef.current.value = '';
    if (!file) return;
    const ok = /^image\/(jpeg|jpg|png|webp)$/i.test(file.type);
    if (!ok) {
      toast({ title: 'Formato no válido', description: 'Usa JPEG, PNG o WebP.', status: 'warning' });
      return;
    }
    try {
      setCartelUploading(true);
      await actuacionesAPI.uploadCartel(actuacionId, file);
      toast({ title: 'Cartel actualizado', status: 'success' });
      await fetchActuacion();
    } catch (err) {
      toast({
        title: 'No se pudo subir el cartel',
        description: err.response?.data?.message || err.message,
        status: 'error',
      });
    } finally {
      setCartelUploading(false);
    }
  };

  const handleRemoveCartel = async () => {
    if (isReadOnly) return;
    if (!act?.cartelUrl) return;
    if (!window.confirm('¿Quitar el cartel de esta actuación?')) return;
    try {
      setCartelUploading(true);
      await actuacionesAPI.removeCartel(actuacionId);
      toast({ title: 'Cartel eliminado', status: 'success' });
      await fetchActuacion();
    } catch (err) {
      toast({
        title: 'Error al quitar el cartel',
        description: err.response?.data?.message || err.message,
        status: 'error',
      });
    } finally {
      setCartelUploading(false);
    }
  };

  const handleSave = async () => {
    if (isReadOnly) return;
    if (editForm.necesidadAutobus) {
      const busCoordsOk =
        editForm.autobusLat != null &&
        editForm.autobusLng != null &&
        !Number.isNaN(Number(editForm.autobusLat)) &&
        !Number.isNaN(Number(editForm.autobusLng));
      if (!editForm.autobusUbicacion?.trim() || !editForm.autobusHoraSalida?.trim() || !busCoordsOk) {
        toast({
          title: 'Datos del autobús incompletos',
          description:
            'Busca la recogida del autobús, elige una sugerencia del listado (para fijar el punto en el mapa), indica la hora de salida, o desactiva «Preguntar por autobús».',
          status: 'warning',
          duration: 6000,
        });
        return;
      }
    }
    try {
      setSaving(true);
      const payload = buildActuacionDatosPayload(editForm, ubicLlegadaDistinta);
      await actuacionesAPI.update(actuacionId, payload);
      toast({ title: 'Guardado', status: 'success' });
      setEditMode(false);
      fetchActuacion();
    } catch (err) { toast({ title: 'Error', status: 'error' }); }
    finally { setSaving(false); }
  };

  const handleCrearEncuesta = async () => {
    if (isReadOnly) return;
    try {
      await actuacionesAPI.crearEncuesta(actuacionId, { preguntaAutobus: Boolean(editForm.necesidadAutobus) });
      toast({ title: 'Encuesta creada', status: 'success' });
      fetchActuacion();
    } catch (err) { toast({ title: err.response?.data?.message || 'Error', status: 'error' }); }
  };

  const handleCerrarEncuesta = async () => {
    if (isReadOnly) return;
    try {
      await actuacionesAPI.cerrarEncuesta(actuacionId);
      toast({ title: 'Encuesta cerrada', status: 'success' });
      fetchActuacion();
    } catch (err) { toast({ title: 'Error', status: 'error' }); }
  };

  const cerrarRef = React.useRef();
  const { open: isCloseEncuestaOpen, onOpen: onCloseEncuestaOpen, onClose: onCloseEncuestaClose } = useDisclosure();
  const [closeConfirmA, setCloseConfirmA] = useState('');
  const [closeConfirmB, setCloseConfirmB] = useState('');
  const CLOSE_WORD = 'CERRAR';

  const handleCloseEncuestaConfirm = async () => {
    onCloseEncuestaClose();
    setCloseConfirmA('');
    setCloseConfirmB('');
    await handleCerrarEncuesta();
  };

  const reabrirRef = React.useRef();
  const { open: isReopenOpen, onOpen: onReopenOpen, onClose: onReopenClose } = useDisclosure();

  const handleReopenConfirm = async () => {
    onReopenClose();
    if (isReadOnly) return;
    try {
      await actuacionesAPI.reabrirEncuesta(actuacionId);
      toast({ title: 'Encuesta reabierta', description: 'Solo pueden responder quienes aún no lo han hecho.', status: 'success' });
      fetchActuacion();
    } catch (err) { toast({ title: 'Error al reabrir la encuesta', status: 'error' }); }
  };

  const handleWhatsappEncuestaAsistencia = (r) => {
    const url = `${baseUrl}/encuesta/${r.token}`;
    const titulo = (act?.nombre || 'Actuación').trim();
    const msg = `*${titulo}* ¿Vas a venir? Responde al cuestionario, por favor. Será rápido.\n${url}`;
    openWhatsappChat(r.musico?.telefono, msg);
  };

  const handleWhatsappEncuestaGrupo = () => {
    const shareToken = act?.encuesta?.shareToken;
    if (!shareToken) {
      toast({
        title: 'No hay enlace de grupo',
        description: 'Recarga la actuación o vuelve a crear la encuesta.',
        status: 'warning',
      });
      return;
    }
    const url = `${baseUrl}/encuesta-grupo/${shareToken}`;
    const titulo = (act?.nombre || 'Actuación').trim();
    const msg = `*${titulo}* ¿Vas a venir? Identifícate y responde al cuestionario, por favor. Será rápido.\n${url}`;
    openWhatsappChat(null, msg);
  };

  const startEditRespuesta = (r) => {
    setEditingRespuestaId(r.id);
    setEditingRespuestaValues({ asiste: r.asiste, estado: r.estado ?? null, necesitaAutobus: r.necesitaAutobus });
  };

  const cancelEditRespuesta = () => {
    setEditingRespuestaId(null);
    setEditingRespuestaValues({ asiste: null, estado: null, necesitaAutobus: null });
  };

  const saveEditRespuesta = async (respuestaId) => {
    try {
      setSavingRespuesta(true);
      await actuacionesAPI.editarRespuesta(actuacionId, respuestaId, editingRespuestaValues);
      toast({ title: 'Respuesta actualizada', status: 'success', duration: 1500 });
      setEditingRespuestaId(null);
      fetchActuacion();
    } catch (err) {
      toast({ title: 'Error al guardar', status: 'error' });
    } finally {
      setSavingRespuesta(false);
    }
  };

  const handlePasarListaMark = useCallback(async (respuestaId, estado) => {
    const r = act?.encuesta?.respuestas?.find((x) => x.id === respuestaId);
    if (!r) throw new Error('Respuesta no encontrada');
    const asiste = asisteFromEstado(estado);
    const payload = {
      asiste,
      estado,
      necesitaAutobus: r.necesitaAutobus ?? null,
    };
    await actuacionesAPI.editarRespuesta(actuacionId, respuestaId, payload);
    setAct((prev) => {
      if (!prev?.encuesta?.respuestas) return prev;
      return {
        ...prev,
        encuesta: {
          ...prev.encuesta,
          respuestas: prev.encuesta.respuestas.map((x) =>
            x.id === respuestaId ? { ...x, asiste, estado } : x,
          ),
        },
      };
    });
  }, [act?.encuesta?.respuestas, actuacionId]);

  const loadAgendaRefuerzos = useCallback(async () => {
    setAgendaLoading(true);
    try {
      const res = await contactosAPI.getContactos({ seccion: 'REFUERZO' });
      setAgendaRefuerzos(res.data || []);
    } catch { /* silencioso */ } finally { setAgendaLoading(false); }
  }, []);

  const openRefModal = () => {
    setRefModo('seleccionar');
    setRefBusqueda('');
    setRefContactoSeleccionado(null);
    setRefPosicionVacanteId('');
    setRefForm({ nombre: '', instrumentoId: '', telefono: '', email: '', notas: '', confirmado: false });
    loadAgendaRefuerzos();
    onRefOpen();
  };

  const handleAddRefuerzo = async () => {
    if (isReadOnly) return;
    // Construye el marcador de vacante de forma invisible si viene de una necesidad
    const buildNotas = (notasVisibles) => {
      const base = notasVisibles?.trim() || '';
      if (!refPosicionVacanteId) return base || null;
      const marker = `[posicion:${refPosicionVacanteId}]`;
      return base ? `${marker} ${base}` : marker;
    };

    try {
      if (refModo === 'seleccionar' && refContactoSeleccionado) {
        // Si el contacto ya tiene un Refuerzo en esta actuación, actualizarlo en lugar de crear uno nuevo
        const existingRef = (act.refuerzos || []).find(r => r.contactoAgendaId === refContactoSeleccionado.id);
        if (existingRef) {
          await actuacionesAPI.updateRefuerzo(actuacionId, existingRef.id, {
            notas: buildNotas(refForm.notas) ?? existingRef.notas,
            confirmado: refForm.confirmado || existingRef.confirmado,
          });
        } else {
          await actuacionesAPI.addRefuerzo(actuacionId, {
            nombre: [refContactoSeleccionado.nombre, refContactoSeleccionado.apellidos].filter(Boolean).join(' '),
            telefono: refContactoSeleccionado.telefono || null,
            email: refContactoSeleccionado.email || null,
            instrumentoId: refContactoSeleccionado.instrumentoId || null,
            notas: buildNotas(refForm.notas),
            confirmado: refForm.confirmado || false,
            contactoAgendaId: refContactoSeleccionado.id,
          });
        }
      } else {
        if (!refForm.nombre.trim()) return;
        await actuacionesAPI.addRefuerzo(actuacionId, {
          ...refForm,
          instrumentoId: refForm.instrumentoId || null,
          notas: buildNotas(refForm.notas),
        });
      }
      toast({ title: 'Refuerzo añadido', status: 'success' });
      onRefClose();
      setRefPosicionVacanteId('');
      setRefForm({ nombre: '', instrumentoId: '', telefono: '', email: '', notas: '', confirmado: false });
      setRefContactoSeleccionado(null);
      fetchActuacion();
    } catch (err) { toast({ title: 'Error', status: 'error' }); }
  };

  const handleToggleRefuerzo = async (ref) => {
    if (isReadOnly) return;
    try {
      await actuacionesAPI.updateRefuerzo(actuacionId, ref.id, { confirmado: !ref.confirmado });
      fetchActuacion();
    } catch (err) { toast({ title: 'Error', status: 'error' }); }
  };

  const handleDeleteRefuerzo = async (refId) => {
    if (isReadOnly) return;
    try {
      await actuacionesAPI.deleteRefuerzo(actuacionId, refId);
      fetchActuacion();
    } catch (err) { toast({ title: 'Error', status: 'error' }); }
  };

  const handleRegistrarContacto = async (refId) => {
    try {
      const res = await actuacionesAPI.registrarContacto(actuacionId, refId);
      setAct(prev => ({
        ...prev,
        refuerzos: (prev.refuerzos || []).map(r => r.id === refId ? { ...r, ultimoContactoEn: res.data.ultimoContactoEn } : r),
      }));
      toast({ title: 'Contacto registrado', status: 'success', duration: 1500 });
    } catch { toast({ title: 'Error', status: 'error' }); }
  };

  /**
   * Desde la agenda: contactar (WA/llamada). Si no hay Refuerzo para esta
   * actuación lo crea primero, luego registra el contacto.
   */
  const handleContactarDesdeAgenda = async (contacto, existingRef) => {
    try {
      let refId = existingRef?.id;
      if (!refId) {
        const res = await actuacionesAPI.addRefuerzo(actuacionId, {
          nombre: [contacto.nombre, contacto.apellidos].filter(Boolean).join(' '),
          telefono: contacto.telefono || null,
          email: contacto.email || null,
          instrumentoId: contacto.instrumentoId || null,
          confirmado: false,
          contactoAgendaId: contacto.id,
        });
        refId = res.data.id;
        await fetchActuacion();
      }
      await actuacionesAPI.registrarContacto(actuacionId, refId);
      setAct(prev => ({
        ...prev,
        refuerzos: (prev.refuerzos || []).map(r => r.id === refId ? { ...r, ultimoContactoEn: new Date().toISOString() } : r),
      }));
    } catch { /* silencioso: el enlace ya se abrió */ }
  };

  /**
   * Desde la agenda: confirmar/desconfirmar disponibilidad.
   */
  const handleConfirmarRefuerzoAgenda = async (contacto, existingRef) => {
    if (isReadOnly) return;
    try {
      if (!existingRef) {
        await actuacionesAPI.addRefuerzo(actuacionId, {
          nombre: [contacto.nombre, contacto.apellidos].filter(Boolean).join(' '),
          telefono: contacto.telefono || null,
          email: contacto.email || null,
          instrumentoId: contacto.instrumentoId || null,
          confirmado: true,
          noDisponible: false,
          contactoAgendaId: contacto.id,
        });
      } else {
        // Confirmar limpia el "no disponible"; desconfirmar solo quita confirmado
        await actuacionesAPI.updateRefuerzo(actuacionId, existingRef.id, {
          confirmado: !existingRef.confirmado,
          noDisponible: false,
        });
      }
      fetchActuacion();
    } catch { toast({ title: 'Error', status: 'error' }); }
  };

  /** Marca al refuerzo como no disponible para esta actuación */
  const handleMarcarNoDisponible = async (contacto, existingRef) => {
    if (isReadOnly) return;
    try {
      if (!existingRef) {
        // Creamos el registro y lo marcamos no disponible de golpe
        await actuacionesAPI.addRefuerzo(actuacionId, {
          nombre: [contacto.nombre, contacto.apellidos].filter(Boolean).join(' '),
          telefono: contacto.telefono || null,
          email: contacto.email || null,
          instrumentoId: contacto.instrumentoId || null,
          confirmado: false,
          noDisponible: true,
          contactoAgendaId: contacto.id,
        });
      } else {
        await actuacionesAPI.updateRefuerzo(actuacionId, existingRef.id, {
          noDisponible: !existingRef.noDisponible,
          confirmado: false,
        });
      }
      fetchActuacion();
    } catch { toast({ title: 'Error', status: 'error' }); }
  };

  /** Abre el modal de WhatsApp con mensaje pre-rellenado */
  const openWaModal = (contacto, ref) => {
    const nombre = [contacto.nombre, contacto.apellidos].filter(Boolean).join(' ');
    const fecha  = act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }) : '';
    const msg    = `Hola ${nombre},\n\nTe escribimos desde la banda para preguntarte si estarías disponible para la actuación del ${fecha}${act.ubicacion ? ` en ${act.ubicacion}` : ''}.\n\n¿Podrías confirmarnos tu disponibilidad?\n\nGracias.`;
    setWaTarget({ contacto, ref });
    setWaMensaje(msg);
    onWaOpen();
  };

  /** Envía el mensaje por WA y registra el contacto */
  const handleEnviarWa = async () => {
    if (!waTarget) return;
    const { contacto, ref } = waTarget;
    const telefono = contacto.telefono?.replace(/\D/g, '');
    if (telefono) {
      window.open(`https://wa.me/${telefono}?text=${encodeURIComponent(waMensaje)}`, '_blank');
    }
    onWaClose();
    await handleContactarDesdeAgenda(contacto, ref);
  };

  /** Registra manualmente que se ha contactado (sin abrir WA/teléfono) */
  const handleMarcarContactado = async (contacto, existingRef) => {
    try {
      let refId = existingRef?.id;
      if (!refId) {
        const res = await actuacionesAPI.addRefuerzo(actuacionId, {
          nombre: [contacto.nombre, contacto.apellidos].filter(Boolean).join(' '),
          telefono: contacto.telefono || null,
          email: contacto.email || null,
          instrumentoId: contacto.instrumentoId || null,
          confirmado: false,
          contactoAgendaId: contacto.id,
        });
        refId = res.data.id;
        await fetchActuacion();
      }
      await actuacionesAPI.registrarContacto(actuacionId, refId);
      setAct(prev => ({
        ...prev,
        refuerzos: (prev.refuerzos || []).map(r =>
          r.id === refId ? { ...r, ultimoContactoEn: new Date().toISOString() } : r
        ),
      }));
      toast({ title: 'Marcado como contactado', status: 'info', duration: 1500 });
    } catch { toast({ title: 'Error', status: 'error' }); }
  };

  /**
   * Asigna un refuerzo confirmado a una vacante (guarda en notas).
   */
  const handleAsignarVacante = async (ref, vacante) => {
    if (!ref || !vacante) return;
    // Incluimos el posicionId con un marcador para poder recuperarlo al renderizar
    const nota = `[posicion:${vacante.posicionId}] Cubre baja de ${
      vacante.musicoAusente
        ? `${vacante.musicoAusente.nombre} ${vacante.musicoAusente.apellidos || ''}`.trim()
        : '—'
    } (${vacante.instrumento?.nombre || '—'})`;
    try {
      await actuacionesAPI.updateRefuerzo(actuacionId, ref.id, { notas: nota });
      setAct(prev => ({
        ...prev,
        refuerzos: (prev.refuerzos || []).map(r => r.id === ref.id ? { ...r, notas: nota } : r),
      }));
      toast({ title: 'Asignado a baja', status: 'success', duration: 1500 });
    } catch { toast({ title: 'Error', status: 'error' }); }
  };

  /** Extrae el texto legible de las notas de asignación (sin el marcador técnico) */
  const notasLegibles = (notas) =>
    notas ? notas.replace(/^\[posicion:[^\]]+\]\s*/, '') : '';

  const handleGenerarFormacion = async () => {
    if (isReadOnly) return;
    try {
      const res = await actuacionesAPI.generarFormacion(actuacionId);
      toast({
        title: 'Formación generada',
        description: `${res.data.asignacionesCompletas.length} asignados, ${res.data.uncovered.length} sin cubrir`,
        status: res.data.uncovered.length > 0 ? 'warning' : 'success',
      });
      fetchActuacion();
    } catch (err) { toast({ title: err.response?.data?.message || 'Error', status: 'error' }); }
  };

  const handleAddRepertorio = () => {
    if (isReadOnly) return;
    if (!repItem.titulo.trim()) return;
    const line = { id: repLineId(), titulo: repItem.titulo.trim() };
    if (repItem.autor?.trim()) line.autor = repItem.autor.trim();
    if (repItem.enlace?.trim()) line.enlace = repItem.enlace.trim();
    const newRep = [...(act.repertorio || []), line];
    actuacionesAPI.update(actuacionId, { repertorio: newRep }).then(() => {
      setRepItem({ titulo: '', autor: '', enlace: '' });
      fetchActuacion();
    });
  };

  const handleRemoveRepertorio = (lineId) => {
    if (isReadOnly) return;
    const newRep = (act.repertorio || []).filter((x) => x.id !== lineId);
    actuacionesAPI.update(actuacionId, { repertorio: newRep }).then(fetchActuacion);
  };

  const repSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const repSortableIds = useMemo(
    () => (act?.repertorio || []).map((r) => r.id).filter(Boolean),
    [act?.repertorio],
  );

  const handleRepertorioDragEnd = async (event) => {
    if (isReadOnly) return;
    const { active, over } = event;
    if (!act || !over || active.id === over.id) return;
    const rep = [...(act.repertorio || [])];
    const oldIndex = rep.findIndex((x) => String(x.id) === String(active.id));
    const newIndex = rep.findIndex((x) => String(x.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const newRep = arrayMove(rep, oldIndex, newIndex);
    try {
      await actuacionesAPI.update(actuacionId, { repertorio: newRep });
      setAct((prev) => (prev ? { ...prev, repertorio: newRep } : prev));
      toast({ title: 'Orden actualizado', status: 'success', duration: 1500 });
    } catch {
      toast({ title: 'Error al guardar el orden', status: 'error' });
      fetchActuacion();
    }
  };

  const loadCarpetasPartituras = useCallback(async () => {
    try {
      setCarpetasLoading(true);
      const res = await bandasAPI.listPartiturasCarpetas();
      setCarpetasPartituras(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setCarpetasPartituras([]);
      toast({ title: 'No se pudieron cargar las carpetas de partituras', status: 'error' });
    } finally {
      setCarpetasLoading(false);
    }
  }, [toast]);

  const openModalCarpetas = () => {
    setCarpetaIdsSeleccion([]);
    onCarpetasOpen();
    loadCarpetasPartituras();
  };

  const loadObrasArchivo = useCallback(async () => {
    try {
      setObrasArchivoLoading(true);
      const res = await bandasAPI.listObrasPartituras();
      setObrasArchivo(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setObrasArchivo([]);
      toast({ title: 'No se pudieron cargar las obras del archivo', status: 'error' });
    } finally {
      setObrasArchivoLoading(false);
    }
  }, [toast]);

  const openModalImportObra = () => {
    setSelectedObraArchivoId('');
    setObraArchivoQuery('');
    onImportObraOpen();
    loadObrasArchivo();
  };

  const toggleCarpetaSeleccion = (id) => {
    setCarpetaIdsSeleccion((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleImportarCarpetas = async () => {
    if (isReadOnly) return;
    if (!act || !carpetaIdsSeleccion.length) return;
    const ya = carpetaIdsEnRepertorio(act.repertorio);
    const toAdd = carpetaIdsSeleccion.filter((id) => !ya.has(id));
    if (!toAdd.length) {
      toast({ title: 'Esas carpetas ya están en el repertorio', status: 'info' });
      return;
    }
    const lineas = [];
    const noListoTitles = [];
    for (const id of toAdd) {
      const c = carpetasPartituras.find((x) => x.id === id);
      if (!c) continue;
      const sorted = [...(c.obras || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      for (const row of sorted) {
        if (row?.obra?.estado && row.obra.estado !== 'LISTO') {
          const t = String(row.obra?.titulo || row.titulo || '').trim();
          if (t) noListoTitles.push(t);
        }
        const oid = row.obra?.id ?? row.obraId ?? null;
        lineas.push({
          id: repLineId(),
          tipo: 'obra_carpeta',
          titulo: row.obra?.titulo || 'Sin título',
          autor: row.obra?.autor || null,
          nombreCarpeta: c.nombre,
          carpetaId: c.id,
          ...(oid ? { obraId: oid } : {}),
        });
      }
    }
    if (!lineas.length) return;
    try {
      const newRep = [...(act.repertorio || []), ...lineas];
      await actuacionesAPI.update(actuacionId, { repertorio: newRep });
      toast({
        title:
          lineas.length === 1
            ? 'Se añadió 1 obra'
            : `Se añadieron ${lineas.length} obras de ${toAdd.length} carpeta(s)`,
        status: 'success',
      });
      onCarpetasClose();
      setCarpetaIdsSeleccion([]);
      fetchActuacion();
      if (noListoTitles.length) {
        const uniq = Array.from(new Set(noListoTitles));
        const shown = uniq.slice(0, 5);
        const more = Math.max(0, uniq.length - shown.length);
        toast({
          title: 'Atención',
          description:
            `Las obras ${shown.map((x) => `“${x}”`).join(', ')}${more ? ` y ${more} más` : ''} no están pasadas a LISTO y por lo tanto si compartes este repertorio no serán visibles.`,
          status: 'warning',
          duration: 9000,
          isClosable: true,
        });
      }
    } catch {
      toast({ title: 'Error al guardar el repertorio', status: 'error' });
    }
  };

  const handleImportarObraArchivo = async () => {
    if (isReadOnly) return;
    if (!act || !selectedObraArchivoId) return;
    const obra = obrasArchivo.find((o) => String(o.id) === String(selectedObraArchivoId));
    if (!obra) return;
    const ya = new Set((act.repertorio || []).map((x) => String(x?.obraId || '')).filter(Boolean));
    if (ya.has(String(obra.id))) {
      toast({ title: 'Esa obra ya está añadida', status: 'info' });
      return;
    }
    const line = {
      id: repLineId(),
      titulo: String(obra.titulo || 'Sin título'),
      autor: obra.autor || null,
      obraId: obra.id,
    };
    try {
      const newRep = [...(act.repertorio || []), line];
      await actuacionesAPI.update(actuacionId, { repertorio: newRep });
      onImportObraClose();
      fetchActuacion();
      toast({ title: 'Obra añadida al repertorio', status: 'success', duration: 1500 });
      if (obra?.estado && obra.estado !== 'LISTO') {
        toast({
          title: 'Atención',
          description:
            `La obra “${String(obra.titulo || 'Sin título')}” no está pasada a LISTO y por lo tanto si compartes este repertorio no será visible.`,
          status: 'warning',
          duration: 9000,
          isClosable: true,
        });
      }
    } catch {
      toast({ title: 'No se pudo importar la obra', status: 'error' });
    }
  };

  const handleClearRepertorio = async () => {
    if (isReadOnly) return;
    if (!act) return;
    try {
      await actuacionesAPI.update(actuacionId, { repertorio: [] });
      onClearRepertorioClose();
      fetchActuacion();
      toast({ title: 'Repertorio vaciado', status: 'success', duration: 1500 });
    } catch {
      toast({ title: 'No se pudo vaciar el repertorio', status: 'error' });
    }
  };

  const closeRevealPwdModal = () => {
    setRevealAccountPwd('');
    onRevealPwdClose();
  };

  const handleRevealSharePwd = async () => {
    if (isReadOnly) return;
    if (!revealAccountPwd.trim()) {
      toast({ title: 'Introduce tu contraseña de cuenta', status: 'warning' });
      return;
    }
    try {
      setRevealLoading(true);
      const res = await bandasAPI.revealRepertorioSharePassword({ accountPassword: revealAccountPwd });
      setSharePwdPlain(res.data?.sharePassword || '');
      closeRevealPwdModal();
      toast({ title: 'Contraseña del enlace mostrada', status: 'success' });
    } catch (e) {
      toast({
        title: e.response?.data?.message || 'No se pudo mostrar la contraseña',
        status: 'error',
      });
    } finally {
      setRevealLoading(false);
    }
  };

  /** Construye el bloque HTML de la formación para el PDF */
  const buildFormacionPdfHtml = (fd, refuerzos) => {
    if (!fd) return '';

    const PAL = {
      confirmado: { bg: '#F0FFF4', border: '#38A169', text: '#22543D', icon: '✓', iconBg: '#38A169' },
      no_asiste:  { bg: '#FFF5F5', border: '#E53E3E', text: '#742A2A', icon: '✗', iconBg: '#E53E3E' },
      cubierto:   { bg: '#FFFAF0', border: '#DD6B20', text: '#7B341E', icon: '⇄', iconBg: '#DD6B20' },
      pendiente:  { bg: '#EDF2F7', border: '#A0AEC0', text: '#2D3748', icon: '?', iconBg: '#A0AEC0' },
    };
    const EMPTY = { bg: '#F7FAFC', border: '#E2E8F0' };

    // mapa posicionId → refuerzo que la cubre
    const RE_POS = /^\[posicion:([^\]]+)\]/;
    const refuerzoPosMap = {};
    for (const r of (refuerzos || [])) {
      const m = r.notas && RE_POS.exec(r.notas);
      if (m) refuerzoPosMap[m[1]] = r;
    }

    const cellStyle = (pal, extra = '') =>
      `background:${pal.bg};border:2px solid ${pal.border};border-radius:8px;padding:5px 3px;min-height:76px;`
      + `display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:2px;${extra}`;

    const iconCircle = (pal) =>
      `<div style="background:${pal.iconBg};border-radius:50%;width:20px;height:20px;display:flex;align-items:center;`
      + `justify-content:center;font-size:10px;color:white;font-weight:800;flex-shrink:0;line-height:1">${pal.icon}</div>`;

    const buildMusico = (pos, compact = false) => {
      const musico = pos.musico;
      if (!musico) {
        const inst = pos.instrumento?.nombre || pos.instrumentoPersonalizado?.nombre || '';
        return `<div style="${cellStyle(EMPTY, 'opacity:0.55;border-style:dashed')}">`
          + `<div style="font-size:9px;color:${EMPTY.border}">${escapeHtml(inst)}</div></div>`;
      }
      const ref = refuerzoPosMap[pos.id];
      const isCub = pos.asistencia === 'no_asiste' && !!ref;
      const pal = isCub ? PAL.cubierto : (PAL[pos.asistencia] || PAL.pendiente);
      const ia = musico.instrumentosAsignados?.[0];
      const iaName = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '';
      const ap = musico.apellidos ? ` ${musico.apellidos.trim().split(/\s+/)[0][0]}.` : '';
      const refN = ref ? [ref.contactoAgenda?.nombre || ref.nombre, ref.contactoAgenda?.apellidos].filter(Boolean).join(' ') : '';
      const minH = compact ? 'min-height:64px;' : 'min-height:76px;';
      return `<div style="${cellStyle(pal, minH)}">
        ${iconCircle(pal)}
        <div style="font-size:${compact ? 8 : 9}px;font-weight:800;color:${pal.text};line-height:1.2;${isCub ? 'text-decoration:line-through;opacity:0.6' : ''}">
          ${escapeHtml(musico.nombre)}${escapeHtml(ap)}</div>
        ${isCub && refN ? `<div style="font-size:8px;font-weight:700;color:${pal.border}">↳ ${escapeHtml(refN)}</div>` : ''}
        ${iaName && !isCub ? `<div style="font-size:8px;color:${pal.border};opacity:0.85">${escapeHtml(iaName)}</div>` : ''}
      </div>`;
    };

    const emptyBox = () =>
      `<div style="background:${EMPTY.bg};border:2px dashed ${EMPTY.border};border-radius:8px;min-height:76px;opacity:0.25"></div>`;

    let inner = '';

    if ((fd.modo || 'GRID') === 'GRID') {
      const { filas, columnas, tieneDirector, tieneFilaExtra,
              filaExtraCeldas = 6, filaExtraPos = 'before', filaExtraEtiqueta } = fd;
      const posMap = {};
      for (const p of fd.posiciones) posMap[`${p.fila}-${p.columna}`] = p;

      const gridRow = (filaIdx, nCols, etiqueta) => {
        const cells = Array.from({ length: nCols }, (_, c) => {
          const pos = posMap[`${filaIdx}-${c}`];
          return `<div>${pos ? buildMusico(pos, nCols > 8) : emptyBox()}</div>`;
        }).join('');
        const label = etiqueta ? `<div style="font-size:9px;color:#718096;font-style:italic;margin-bottom:4px;text-align:center">${escapeHtml(etiqueta)}</div>` : '';
        return `${label}<div style="display:grid;grid-template-columns:repeat(${nCols},1fr);gap:5px;margin-bottom:8px">${cells}</div>`;
      };

      const directorRow = () => {
        const pos = posMap['-1-0'];
        return `<div style="display:flex;justify-content:center;margin-bottom:8px">
          <div style="width:${Math.max(90, 100 / columnas)}px">
            ${pos ? buildMusico(pos) : emptyBox()}
          </div>
        </div>`;
      };

      const extraRow = () => gridRow(-2, filaExtraCeldas, filaExtraEtiqueta || 'Fila extraordinaria');

      if (tieneFilaExtra && filaExtraPos === 'before') inner += extraRow();
      if (tieneDirector) inner += directorRow();
      inner += Array.from({ length: filas }, (_, r) => gridRow(r, columnas, '')).join('');
      if (tieneFilaExtra && filaExtraPos === 'after') inner += extraRow();

    } else {
      // LIBRE — canvas relativo con chips posicionados
      const placed = fd.posiciones.filter(p => p.musico && p.x != null && p.y != null);
      const subtipo = fd.subtipoLibre || 'teatro';

      // Guías SVG — mismas que FormacionEditor.jsx
      const w = 720, h = 420;
      let guideSvg = '';
      if (subtipo === 'calle') {
        // Calle: aceras, línea central y flecha de marcha (igual que FormacionEditor)
        guideSvg = `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"
            style="position:absolute;inset:0;pointer-events:none;color:#6B46C1">
          <!-- Acera izquierda -->
          <rect x="${w * 0.02}" y="0" width="${w * 0.12}" height="${h}" fill="currentColor" opacity="0.06" rx="4"/>
          <line x1="${w * 0.14}" y1="0" x2="${w * 0.14}" y2="${h}" stroke="currentColor" stroke-width="2" opacity="0.18" stroke-dasharray="8 5"/>
          <!-- Acera derecha -->
          <rect x="${w * 0.86}" y="0" width="${w * 0.12}" height="${h}" fill="currentColor" opacity="0.06" rx="4"/>
          <line x1="${w * 0.86}" y1="0" x2="${w * 0.86}" y2="${h}" stroke="currentColor" stroke-width="2" opacity="0.18" stroke-dasharray="8 5"/>
          <!-- Línea central discontinua -->
          <line x1="${w * 0.5}" y1="${h * 0.05}" x2="${w * 0.5}" y2="${h * 0.95}" stroke="currentColor" stroke-width="1" opacity="0.1" stroke-dasharray="12 8"/>
          <!-- Flecha de dirección de marcha -->
          <g opacity="0.22">
            <line x1="${w * 0.5}" y1="${h * 0.85}" x2="${w * 0.5}" y2="${h * 0.12}" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
            <polyline points="${w * 0.44},${h * 0.22} ${w * 0.5},${h * 0.1} ${w * 0.56},${h * 0.22}"
              fill="none" stroke="currentColor" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>
          </g>
        </svg>`;
      } else {
        // Teatro: arcos concéntricos desde la parte inferior y línea de escenario (igual que FormacionEditor)
        guideSvg = `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"
            style="position:absolute;inset:0;pointer-events:none;color:#6B46C1;opacity:0.13">
          ${[85, 70, 55, 40].map(r =>
            `<ellipse cx="${w * 0.5}" cy="${h * 0.95}" rx="${w * r / 100}" ry="${h * r * 0.55 / 100}"
              fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="6 4"/>`
          ).join('')}
          <line x1="${w * 0.05}" y1="${h * 0.9}" x2="${w * 0.95}" y2="${h * 0.9}" stroke="currentColor" stroke-width="2"/>
        </svg>`;
      }

      const chips = placed.map(p => {
        const ref = refuerzoPosMap[p.id];
        return `<div style="position:absolute;left:${p.x}%;top:${p.y}%;transform:translate(-50%,-50%)">
          ${buildMusico(p, true)}
        </div>`;
      }).join('');

      inner = `<div style="position:relative;width:${w}px;height:${h}px;background:#F8F5FF;border-radius:12px;overflow:hidden;margin:0 auto">
        ${guideSvg}${chips}
      </div>`;
    }

    return `<div style="page-break-before:always;padding:32px 32px 24px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
        <div>
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:#6B46C1;border-bottom:1.5px solid #E9D8FD;padding-bottom:5px;margin-bottom:6px">
            Formación
          </div>
          <div style="font-size:15px;font-weight:800;color:#1A1A2E">${escapeHtml(fd.nombre)}</div>
          <div style="font-size:11px;color:#718096;margin-top:2px">
            ${fd.modo === 'LIBRE' ? (fd.subtipoLibre === 'calle' ? 'Modo libre — Calle' : 'Modo libre — Teatro/Auditorio') : `${fd.filas} filas × ${fd.columnas} columnas`}
          </div>
        </div>
        <div style="text-align:right;font-size:11px;color:#718096">
          ${fd.posiciones.filter(p => p.musico).length} músicos asignados
        </div>
      </div>
      ${inner}
    </div>`;
  };

  const handleExportPDF = async (sec = pdfSecciones) => {
    setPdfGenerando(true);
    onPdfClose();
    const w = window.open('', '_blank');
    if (!w) {
      toast({ title: 'Permite ventanas emergentes', description: 'El PDF se abre en una nueva ventana.', status: 'warning' });
      setPdfGenerando(false);
      return;
    }

    const enc = act.encuesta;
    const asisten    = enc?.respuestas?.filter(r => r.asiste === true)  || [];
    const noAsisten  = enc?.respuestas?.filter(r => r.asiste === false) || [];
    const horarioPdf = formatActuacionHorario(act);
    const fechaStr   = act.fecha
      ? new Date(act.fecha).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : '';

    // ── Imágenes de marca (data URLs para PDF sin CORS) ─────────────────────
    const origin = window.location.origin;
    const fetchStaticImg = async (path) => {
      try {
        const res = await fetch(`${origin}${path}`);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(String(fr.result || ''));
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        });
      } catch { return null; }
    };

    const [logoDataUrl, musicoDataUrl] = await Promise.all([
      fetchStaticImg('/logo%20y%20texto%20cadencya%20blanco.png'),
      fetchStaticImg('/musico-cofradia-1.png'),
    ]);

    const IMG_LOGO = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:42px;display:block">`
      : `<span style="font-size:21px;font-weight:900;color:#fff;letter-spacing:-0.5px">Cadencya</span>`;

    const IMG_LOGO_SM = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:28px;display:block">`
      : `<span style="font-size:14px;font-weight:900;color:#fff">Cadencya</span>`;

    const IMG_MUSICO = (height = 120, opacity = 0.15) => musicoDataUrl
      ? `<img src="${musicoDataUrl}" alt="" style="height:${height}px;opacity:${opacity};display:block;object-fit:contain">`
      : '';

    // ── Cartel ──────────────────────────────────────────────────────────────
    let cartelImgHtml = '';
    let waitForCartelLoad = false;
    if (sec.cartel && act.cartelUrl) {
      try {
        const dataUrl = await fetchImageAsDataUrlForPdf(act.cartelUrl);
        if (dataUrl) cartelImgHtml = `<img src="${escapeHtml(dataUrl)}" alt="Cartel" />`;
      } catch {
        const abs = resolveCartelUrlForPdf(act.cartelUrl);
        waitForCartelLoad = true;
        cartelImgHtml = `<img id="cartel-pdf-img" src="${escapeHtml(abs)}" alt="Cartel" />`;
      }
    }

    // ── Sección de información — tarjetas ────────────────────────────────────
    const infoCard = (label, value, full = false) =>
      value ? `<div class="ic${full ? ' ic-full' : ''}"><div class="ic-label">${label}</div><div class="ic-value">${value}</div></div>` : '';

    const infoCards = sec.infoGeneral ? [
      infoCard('📅 Fecha', fechaStr, true),
      infoCard('🕐 Horario', horarioPdf ? escapeHtml(horarioPdf) : ''),
      infoCard('🎭 Tipo', escapeHtml(labelTipoActuacion(act.tipo, tiposFormacionCatalogo))),
      infoCard('📍 Punto de inicio', act.ubicacion ? escapeHtml(act.ubicacion) : ''),
      infoCard('🏁 Punto de llegada', act.ubicacionFin ? escapeHtml(act.ubicacionFin) : ''),
      infoCard('👔 Uniforme', act.uniforme ? escapeHtml(act.uniforme) : ''),
    ].join('') : '';

    let busInfo = '';
    if (sec.autobus && act.necesidadAutobus && (act.autobusHoraSalida?.trim() || act.autobusUbicacion?.trim())) {
      const parts = [];
      if (act.autobusHoraSalida?.trim()) parts.push(`Salida: <strong>${escapeHtml(act.autobusHoraSalida.trim())}</strong>`);
      if (act.autobusUbicacion?.trim())  parts.push(`Recogida en: <strong>${escapeHtml(act.autobusUbicacion.trim())}</strong>`);
      busInfo = `<div class="bus-box">🚌&ensp;Autobús — ${parts.join(' &middot; ')}</div>`;
    }

    // ── Observaciones ────────────────────────────────────────────────────────
    const obsHtml = sec.observaciones && act.observaciones
      ? `<div class="section-title">Observaciones</div>
         <div class="obs-box">${escapeHtml(act.observaciones).replace(/\n/g, '<br>')}</div>`
      : '';

    // ── Repertorio ───────────────────────────────────────────────────────────
    const repItems = normalizeRepertorio(act.repertorio || []);
    let repHtml = '';
    if (sec.repertorio && repItems.length) {
      const rows = repItems.map((r, i) => {
        const tit    = escapeHtml(r.titulo || '');
        const autor  = r.autor ? `<span class="rep-autor">(${escapeHtml(r.autor)})</span>` : '';
        const folderIcon = `<svg style="display:inline;vertical-align:middle;margin-right:3px" width="11" height="11" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 3A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H7.914a.5.5 0 0 1-.354-.146L6.146 2.94A1.5 1.5 0 0 0 5.086 2.5H1.5Z"/></svg>`;
        const folder = r.nombreCarpeta
          ? `<span class="rep-folder">${folderIcon}${escapeHtml(r.nombreCarpeta)}</span>`
          : '';
        const enlace = r.enlace
          ? `<a class="rep-link" href="${escapeHtml(r.enlace)}">${escapeHtml(r.enlace)}</a>`
          : '';
        return `<li><span class="rep-num">${i + 1}.</span>
          <span class="rep-body"><strong>${tit}</strong>&ensp;${autor}${folder}${enlace}</span></li>`;
      });
      repHtml = `<div class="section-title">Repertorio</div><ol class="rep-list">${rows.join('')}</ol>`;
    }

    const repCompartidoHtml = (() => {
      const share = repertorioShare;
      if (!sec.partiturasCompartidas || !incluirRepertorioCompartidoEnExport) return '';
      if (!share?.shareUrl) {
        if (share?.hasPassword)
          return `<div class="section-title">Partituras (Cadencya)</div>
                  <p style="font-size:12px;color:#553C9A;margin-top:6px;font-style:italic">Enlace disponible en la app Cadencya.</p>`;
        return '';
      }
      let h = `<div class="section-title">Partituras (Cadencya)</div>
               <p style="font-size:12px;margin-top:6px"><a href="${escapeHtml(share.shareUrl)}" style="color:#6B46C1">${escapeHtml(share.shareUrl)}</a></p>`;
      if (sharePwdPlain)
        h += `<p style="font-size:12px;color:#553C9A"><strong>Contraseña:</strong> ${escapeHtml(sharePwdPlain)}</p>`;
      return h;
    })();

    const enlaceManualHtml = sec.enlaceManual && act.enlacePartituras
      ? `<p style="font-size:12px;margin-top:8px">📎 <strong>Partituras (enlace manual):</strong>
         <a href="${escapeHtml(act.enlacePartituras)}" style="color:#6B46C1">${escapeHtml(act.enlacePartituras)}</a></p>`
      : '';

    // ── Tabla de asistencia ───────────────────────────────────────────────────
    const tableRow = (musico, extra = '') =>
      `<tr>
        <td><strong>${escapeHtml(musico.nombre)} ${escapeHtml(musico.apellidos || '')}</strong></td>
        <td>${escapeHtml(formatMusicoInstrumentosText(musico))}</td>
        ${extra}
      </tr>`;

    const asistHtml = sec.musicosConfirmados && asisten.length
      ? `<div class="section-title">Músicos confirmados (${asisten.length})</div>
         <table>
           <thead><tr><th>Nombre</th><th>Instrumento</th></tr></thead>
           <tbody>${asisten.map(r => tableRow(r.musico)).join('')}</tbody>
         </table>`
      : '';

    const refuerzosHtml = sec.refuerzos && act.refuerzos?.length
      ? (() => {
          const RE_POS = /^\[posicion:[^\]]+\]/;
          return `<div class="section-title">Refuerzos (${act.refuerzos.length})</div>
          <table>
            <thead><tr><th>Nombre</th><th>Instrumento(s)</th><th>Estado</th></tr></thead>
            <tbody>${act.refuerzos.map(r => {
              const nombre = r.contactoAgenda
                ? `${escapeHtml(r.contactoAgenda.nombre || '')} ${escapeHtml(r.contactoAgenda.apellidos || '')}`
                : escapeHtml(r.nombre || '');
              const instrs = (r.contactoAgenda?.instrumentos || []).map(i => escapeHtml(i.nombre)).join(', ') || '-';
              const estado = r.noDisponible
                ? `<span class="badge badge-red">No disponible</span>`
                : r.confirmado
                  ? `<span class="badge badge-green">Confirmado</span>`
                  : r.ultimoContactoEn
                    ? `<span class="badge badge-orange">Contactado</span>`
                    : `<span class="badge badge-gray">Sin contactar</span>`;
              const asignado = r.notas && RE_POS.test(r.notas)
                ? `<span class="badge badge-purple">Asignado a baja</span>` : '';
              return `<tr><td><strong>${nombre}</strong></td><td>${instrs}</td><td>${estado} ${asignado}</td></tr>`;
            }).join('')}</tbody>
          </table>`;
        })()
      : '';

    const noAsistHtml = sec.noAsisten && noAsisten.length
      ? `<div class="section-title">No asisten (${noAsisten.length})</div>
         <table>
           <thead><tr><th>Nombre</th><th>Instrumento</th></tr></thead>
           <tbody>${noAsisten.map(r => tableRow(r.musico)).join('')}</tbody>
         </table>`
      : '';

    // ── Formación ─────────────────────────────────────────────────────────────
    let formacionPdfHtml = '';
    if (sec.formacion && act.plantillaId) {
      let fd = formacionData;
      if (!fd) {
        try {
          const res = await actuacionesAPI.getFormacionConAsistencia(actuacionId);
          fd = res.data;
        } catch { fd = null; }
      }
      formacionPdfHtml = buildFormacionPdfHtml(fd, act.refuerzos);
    }

    const now = new Date().toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    // ── HTML completo ─────────────────────────────────────────────────────────
    w.document.write(`<!DOCTYPE html>
<html lang="es"><head>
<meta charset="utf-8">
<title>Ficha · ${escapeHtml(act.nombre)}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',system-ui,sans-serif;color:#1A1A2E;background:#fff;font-size:13px;line-height:1.55}

/* ── HEADER ── */
.hd{background:linear-gradient(135deg,#2D1B69 0%,#6B46C1 55%,#9F7AEA 100%);display:flex;align-items:center;justify-content:space-between;padding:22px 32px;gap:16px;position:relative;overflow:hidden}
.hd::after{content:'';position:absolute;right:90px;bottom:-30px;width:120px;height:120px;background:rgba(255,255,255,0.04);border-radius:50%}
.hd-logo{display:flex;align-items:center;flex-shrink:0}
.hd-center{flex:1;text-align:center;padding:0 16px}
.hd-label{font-size:9px;color:#C4B5FD;letter-spacing:2px;text-transform:uppercase;margin-bottom:5px}
.hd-name{font-size:19px;font-weight:800;color:#fff;line-height:1.25}
.hd-tipo{font-size:12px;color:#E9D8FD;margin-top:3px}
.hd-deco{flex-shrink:0;opacity:0.28}
.hd-stripe{height:4px;background:linear-gradient(90deg,#553C9A,#B794F4,#6B46C1)}

/* ── MAIN TWO-COLUMN ── */
.main{display:flex;gap:0;min-height:320px}
.main-left{flex:1;padding:24px 20px 24px 32px}
.main-right{width:220px;padding:20px 32px 20px 12px;display:flex;flex-direction:column;align-items:center;gap:12px}

/* ── INFO CARDS ── */
.ic-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px}
.ic{background:#FAF5FF;border:1.5px solid #E9D8FD;border-radius:10px;padding:8px 12px}
.ic-full{grid-column:1/-1}
.ic-label{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:#805AD5;margin-bottom:2px}
.ic-value{font-size:13px;font-weight:600;color:#1A1A2E}

/* ── BUS ── */
.bus-box{background:#FFFBEB;border-left:3px solid #D69E2E;border-radius:0 8px 8px 0;padding:8px 12px;font-size:12px;color:#744210;margin-bottom:14px}

/* ── CARTEL ── */
.cartel-box{width:100%;background:#F3F0FF;border:2px solid #DDD6FE;border-radius:12px;overflow:hidden;text-align:center}
.cartel-box img{width:100%;height:auto;max-height:280px;object-fit:contain;display:block}
.cartel-label{font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:#805AD5;padding:6px;background:#EDE9FE}
.cartel-empty{padding:28px 12px;color:#B794F4;font-size:11px;font-style:italic}

/* ── SECTION TITLE ── */
.section-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:#6B46C1;border-bottom:1.5px solid #E9D8FD;padding-bottom:5px;margin:20px 0 12px}
.section-title:first-child{margin-top:0}

/* ── OBS ── */
.obs-box{background:#FFFBEB;border-left:3px solid #D69E2E;border-radius:0 8px 8px 0;padding:10px 14px;font-size:12px;color:#744210;white-space:pre-wrap}

/* ── TABLES ── */
table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px}
thead tr{background:linear-gradient(135deg,#553C9A,#7C3AED);color:#fff}
thead th{padding:7px 10px;text-align:left;font-weight:600;font-size:11px;letter-spacing:0.3px}
tbody tr:nth-child(even){background:#FAF5FF}
tbody td{padding:6px 10px;border-bottom:1px solid #EDE9FE;color:#2D1B69;vertical-align:top}

/* ── BADGES ── */
.badge{display:inline-block;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700;white-space:nowrap}
.badge-green{background:#C6F6D5;color:#22543D}
.badge-red{background:#FED7D7;color:#742A2A}
.badge-orange{background:#FEEBC8;color:#7B341E}
.badge-gray{background:#EDF2F7;color:#4A5568}
.badge-purple{background:#EDE9FE;color:#553C9A}

/* ── REPERTOIRE ── */
ol.rep-list{list-style:none;padding:0;margin-top:4px}
ol.rep-list li{display:flex;align-items:baseline;gap:8px;padding:5px 10px;border-radius:6px;margin-bottom:3px}
ol.rep-list li:nth-child(odd){background:#FAF5FF}
.rep-num{font-size:11px;font-weight:700;color:#6B46C1;min-width:22px;flex-shrink:0}
.rep-body{font-size:12px;color:#1A1A2E;flex:1}
.rep-autor{font-size:11px;color:#718096}
.rep-folder{font-size:10px;background:#EDE9FE;color:#553C9A;padding:1px 7px;border-radius:10px;font-weight:600;margin-left:4px}
.rep-link{font-size:11px;color:#6B46C1;margin-left:6px}

/* ── FULL-WIDTH CONTENT ── */
.full-content{padding:0 32px 28px}

/* ── FOOTER ── */
.footer{background:linear-gradient(135deg,#1A0D40 0%,#2D1B69 100%);padding:11px 32px;display:flex;align-items:center;justify-content:space-between;margin-top:auto}
.footer-brand{display:flex;flex-direction:column;align-items:flex-start;gap:4px}
.footer-tagline{font-size:10px;color:#B794F4}
.footer-meta{font-size:10px;color:#9F7AEA;text-align:right}
.footer-deco{opacity:0.3;flex-shrink:0}

/* ── PRINT ── */
@media print{
  body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
</style>
</head>
<body>

<!-- ▸ HEADER -->
<div class="hd">
  <div class="hd-logo">
    ${IMG_LOGO}
  </div>
  <div class="hd-center">
    <div class="hd-label">Ficha de Actuación</div>
    <div class="hd-name">${escapeHtml(act.nombre)}</div>
    ${act.tipo ? `<div class="hd-tipo">${escapeHtml(labelTipoActuacion(act.tipo, tiposFormacionCatalogo))}</div>` : ''}
  </div>
  <div class="hd-deco">${IMG_MUSICO(70, 0.35)}</div>
</div>
<div class="hd-stripe"></div>

<!-- ▸ TWO-COLUMN: INFO + CARTEL -->
<div class="main">
  <div class="main-left">
    <div class="ic-grid">${infoCards}</div>
    ${busInfo}
    ${obsHtml}
  </div>
  <div class="main-right">
    <div class="cartel-box">
      <div class="cartel-label">Cartel</div>
      ${cartelImgHtml || '<div class="cartel-empty">Sin cartel</div>'}
    </div>
    <div style="margin-top:auto;text-align:center">${IMG_MUSICO(90, 0.12)}</div>
  </div>
</div>

<!-- ▸ FULL-WIDTH SECTIONS -->
<div class="full-content">
  ${repHtml}
  ${repCompartidoHtml}
  ${enlaceManualHtml}
  ${asistHtml}
  ${refuerzosHtml}
  ${noAsistHtml}
</div>
${formacionPdfHtml}

<!-- ▸ FOOTER -->
<div class="footer">
  <div class="footer-brand">
    ${IMG_LOGO_SM}
    <div class="footer-tagline" style="margin-top:4px">La herramienta de tu banda</div>
  </div>
  <div class="footer-meta">Generado el ${now}<br>cadencya.app</div>
  <div class="footer-deco">${IMG_MUSICO(36, 0.4)}</div>
</div>

</body></html>`);
    w.document.close();

    const triggerPrint = () => {
      try { w.focus(); w.print(); } catch (err) { console.error(err); }
      setPdfGenerando(false);
    };

    if (waitForCartelLoad) {
      const img = w.document.getElementById('cartel-pdf-img');
      if (img) {
        if (img.complete && img.naturalHeight > 0) {
          setTimeout(triggerPrint, 200);
        } else {
          img.onload  = () => setTimeout(triggerPrint, 200);
          img.onerror = triggerPrint;
          setTimeout(triggerPrint, 12000);
        }
      } else {
        triggerPrint();
      }
    } else {
      setTimeout(triggerPrint, sec.cartel && act.cartelUrl ? 250 : 0);
    }
  };

  const handleShareWhatsApp = () => {
    if (shareWaCartelImgUrl) URL.revokeObjectURL(shareWaCartelImgUrl);
    setShareWaStep('formato');
    setShareWaFormato(null);
    setShareWaMensaje('');
    setShareWaFormacionImgUrl(null);
    setShareWaFormacionBlob(null);
    setShareWaCartelImgUrl(null);
    setShareWaCartelBlob(null);
    setShareWaCopied(false);
    setShareWaCopyNotice(null);
    setShareWaSecciones({
      infoBasica: true,
      horarios: true,
      autobus: Boolean(act?.necesidadAutobus),
      observaciones: false,
      cartel: Boolean(act?.cartelUrl),
      repertorio: Boolean(act?.repertorio?.length),
      partiturasEnlace: Boolean(repertorioShare?.shareUrl || act?.enlacePartituras),
      asistencia: false,
      formacion: false,
      refuerzos: false,
    });
    onShareWaOpen();
  };

  // ── Share WhatsApp helpers ───────────────────────────────────────────────────

  const buildShareWaMensaje = (secciones) => {
    if (!act) return '';
    const enc = act.encuesta;
    const lines = [];
    lines.push(`\uD83C\uDFB5 *${act.nombre || 'Actuaci\xF3n'}*`);
    if (act.tipo) lines.push(`_${labelTipoActuacion(act.tipo, tiposFormacionCatalogo)}_`);
    lines.push('');

    if (secciones.infoBasica) {
      if (act.fecha) {
        const fecha = new Date(act.fecha).toLocaleDateString('es-ES', {
          weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
        });
        lines.push(`\uD83D\uDCC5 *Fecha:* ${fecha}`);
      }
      if (act.ubicacion) {
        appendUbicacionWaLines(lines, '\uD83D\uDCCD *Lugar inicio:*', act.ubicacion, act.lat, act.lng);
      }
      if (act.ubicacionFin) {
        appendUbicacionWaLines(lines, '\uD83C\uDFC1 *Lugar fin:*', act.ubicacionFin, act.latFin, act.lngFin);
      }
      if (act.uniforme) lines.push(`\uD83D\uDC54 *Uniforme:* ${act.uniforme}`);
      lines.push('');
    }

    if (secciones.horarios) {
      const hs = [];
      if (act.horaConcentracion) hs.push(`\u23F0 *Concentraci\xF3n:* ${act.horaConcentracion}h`);
      if (act.horaSalida)        hs.push(`\uD83D\uDEB6 *Salida:* ${act.horaSalida}h`);
      if (act.horaInicio)        hs.push(`\uD83C\uDFBC *Inicio:* ${act.horaInicio}h`);
      if (act.horaFin)           hs.push(`\u23F1\uFE0F *Fin aprox.:* ${act.horaFin}h`);
      if (hs.length) { lines.push(...hs); lines.push(''); }
    }

    if (secciones.autobus && act.necesidadAutobus) {
      lines.push('\uD83D\uDE8C *AUT\xD3BUS*');
      const parada = act.paradaAutobus || act.autobusUbicacion;
      const hora   = act.horaAutobus   || act.autobusHoraSalida;
      if (parada) {
        appendUbicacionWaLines(lines, '\uD83D\uDCCD Parada:', parada, act.autobusLat, act.autobusLng);
      }
      if (hora)   lines.push(`\uD83D\uDD57 Salida: ${hora}h`);
      lines.push('');
    }

    if (secciones.repertorio && act.repertorio?.length) {
      lines.push('\uD83C\uDFB6 *REPERTORIO*');
      const items = normalizeRepertorio(act.repertorio);
      items.forEach((r, i) => {
        const au = r.autor ? ` _(${r.autor})_` : '';
        const ca = r.nombreCarpeta ? ` [${r.nombreCarpeta}]` : '';
        lines.push(`${i + 1}. ${r.titulo || ''}${au}${ca}`);
      });
      lines.push('');
    }

    if (secciones.partiturasEnlace) {
      if (repertorioShare?.shareUrl) {
        lines.push('\uD83D\uDCCE *PARTITURAS*');
        lines.push(repertorioShare.shareUrl);
        if (sharePwdPlain) {
          lines.push(`\uD83D\uDD11 Contrase\xF1a: ${sharePwdPlain}`);
        } else if (repertorioShare.hasPassword) {
          lines.push('_Contrase\xF1a: cons\xFAltala en Cadencya_');
        }
        lines.push('');
      } else if (act.enlacePartituras) {
        lines.push('\uD83D\uDCCE *PARTITURAS*');
        lines.push(act.enlacePartituras);
        lines.push('');
      }
    }

    if (secciones.asistencia && enc) {
      const conf = enc.respuestas?.filter(r => r.asiste === true).length  || 0;
      const noas = enc.respuestas?.filter(r => r.asiste === false).length || 0;
      const pend = enc.respuestas?.filter(r => r.asiste === null).length  || 0;
      lines.push('\uD83D\uDC65 *ASISTENCIA*');
      lines.push(`\u2705 Confirmados: ${conf}`);
      lines.push(`\u274C No asisten: ${noas}`);
      lines.push(`\u23F3 Pendientes: ${pend}`);
      lines.push('');
    }

    if (secciones.formacion && act.plantillaId && formacionData) {
      lines.push('\uD83C\uDFAD *FORMACI\xD3N*');
      lines.push(`*${formacionData.nombre}*`);
      const tm = formacionData.posiciones?.filter(p => p.musico).length || 0;
      lines.push(`_${tm} m\xFAsicos asignados_`);
      lines.push('');
    }

    if (secciones.refuerzos && act.refuerzos?.length) {
      lines.push('\uD83D\uDD04 *REFUERZOS*');
      act.refuerzos.forEach(r => {
        const nombre = r.nombre
          || [r.contactoAgenda?.nombre, r.contactoAgenda?.apellidos].filter(Boolean).join(' ')
          || 'Refuerzo';
        const icon = r.confirmado ? '\u2705' : (r.contactado ? '\uD83D\uDCDE' : '\u23F3');
        lines.push(`${icon} ${nombre}`);
      });
      lines.push('');
    }

    if (secciones.observaciones && act.observaciones) {
      lines.push('\uD83D\uDCCB *OBSERVACIONES*');
      lines.push(act.observaciones);
      lines.push('');
    }

    lines.push('_Enviado desde Cadencya \uD83C\uDFB5_');
    return lines.join('\n');
  };

  const handleShareWaFormatoSelect = (formato) => {
    setShareWaFormato(formato);
    setShareWaStep('secciones');
  };

  const handleShareWaSiguiente = async () => {
    if (shareWaFormato === 'pdf') {
      const sec = {
        cartel:               shareWaSecciones.cartel,
        infoGeneral:          shareWaSecciones.infoBasica,
        autobus:              shareWaSecciones.autobus,
        observaciones:        shareWaSecciones.observaciones,
        repertorio:           shareWaSecciones.repertorio,
        partiturasCompartidas: shareWaSecciones.partiturasEnlace,
        enlaceManual:         shareWaSecciones.partiturasEnlace,
        formacion:            shareWaSecciones.formacion,
        musicosConfirmados:   shareWaSecciones.asistencia,
        noAsisten:            shareWaSecciones.asistencia,
        refuerzos:            shareWaSecciones.refuerzos,
      };
      closeShareWaModal();
      handleExportPDF(sec);
      return;
    }
    // texto path
    const msg = buildShareWaMensaje(shareWaSecciones);
    setShareWaMensaje(msg);
    setShareWaCopied(false);
    setShareWaCopyNotice(null);

    setShareWaGenerando(true);
    try {
      if (shareWaSecciones.cartel && act?.cartelUrl) {
        try {
          const abs = resolveCartelUrlForPdf(act.cartelUrl);
          const res = await fetch(abs, { mode: 'cors', credentials: 'include' });
          if (res.ok) {
            const blob = await res.blob();
            setShareWaCartelBlob(blob);
            setShareWaCartelImgUrl(URL.createObjectURL(blob));
          }
        } catch (e) {
          console.warn('Cartel image error:', e);
          setShareWaCartelBlob(null);
          setShareWaCartelImgUrl(null);
        }
      } else {
        if (shareWaCartelImgUrl) URL.revokeObjectURL(shareWaCartelImgUrl);
        setShareWaCartelBlob(null);
        setShareWaCartelImgUrl(null);
      }

      if (shareWaSecciones.formacion && act?.plantillaId) {
        try {
          let fd = formacionData;
          if (!fd) {
            const res = await actuacionesAPI.getFormacionConAsistencia(actuacionId);
            fd = res.data;
          }
          const canvas = generateFormacionCanvas(fd);
          if (canvas) {
            setShareWaFormacionImgUrl(canvas.toDataURL('image/png'));
            await new Promise(resolve => {
              canvas.toBlob(blob => { setShareWaFormacionBlob(blob); resolve(); }, 'image/png');
            });
          }
        } catch (e) { console.warn('Formation canvas error:', e); }
      } else {
        setShareWaFormacionImgUrl(null);
        setShareWaFormacionBlob(null);
      }
    } finally {
      setShareWaGenerando(false);
    }
    setShareWaStep('preview');
  };

  const handleShareWaCopiar = async () => {
    const msg = shareWaMensaje.trim();
    if (!msg) return;

    setShareWaCopied(false);
    setShareWaCopyNotice(null);

    const imageItems = [];
    if (shareWaSecciones.cartel && shareWaCartelImgUrl) {
      imageItems.push({ url: shareWaCartelImgUrl, name: 'cartel-cadencya.png', label: 'cartel' });
    }
    if (shareWaSecciones.formacion && shareWaFormacionImgUrl) {
      imageItems.push({ url: shareWaFormacionImgUrl, name: 'formacion-cadencya.png', label: 'formación' });
    }

    try {
      await navigator.clipboard.writeText(msg);
    } catch (err) {
      console.warn('clipboard text failed:', err);
      setShareWaCopyNotice({
        status: 'error',
        title: 'No se pudo copiar',
        description: 'Comprueba que el navegador tenga permiso para usar el portapapeles.',
      });
      toast({ title: 'No se pudo copiar el texto', status: 'error', duration: 3000 });
      return;
    }

    let imageCopied = false;
    let imagesDownloaded = 0;

    if (imageItems.length === 1) {
      try {
        await copyTextAndPngToClipboard(msg, imageItems[0].url);
        imageCopied = true;
      } catch (err) {
        console.warn('clipboard image combined failed:', err);
        try {
          const png = await urlToPngBlob(imageItems[0].url);
          downloadBlob(png, imageItems[0].name);
          imagesDownloaded = 1;
        } catch (dlErr) {
          console.warn('image download fallback failed:', dlErr);
        }
      }
    } else if (imageItems.length > 1) {
      for (const item of imageItems) {
        try {
          const png = await urlToPngBlob(item.url);
          downloadBlob(png, item.name);
          imagesDownloaded += 1;
        } catch (dlErr) {
          console.warn(`download ${item.label} failed:`, dlErr);
        }
      }
    }

    setShareWaCopied(true);

    if (imageCopied) {
      setShareWaCopyNotice({
        status: 'success',
        title: 'Ya está en el portapapeles',
        description: 'Abre WhatsApp, elige el chat o grupo y pega el mensaje (Ctrl+V). Si hace falta, pega también la imagen en el mismo chat.',
      });
      toast({ title: 'Texto e imagen copiados', status: 'success', duration: 3000 });
    } else if (imagesDownloaded > 0) {
      setShareWaCopyNotice({
        status: 'warning',
        title: 'Texto copiado',
        description: `Las imágenes se han descargado como PNG (${imagesDownloaded}). Pégalas o adjúntalas en WhatsApp junto al mensaje.`,
      });
      toast({
        title: 'Texto copiado',
        description: 'Las imágenes se han descargado para adjuntarlas en WhatsApp.',
        status: 'warning',
        duration: 4000,
      });
    } else if (imageItems.length > 0) {
      setShareWaCopyNotice({
        status: 'warning',
        title: 'Texto copiado',
        description: 'No se pudieron copiar las imágenes. Usa los botones de copiar en cada miniatura o descárgalas.',
      });
      toast({ title: 'Texto copiado', status: 'success', duration: 2500 });
    } else {
      setShareWaCopyNotice({
        status: 'success',
        title: 'Ya está en el portapapeles',
        description: 'Abre WhatsApp, elige el chat o grupo y pega el mensaje (Ctrl+V o mantener pulsado → Pegar).',
      });
      toast({ title: 'Texto copiado', status: 'success', duration: 2500 });
    }
  };

  const closeRrssCartelModal = () => {
    onRrssCartelClose();
    setRrssCartelStep('formato');
    setRrssCartelGen(false);
    if (rrssCartelPreviewUrl) URL.revokeObjectURL(rrssCartelPreviewUrl);
    setRrssCartelPreviewUrl('');
  };

  const openRrssCartelModal = () => {
    setRrssCartelStep('formato');
    setRrssCartelFormato('feed');
    setRrssCartelOpts(defaultRrssCartelOpts());
    if (rrssCartelPreviewUrl) URL.revokeObjectURL(rrssCartelPreviewUrl);
    setRrssCartelPreviewUrl('');
    onRrssCartelOpen();
  };

  const buildRrssCartelCanvas = async () => {
    if (!act) return;
    const spec = RRSS_CARTEL_FORMATOS[rrssCartelFormato] || RRSS_CARTEL_FORMATOS.feed;
    const origin = window.location.origin;
    const loadStaticImgEl = async (path) => {
      try {
        // Igual que en el resto de la app (public/): permitimos rutas con espacios.
        const safePath = encodeURI(String(path || ''));
        const res = await fetch(`${origin}${safePath}`);
        if (!res.ok) return null;
        const blob = await res.blob();
        const dataUrl = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(String(fr.result || ''));
          fr.onerror = () => resolve('');
          fr.readAsDataURL(blob);
        });
        if (!dataUrl) return null;
        return await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = dataUrl;
        });
      } catch {
        return null;
      }
    };

    const loadRemoteImg = async (url) => {
      if (!url) return null;
      try {
        const dataUrl = await fetchImageAsDataUrlForPdf(url);
        if (!dataUrl) return null;
        return await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = dataUrl;
        });
      } catch {
        return null;
      }
    };

    const loadBlobImg = async (blobRes) => {
      const blob = blobRes?.data;
      if (!blob || !blob.size) return null;
      try {
        if (typeof createImageBitmap === 'function') {
          // eslint-disable-next-line no-undef
          return await createImageBitmap(blob);
        }
      } catch {
        // ignore
      }
      const objectUrl = URL.createObjectURL(blob);
      const imgEl = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = objectUrl;
      });
      URL.revokeObjectURL(objectUrl);
      if (imgEl) return imgEl;
      const dataUrl = await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onloadend = () => resolve(String(fr.result || ''));
        fr.onerror = () => resolve('');
        fr.readAsDataURL(blob);
      });
      if (!dataUrl) return null;
      return await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = dataUrl;
      });
    };

    const cadencyaLogo =
      rrssCartelOpts.footerCadencya && (await loadStaticImgEl('/logo y texto cadencya blanco.png'));
    let bandaAvatar = null;
    if (rrssCartelOpts.logoBanda) {
      try {
        const blobRes = await actuacionesAPI.getBandaAvatarFile(actuacionId);
        bandaAvatar = await loadBlobImg(blobRes);
      } catch {
        bandaAvatar = null;
      }
      if (!bandaAvatar && act.banda?.avatar) {
        bandaAvatar = await loadRemoteImg(resolveCartelUrlForPdf(act.banda.avatar));
      }
    }
    let cartelImg = null;
    if (rrssCartelOpts.cartelFondo && act.cartelUrl) {
      try {
        const blobRes = await actuacionesAPI.getCartelBytes(actuacionId);
        cartelImg = await loadBlobImg(blobRes);
      } catch {
        cartelImg = null;
      }
      if (!cartelImg) {
        cartelImg = await loadRemoteImg(resolveCartelUrlForPdf(act.cartelUrl));
      }
    }

    const fechaStr = act.fecha
      ? new Date(act.fecha).toLocaleDateString('es-ES', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';
    const horarioPdf = formatActuacionHorario(act);
    const repFlat = normalizeRepertorio(act.repertorio || []);
    const repertorioLines = repFlat.map((r, idx) => {
      if (isObraCarpetaLine(r)) {
        const titulo = (r.titulo || '').trim();
        // Si la "línea carpeta" es solo separador sin obra, no la pintamos.
        if (!titulo) return null;
        return `${idx + 1}. ${titulo}`;
      }
      return `${idx + 1}. ${r.titulo || ''}`;
    }).filter(Boolean);

    const canvas = document.createElement('canvas');
    drawActuacionCartelRrss(canvas, {
      width: spec.w,
      height: spec.h,
      opts: rrssCartelOpts,
      content: {
        titulo: act.nombre,
        fecha: fechaStr,
        horario: horarioPdf,
        ubicacion: act.ubicacion?.trim() || '',
        ubicacionFin: act.ubicacionFin?.trim() || '',
        tipoLabel: labelTipoActuacion(act.tipo, tiposFormacionCatalogo),
        repertorioLines,
        observaciones: act.observaciones?.trim() || '',
        bandaNombre: act.banda?.nombre || '',
      },
      cadencyaLogo,
      bandaAvatar,
      cartelImg,
    });
    return { canvas, spec };
  };

  const handleRrssCartelGenerar = async () => {
    if (!act) return;
    setRrssCartelGen(true);
    try {
      const { canvas, spec } = await buildRrssCartelCanvas();
      const origin = window.location.origin;
      await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('blob'));
              return;
            }
            const a = document.createElement('a');
            const fname = `cartel-rrss-${slugifyFilename(act.nombre)}-${spec.key}.png`;
            a.href = URL.createObjectURL(blob);
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(a.href), 2000);
            resolve();
          },
          'image/png',
          0.92,
        );
      });

      toast({ title: 'Imagen descargada', description: 'Lista para publicar en tus redes.', status: 'success' });
      closeRrssCartelModal();
    } catch (e) {
      console.warn(e);
      toast({
        title: 'No se pudo generar la imagen',
        description: 'Si usas cartel o avatar, comprueba que carguen correctamente e inténtalo de nuevo.',
        status: 'error',
      });
    } finally {
      setRrssCartelGen(false);
    }
  };

  const handleRrssCartelGenerarPreview = async () => {
    if (!act) return;
    setRrssCartelGen(true);
    try {
      const { canvas } = await buildRrssCartelCanvas();
      const blob = await new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b || null), 'image/png', 0.92);
      });
      if (!blob) throw new Error('blob');
      if (rrssCartelPreviewUrl) URL.revokeObjectURL(rrssCartelPreviewUrl);
      const url = URL.createObjectURL(blob);
      setRrssCartelPreviewUrl(url);
      setRrssCartelStep('preview');
    } catch (e) {
      console.warn(e);
      toast({
        title: 'No se pudo generar la previsualización',
        description: 'Comprueba que carguen el logo y el cartel (si los usas) e inténtalo de nuevo.',
        status: 'error',
      });
    } finally {
      setRrssCartelGen(false);
    }
  };

  if (loading || !act) return <Flex justify="center" py={12}><Spinner size="xl" color="purple.500" /></Flex>;

  if (showEditor && act.plantilla && !isReadOnly) {
    return <FormacionEditor plantilla={act.plantilla} onBack={() => { setShowEditor(false); fetchActuacion(); }} toast={toast} />;
  }

  const encuesta = act?.encuesta;
  const ensayosAsociados = mergeEnsayosAsociados(act);
  const totalResp = encuesta?.respuestas?.length || 0;
  const asisten   = encuesta?.respuestas?.filter(r => r.asiste === true)  || [];
  const noAsisten = encuesta?.respuestas?.filter(r => r.asiste === false) || [];
  const sinResp   = encuesta?.respuestas?.filter(r => r.asiste === null)  || [];

  return (
    <Box maxW="1200px" mx="auto" px={{ base: 2, md: 4 }} py={{ base: 2, md: 4 }}>
      <VStack gap={4} align="stretch">
        {isReadOnly ? (
          <Alert.Root status="info" variant="subtle" borderRadius="lg">
            <Alert.Indicator />
            <Box>
              <Text fontWeight="700">Consulta de histórico</Text>
              <Text fontSize="sm">
                Esta actuación ya terminó según su fecha y hora. Solo puedes revisar la información guardada; no se puede
                editar ni borrar.
              </Text>
            </Box>
          </Alert.Root>
        ) : null}
        <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
          <HStack>
            <IconButton variant="ghost" onClick={onBack} aria-label="Volver"><FaArrowLeft /></IconButton>
            <Heading size="md" lineClamp={1}>{act.nombre}</Heading>
            <Badge colorPalette={ESTADO_COLORS[act.estado]}>{ESTADO_LABELS[act.estado]}</Badge>
          </HStack>
          <HStack>
            {!isReadOnly ? (
              <Button size="sm" colorPalette="whatsapp" onClick={handleShareWhatsApp}><FaWhatsapp />WhatsApp
                              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={onPdfOpen}
              loading={pdfGenerando}
              loadingText="Generando…"><FaFilePdf />PDF
                          </Button>
            <Button
              size="sm"
              colorPalette="purple"
              variant="solid"
              onClick={openRrssCartelModal}><FaInstagram />Cartel para RRSS
                          </Button>
          </HStack>
        </Flex>

        <Tabs.Root
          variant='subtle'
          colorPalette="purple"
          size="sm"
          lazyMount
          value={tabsControlledValue(mainTabIndex)}
          onValueChange={tabsOnValueChange((idx) => {
            setMainTabIndex(idx);
            if (idx === ACT_TAB.ASISTENCIA) loadBajaAsistencia();
            if (idx === ACT_TAB.REFUERZOS) { loadVacantes(); loadAgendaRefuerzos(); }
            if (idx === ACT_TAB.FORMACION) loadFormacion();
          })}
        >
          <Tabs.List flexWrap="wrap" gap={1}>
            <Tab value="0" fontWeight="600">Ficha</Tab>
            <Tab value="1" fontWeight="600">
              <HStack gap={1}>
                <Text>Ensayos</Text>
                {ensayosAsociados.length > 0 ? (
                  <Badge colorPalette="purple" fontSize="2xs" rounded="full" px={1.5}>
                    {ensayosAsociados.length}
                  </Badge>
                ) : null}
              </HStack>
            </Tab>
            <Tab value="2" fontWeight="600">Asistencia</Tab>
            <Tab value="3" fontWeight="600">Refuerzos</Tab>
            <Tab value="4" fontWeight="600">Formación</Tab>
            <Tab value="5" fontWeight="600">Repertorio</Tab>
          </Tabs.List>

          <TabPanels>
            {/* ===== FICHA ===== */}
            <TabPanel value="0" px={0}>
              {editMode ? (
                /* ── Modo edición ── */
                <Card.Root bg={cardBg} shadow="md" rounded="xl">
                  <Card.Body>
                    <Flex justify="flex-end" mb={4}>
                      <HStack>
                        <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>Cancelar</Button>
                        <Button size="sm" colorPalette="purple" onClick={handleSave} loading={saving}><FaSave />Guardar cambios
                                                  </Button>
                      </HStack>
                    </Flex>
                    <ActuacionDatosFormFields
                      form={editForm}
                      setForm={setEditForm}
                      plantillas={plantillas}
                      toast={toast}
                      enabled={editMode}
                      ubicLlegadaDistinta={ubicLlegadaDistinta}
                      setUbicLlegadaDistinta={setUbicLlegadaDistinta}
                      searchResetKey={datosFormSearchResetKey}
                    />
                  </Card.Body>
                </Card.Root>
              ) : (
                /* ── Modo vista: Ficha ── */
                <VStack gap={5} align="stretch">

                  {/* ── Barra superior: botón editar ── */}
                  {!isReadOnly ? (
                    <Flex justify="flex-end">
                      <Button
                        colorPalette="purple"
                        size="sm"
                        onClick={() => {
                          setDatosFormSearchResetKey((k) => k + 1);
                          setEditMode(true);
                        }}><FaEdit />Editar ficha
                                              </Button>
                    </Flex>
                  ) : null}

                  {/* ── Hero: cartel ── */}
                  <Box
                    position="relative"
                    rounded="2xl"
                    overflow="hidden"
                    shadow="xl"
                    minH="200px"
                    bg="gray.900"
                  >
                    {act.cartelUrl ? (
                      <Image
                        src={act.cartelUrl}
                        w="full"
                        maxH="460px"
                        objectFit="cover"
                        display="block"
                      />
                    ) : (
                      <Flex
                        minH="200px"
                        bgGradient="linear(135deg, purple.700, purple.500, purple.300)"
                        align="center"
                        justify="center"
                      >
                        <Icon as={FaMusic} boxSize={20} color="whiteAlpha.200" />
                      </Flex>
                    )}
                    {/* Overlay con nombre y badges */}
                    <Box
                      position="absolute"
                      bottom={0} left={0} right={0}
                      bgGradient="linear(to-t, blackAlpha.800, blackAlpha.500, transparent)"
                      pt={12} pb={5} px={5}
                    >
                      <HStack mb={2} gap={2} flexWrap="wrap">
                        <Badge
                          colorPalette="purple"
                          variant="solid"
                          fontSize="xs"
                          px={3} py={1}
                          rounded="full"
                          textTransform="none"
                        >
                          {labelTipoActuacion(act.tipo, tiposFormacionCatalogo)}
                        </Badge>
                        <Badge
                          colorPalette={ESTADO_COLORS[act.estado]}
                          variant="solid"
                          fontSize="xs"
                          px={3} py={1}
                          rounded="full"
                          textTransform="none"
                        >
                          {ESTADO_LABELS[act.estado]}
                        </Badge>
                      </HStack>
                      <Heading size="lg" color="white" lineClamp={2}>
                        {act.nombre}
                      </Heading>
                    </Box>
                    {/* Botón ver a tamaño real (arriba izquierda, solo con cartel) */}
                    {act.cartelUrl ? (
                      <Box position="absolute" top={3} left={3}>
                        <Button
                          size="xs"
                          bg="whiteAlpha.800"
                          color="gray.800"
                          _hover={{ bg: 'white' }}
                          shadow="md"
                          onClick={onCartelOpen}><FaEye />Ver a tamaño real
                                                  </Button>
                      </Box>
                    ) : null}
                    {/* Controles de cartel (arriba derecha) */}
                    {!isReadOnly ? (
                      <Box position="absolute" top={3} right={3}>
                        <input
                          ref={cartelInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          style={{ display: 'none' }}
                          onChange={handleCartelFileChange}
                        />
                        <HStack gap={2}>
                          <Button
                            size="xs"
                            bg="whiteAlpha.800"
                            color="gray.800"
                            _hover={{ bg: 'white' }}
                            shadow="md"
                            onClick={() => cartelInputRef.current?.click()}
                            loading={cartelUploading}><FaImage />{act.cartelUrl ? 'Cambiar cartel' : 'Subir cartel'}</Button>
                          {act.cartelUrl ? (
                            <Button
                              size="xs"
                              bg="whiteAlpha.800"
                              color="red.500"
                              _hover={{ bg: 'white' }}
                              shadow="md"
                              onClick={handleRemoveCartel}
                              disabled={cartelUploading}
                            >
                              Quitar
                            </Button>
                          ) : null}
                        </HStack>
                      </Box>
                    ) : null}
                  </Box>

                  {/* ── Chips de fecha / hora ── */}
                  {(act.fecha || formatActuacionHorario(act)) ? (
                    <HStack gap={3} flexWrap="wrap">
                      {act.fecha ? (
                        <HStack bg={infoBg} px={4} py={2} rounded="xl" gap={2} shadow="sm">
                          <Icon as={FaCalendarAlt} color="purple.500" boxSize={4} />
                          <Text fontWeight="700" fontSize="sm">
                            {new Date(act.fecha).toLocaleDateString('es-ES', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </Text>
                        </HStack>
                      ) : null}
                      {formatActuacionHorario(act) ? (
                        <HStack bg={infoBg} px={4} py={2} rounded="xl" gap={2} shadow="sm">
                          <Icon as={FaClock} color="purple.500" boxSize={4} />
                          <Text fontWeight="700" fontSize="sm">
                            {formatActuacionHorario(act)}
                          </Text>
                        </HStack>
                      ) : null}
                    </HStack>
                  ) : null}

                  {/* ── Grid principal ── */}
                  <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>

                    {/* Columna izquierda: Lugar + Autobús */}
                    <VStack gap={4} align="stretch">
                      {(act.ubicacion || act.ubicacionFin ||
                        (act.lat != null && act.lng != null && !Number.isNaN(Number(act.lat)) && !Number.isNaN(Number(act.lng)))
                      ) ? (
                        <Card.Root rounded="xl" shadow="sm" overflow="hidden" bg={cardBg}>
                          <Box bg={sectionHeaderBg} px={4} py={2}>
                            <HStack gap={2}>
                              <Icon as={FaMapMarkerAlt} color="white" boxSize={4} />
                              <Text color="white" fontWeight="700" fontSize="sm">Lugar</Text>
                            </HStack>
                          </Box>
                          <Card.Body p={4}>
                            <VStack align="start" gap={4}>
                              {act.ubicacion ? (
                                <Box>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="700"
                                    color="gray.500"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    mb={1}
                                  >
                                    Inicio
                                  </Text>
                                  <HStack align="start" gap={2}>
                                    <Icon as={FaMapMarkerAlt} color="purple.500" mt="2px" />
                                    <Text fontSize="sm">{act.ubicacion}</Text>
                                  </HStack>
                                </Box>
                              ) : null}
                              {act.ubicacionFin ? (
                                <Box>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="700"
                                    color="gray.500"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    mb={1}
                                  >
                                    Llegada
                                  </Text>
                                  <HStack align="start" gap={2}>
                                    <Icon as={FaMapMarkerAlt} color="red.400" mt="2px" />
                                    <Text fontSize="sm">{act.ubicacionFin}</Text>
                                  </HStack>
                                </Box>
                              ) : null}
                              {(() => {
                                const hasIni =
                                  act.lat != null && act.lng != null &&
                                  !Number.isNaN(Number(act.lat)) && !Number.isNaN(Number(act.lng));
                                const hasFin =
                                  act.latFin != null && act.lngFin != null &&
                                  !Number.isNaN(Number(act.latFin)) && !Number.isNaN(Number(act.lngFin));
                                const hasBus =
                                  act.necesidadAutobus &&
                                  act.autobusLat != null && act.autobusLng != null &&
                                  !Number.isNaN(Number(act.autobusLat)) && !Number.isNaN(Number(act.autobusLng));
                                if (!hasIni && !hasFin && !hasBus) return null;
                                return (
                                  <Box w="full" rounded="lg" overflow="hidden" borderWidth="1px" borderColor="gray.200" _dark={{ borderColor: 'gray.600' }}>
                                    <ActuacionMapPreview
                                      lat={act.lat}
                                      lng={act.lng}
                                      latFin={hasFin ? act.latFin : null}
                                      lngFin={hasFin ? act.lngFin : null}
                                      latAutobus={hasBus ? act.autobusLat : null}
                                      lngAutobus={hasBus ? act.autobusLng : null}
                                      height="220px"
                                    />
                                  </Box>
                                );
                              })()}
                            </VStack>
                          </Card.Body>
                        </Card.Root>
                      ) : null}

                      {act.necesidadAutobus ? (
                        <Card.Root rounded="xl" shadow="sm" overflow="hidden" bg={cardBg}>
                          <Box bg="blue.500" px={4} py={2} _dark={{ bg: 'blue.700' }}>
                            <HStack gap={2}>
                              <Icon as={FaBus} color="white" boxSize={4} />
                              <Text color="white" fontWeight="700" fontSize="sm">Autobús</Text>
                            </HStack>
                          </Box>
                          <Card.Body p={4}>
                            {!act.autobusHoraSalida?.trim() && !act.autobusUbicacion?.trim() ? (
                              <Text fontSize="sm" color="gray.500">
                                Aún no se ha indicado el punto de recogida ni la hora de salida.
                              </Text>
                            ) : (
                              <VStack align="stretch" gap={4}>
                                {act.autobusHoraSalida?.trim() ? (
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="700"
                                      color="gray.500"
                                      textTransform="uppercase"
                                      letterSpacing="wide"
                                      mb={1}
                                    >
                                      Hora de salida
                                    </Text>
                                    <HStack gap={2}>
                                      <Icon as={FaClock} color="blue.500" />
                                      <Text fontSize="sm" fontWeight="600">{act.autobusHoraSalida.trim()}</Text>
                                    </HStack>
                                  </Box>
                                ) : null}
                                {act.autobusUbicacion?.trim() ? (
                                  <Box>
                                    <Text
                                      fontSize="xs"
                                      fontWeight="700"
                                      color="gray.500"
                                      textTransform="uppercase"
                                      letterSpacing="wide"
                                      mb={1}
                                    >
                                      Punto de recogida
                                    </Text>
                                    <HStack align="start" gap={2}>
                                      <Icon as={FaMapMarkerAlt} color="blue.500" mt="2px" />
                                      <Text fontSize="sm">{act.autobusUbicacion.trim()}</Text>
                                    </HStack>
                                  </Box>
                                ) : null}
                                {act.autobusLat != null &&
                                  act.autobusLng != null &&
                                  !Number.isNaN(Number(act.autobusLat)) &&
                                  !Number.isNaN(Number(act.autobusLng)) ? (
                                    <Box rounded="lg" overflow="hidden" borderWidth="1px" borderColor="blue.200" _dark={{ borderColor: 'blue.700' }}>
                                      <ActuacionMapPreview
                                        latAutobus={act.autobusLat}
                                        lngAutobus={act.autobusLng}
                                        height="160px"
                                      />
                                    </Box>
                                  ) : null}
                              </VStack>
                            )}
                          </Card.Body>
                        </Card.Root>
                      ) : null}
                    </VStack>

                    {/* Columna derecha: Detalles */}
                    <Card.Root rounded="xl" shadow="sm" overflow="hidden" bg={cardBg}>
                      <Box bg={sectionHeaderBg} px={4} py={2}>
                        <HStack gap={2}>
                          <Icon as={FaMusic} color="white" boxSize={4} />
                          <Text color="white" fontWeight="700" fontSize="sm">Detalles</Text>
                        </HStack>
                      </Box>
                      <Card.Body p={4}>
                        <VStack align="start" gap={4}>
                          <Box>
                            <Text
                              fontSize="xs"
                              fontWeight="700"
                              color="gray.500"
                              textTransform="uppercase"
                              letterSpacing="wide"
                              mb={1}
                            >
                              Tipo
                            </Text>
                            <Badge colorPalette="purple" variant="subtle" px={3} py={1} rounded="full" fontSize="sm">
                              {labelTipoActuacion(act.tipo, tiposFormacionCatalogo)}
                            </Badge>
                          </Box>
                          {act.uniforme ? (
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                                mb={1}
                              >
                                Uniforme
                              </Text>
                              <Text fontSize="sm">{act.uniforme}</Text>
                            </Box>
                          ) : null}
                          {act.plantilla ? (
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                                mb={1}
                              >
                                Plantilla
                              </Text>
                              <HStack gap={2}>
                                <Icon as={FaThList} color="purple.500" boxSize={3} />
                                <Text fontSize="sm">{act.plantilla.nombre}</Text>
                              </HStack>
                            </Box>
                          ) : null}
                          {act.enlacePartituras ? (
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                                mb={1}
                              >
                                Partituras
                              </Text>
                              <Link
                                href={act.enlacePartituras}
                                color="purple.500"
                                fontSize="sm"
                                target='_blank'
                                rel='noopener noreferrer'>
                                <HStack gap={1}>
                                  <Icon as={FaLink} />
                                  <Text>Ver partituras</Text>
                                  <Icon as={FaExternalLinkAlt} boxSize={3} />
                                </HStack>
                              </Link>
                            </Box>
                          ) : null}
                          {act.descripcion ? (
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                                mb={1}
                              >
                                Descripción
                              </Text>
                              <Text fontSize="sm" whiteSpace="pre-wrap">{act.descripcion}</Text>
                            </Box>
                          ) : null}
                          {act.observaciones ? (
                            <Box>
                              <Text
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wide"
                                mb={1}
                              >
                                Observaciones
                              </Text>
                              <Text fontSize="sm" whiteSpace="pre-wrap">{act.observaciones}</Text>
                            </Box>
                          ) : null}
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                  </SimpleGrid>

                  {/* ── Repertorio ── */}
                  {(() => {
                    const items = act.repertorio || [];
                    if (!items.length) return null;
                    return (
                      <Card.Root rounded="xl" shadow="sm" overflow="hidden" bg={cardBg}>
                        <Box bg={sectionHeaderBg} px={4} py={2}>
                          <Flex justify="space-between" align="center">
                            <HStack gap={2}>
                              <Icon as={FaMusic} color="white" boxSize={4} />
                              <Text color="white" fontWeight="700" fontSize="sm">
                                Repertorio
                              </Text>
                              <Badge
                                bg="whiteAlpha.300"
                                color="white"
                                fontSize="xs"
                                px={2}
                                rounded="full"
                              >
                                {items.length}
                              </Badge>
                            </HStack>
                            <Button
                              size="xs"
                              bg="whiteAlpha.300"
                              color="white"
                              _hover={{ bg: 'whiteAlpha.500' }}
                              onClick={() => setMainTabIndex(ACT_TAB.REPERTORIO)}
                            >
                              Ver completo
                            </Button>
                          </Flex>
                        </Box>
                        <Card.Body p={0}>
                          <VStack gap={0} align="stretch">{items.map((item, idx) => (
                              <Flex
                                key={item.id || idx}
                                align="center"
                                px={4}
                                py={2.5}
                                gap={3}
                                _hover={{ bg: infoBg }}
                                transition="background 0.15s"
                              >
                                {/* Número */}
                                <Text
                                  fontSize="xs"
                                  fontWeight="700"
                                  color="purple.400"
                                  w="22px"
                                  textAlign="right"
                                  flexShrink={0}
                                >
                                  {idx + 1}
                                </Text>
                                {/* Título + autor */}
                                <Box flex={1} minW={0}>
                                  <HStack gap={2} flexWrap="wrap">
                                    <Text fontSize="sm" fontWeight="600" lineClamp={2}>
                                      {item.titulo}
                                    </Text>
                                    {isObraCarpetaLine(item) && item.nombreCarpeta ? (
                                      <Badge
                                        colorPalette="purple"
                                        variant="subtle"
                                        fontSize="10px"
                                        flexShrink={0}
                                        display="inline-flex"
                                        alignItems="center"
                                        gap="4px"
                                      >
                                        <Icon as={FaFolder} boxSize="9px" />
                                        {item.nombreCarpeta}
                                      </Badge>
                                    ) : null}
                                  </HStack>
                                  {item.autor ? (
                                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                                      {item.autor}
                                    </Text>
                                  ) : null}
                                </Box>
                                {/* Enlace externo */}
                                {item.enlace ? (
                                  <Link
                                    href={item.enlace}
                                    color="purple.400"
                                    onClick={(e) => e.stopPropagation()}
                                    flexShrink={0}
                                    target='_blank'
                                    rel='noopener noreferrer'>
                                    <Icon as={FaExternalLinkAlt} boxSize={3} />
                                  </Link>
                                ) : null}
                              </Flex>
                            ))}</VStack>
                        </Card.Body>
                      </Card.Root>
                    );
                  })()}

                </VStack>
              )}
            </TabPanel>

            {/* ===== ENSAYOS DE PREPARACIÓN ===== */}
            <TabPanel value="1" px={0}>
              <EnsayosPreparacionPanel
                ensayosAsociados={ensayosAsociados}
                cardBg={cardBg}
                infoBg={infoBg}
                sectionHeaderBg={sectionHeaderBg}
                navigate={navigate}
              />
            </TabPanel>

            {/* ===== ASISTENCIA ===== */}
            <TabPanel value="2" px={0}>
              <VStack gap={4} align="stretch">
                {bajaAsistenciaIds.length > 0 && (
                  <Alert.Root
                    status="warning"
                    rounded="xl"
                    variant='subtle'
                    borderStartWidth='3px'
                    borderStartColor='colorPalette.solid'>
                    <Alert.Indicator />
                    <Box>
                      <Text fontWeight="600" fontSize="sm">
                        {bajaAsistenciaIds.length} músico{bajaAsistenciaIds.length > 1 ? 's' : ''} con baja asistencia a ensayos vinculados
                      </Text>
                      <Text fontSize="xs" color="orange.800">
                        Han faltado a más del 50% de los ensayos de esta actuación. Están marcados en naranja en la tabla.
                      </Text>
                    </Box>
                  </Alert.Root>
                )}
                {!encuesta ? (
                  <Card.Root bg={cardBg} shadow="md" rounded="xl">
                    <Card.Body textAlign="center" py={8}>
                      <Icon as={FaUsers} boxSize={10} color="gray.300" mb={3} />
                      <Heading size="sm" color="gray.500" mb={2}>No hay encuesta de asistencia</Heading>
                      <Text fontSize="sm" color="gray.400" mb={4}>
                        {isReadOnly
                          ? 'No consta encuesta para esta actuación en el histórico.'
                          : 'Crea una encuesta para que tus músicos confirmen su asistencia.'}
                      </Text>
                      {!isReadOnly ? <Button colorPalette="purple" onClick={handleCrearEncuesta}>Crear encuesta</Button> : null}
                    </Card.Body>
                  </Card.Root>
                ) : (
                  <>
                    <Card.Root bg={cardBg} shadow="md" rounded="xl">
                      <Card.Body>
                        <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                          <HStack>
                            <Badge colorPalette={encuesta.estado === 'ABIERTA' ? 'green' : 'red'} fontSize="sm">{encuesta.estado}</Badge>
                            <Text fontSize="sm" color="gray.500">{totalResp} músicos</Text>
                          </HStack>
                          <HStack flexWrap="wrap">
                            {encuesta.estado === 'ABIERTA' ? (
                              <Button
                                size="xs"
                                colorPalette="whatsapp"
                                onClick={handleWhatsappEncuestaGrupo}
                                disabled={!encuesta.shareToken}
                                data-testid="encuesta-grupo-wa"><Icon as={FaWhatsapp} boxSize={3} />Compartir encuesta al grupo
                                                              </Button>
                            ) : null}
                            {encuesta.estado === 'ABIERTA' && !isReadOnly ? (
                              <Button size="xs" colorPalette="purple" onClick={onPasarListaOpen}><Icon as={FaUserCheck} boxSize={3} />Pasar lista
                                                              </Button>
                            ) : null}
                            {(!isReadOnly || encuesta.estado === 'ABIERTA') ? (
                              <>
                                {encuesta.estado === 'ABIERTA' && (
                                  <Button size="xs" colorPalette="red" variant="outline" onClick={onCloseEncuestaOpen}>
                                    Cerrar encuesta
                                  </Button>
                                )}
                                {encuesta.estado === 'CERRADA' && (
                                  <Button size="xs" colorPalette="yellow" variant="outline" onClick={onReopenOpen}><Icon as={FaSync} boxSize={3} />Reabrir encuesta
                                                                      </Button>
                                )}
                              </>
                            ) : null}
                          </HStack>
                        </Flex>
                        <SimpleGrid columns={4} gap={2} mb={4}>
                          <Card.Root bg="green.50" rounded="lg"><Card.Body py={2} textAlign="center"><Text fontSize="2xl" fontWeight="700" color="green.600">{asisten.length}</Text><Text fontSize="xs" color="green.500">Asisten</Text></Card.Body></Card.Root>
                          <Card.Root bg="red.50" rounded="lg"><Card.Body py={2} textAlign="center"><Text fontSize="2xl" fontWeight="700" color="red.600">{noAsisten.length}</Text><Text fontSize="xs" color="red.500">No asisten</Text></Card.Body></Card.Root>
                          <Card.Root bg="gray.50" rounded="lg"><Card.Body py={2} textAlign="center"><Text fontSize="2xl" fontWeight="700" color="gray.600">{sinResp.length}</Text><Text fontSize="xs" color="gray.500">Sin responder</Text></Card.Body></Card.Root>
                          <Card.Root bg="purple.50" rounded="lg"><Card.Body py={2} textAlign="center"><Text fontSize="2xl" fontWeight="700" color="purple.600">{totalResp}</Text><Text fontSize="xs" color="purple.500">Total</Text></Card.Body></Card.Root>
                        </SimpleGrid>
                      </Card.Body>
                    </Card.Root>
                    {/* Filtros y ordenación */}
                    <Card.Root bg={cardBg} shadow="md" rounded="xl">
                      <Card.Body>
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} gap={2} mb={2}>
                          <Input
                            size="sm"
                            placeholder="Buscar nombre…"
                            value={filtroNombre}
                            onChange={e => setFiltroNombre(e.target.value)}
                          />
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={filtroFamilia}
                              onChange={e => setFiltroFamilia(e.target.value)}>
                              <option value="">Todas las familias</option>
                              <option value="VIENTO_METAL">Viento Metal</option>
                              <option value="VIENTO_MADERA">Viento Madera</option>
                              <option value="PERCUSION">Percusión</option>
                              <option value="CUERDA">Cuerda</option>
                              <option value="VOZ">Voz</option>
                              <option value="OTRO">Otro</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={filtroAsistencia}
                              onChange={e => setFiltroAsistencia(e.target.value)}>
                              <option value="">Toda la asistencia</option>
                              <option value="si">✓ Asisten</option>
                              <option value="no">✗ No asisten</option>
                              <option value="sin">? Sin responder</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                          {act.necesidadAutobus && (
                            <NativeSelect.Root>
                              <NativeSelect.Field
                                size="sm"
                                value={filtroAutobus}
                                onChange={e => setFiltroAutobus(e.target.value)}>
                                <option value="">Todos (autobús)</option>
                                <option value="si">🚌 Necesita bus</option>
                                <option value="no">✗ No necesita bus</option>
                                <option value="sin">? Sin responder bus</option>
                              </NativeSelect.Field>
                              <NativeSelect.Indicator />
                            </NativeSelect.Root>
                          )}
                          <NativeSelect.Root>
                            <NativeSelect.Field
                              size="sm"
                              value={ordenAsistencia}
                              onChange={e => setOrdenAsistencia(e.target.value)}>
                              <option value="nombre">Ordenar: A–Z</option>
                              <option value="familia">Ordenar: Familia</option>
                              <option value="asistencia">Ordenar: Asistencia</option>
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                        </SimpleGrid>
                        {(filtroNombre || filtroFamilia || filtroAsistencia || filtroAutobus) && (
                          <HStack mb={2} gap={2} flexWrap="wrap">
                            <Text fontSize="xs" color="gray.500">
                              Mostrando {respuestasFiltradas.length} de {totalResp}
                            </Text>
                            <Button size="xs" variant="ghost" colorPalette="gray"
                              onClick={() => { setFiltroNombre(''); setFiltroFamilia(''); setFiltroAsistencia(''); setFiltroAutobus(''); }}>
                              Limpiar filtros
                            </Button>
                          </HStack>
                        )}
                      </Card.Body>
                    </Card.Root>

                    <Card.Root bg={cardBg} shadow="md" rounded="xl">
                      <Card.Body>
                        <Box overflowX="auto">
                          <Table.Root size="sm" variant="simple">
                            <Table.Header>
                              <Table.Row>
                                <Table.ColumnHeader>Músico</Table.ColumnHeader>
                                <Table.ColumnHeader>Instrumento</Table.ColumnHeader>
                                <Table.ColumnHeader>Asistencia</Table.ColumnHeader>
                                {act.necesidadAutobus && <Table.ColumnHeader><Icon as={FaBus} /></Table.ColumnHeader>}
                                <Table.ColumnHeader>Acciones</Table.ColumnHeader>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {respuestasFiltradas.length === 0 ? (
                                <Table.Row><Table.Cell colSpan={act.necesidadAutobus ? 5 : 4} textAlign="center" color="gray.400" py={6} fontSize="sm">Ningún músico coincide con los filtros.</Table.Cell></Table.Row>
                              ) : respuestasFiltradas.map(r => {
                                const isEditing = editingRespuestaId === r.id;
                                const draft = editingRespuestaValues;
                                const draftJust = draft.estado === 'JUSTIFICADO';
                                const draftPendiente =
                                  draft.estado === 'PENDIENTE' ||
                                  ((!draft.estado || draft.estado === undefined) && draft.asiste == null && !draftJust);
                                const isBajaAsistencia = bajaAsistenciaIds.includes(r.musico.id);
                                const bajaDetalle = isBajaAsistencia ? bajaAsistenciaDetalle.find(d => d.id === r.musico.id) : null;
                                return (
                                  <Table.Row key={r.id} bg={isEditing ? infoBg : isBajaAsistencia ? 'orange.50' : undefined}>
                                    {/* Músico */}
                                    <Table.Cell fontWeight="500" whiteSpace="nowrap">
                                      <HStack gap={2}>
                                        <Text>{r.musico.nombre} {r.musico.apellidos || ''}</Text>
                                        {isBajaAsistencia && (
                                          <Tooltip
                                            content={bajaDetalle ? `Baja asistencia a ensayos: ${bajaDetalle.ausencias}/${bajaDetalle.totalEnsayos} ausencias (${bajaDetalle.porcentajeAusencia}%)` : 'Más del 50% de ausencias en ensayos vinculados'}
                                            showArrow
                                            bg="orange.600"
                                          >
                                            <Badge colorPalette="orange" fontSize="2xs" variant="solid" cursor="help">
                                              <Icon as={FaExclamationTriangle} mr={1} />
                                              Ensayos
                                            </Badge>
                                          </Tooltip>
                                        )}
                                      </HStack>
                                    </Table.Cell>
                                    {/* Instrumento */}
                                    <Table.Cell fontSize="xs">
                                      <Badge colorPalette={FAMILIA_COLORS[primeraFamiliaMusico(r.musico)] || 'gray'} fontSize="2xs">
                                        {formatMusicoInstrumentosText(r.musico)}
                                      </Badge>
                                    </Table.Cell>
                                    {/* Asistencia */}
                                    <Table.Cell>
                                      {isEditing ? (
                                        <HStack gap={1}>
                                          <IconButton
                                            size="xs"
                                            colorPalette={draft.estado === 'ASISTE' || (!draft.estado && draft.asiste === true) ? 'green' : 'gray'}
                                            variant={draft.estado === 'ASISTE' || (!draft.estado && draft.asiste === true) ? 'solid' : 'outline'}
                                            aria-label="Asiste"
                                            onClick={() => setEditingRespuestaValues(v => ({ ...v, asiste: true, estado: 'ASISTE' }))}><FaCheck /></IconButton>
                                          <IconButton
                                            size="xs"
                                            colorPalette={draft.estado === 'NO_ASISTE' || (!draft.estado && draft.asiste === false) ? 'red' : 'gray'}
                                            variant={draft.estado === 'NO_ASISTE' || (!draft.estado && draft.asiste === false) ? 'solid' : 'outline'}
                                            aria-label="No asiste"
                                            onClick={() => setEditingRespuestaValues(v => ({ ...v, asiste: false, estado: 'NO_ASISTE' }))}><FaTimes /></IconButton>
                                          <IconButton
                                            size="xs"
                                            colorPalette={draftPendiente ? 'purple' : 'gray'}
                                            variant={draftPendiente ? 'solid' : 'outline'}
                                            aria-label="Sin respuesta"
                                            onClick={() => setEditingRespuestaValues(v => ({ ...v, asiste: null, estado: 'PENDIENTE' }))}><FaQuestion /></IconButton>
                                          <IconButton
                                            size="xs"
                                            colorPalette={draft.estado === 'JUSTIFICADO' ? 'blue' : 'gray'}
                                            variant={draft.estado === 'JUSTIFICADO' ? 'solid' : 'outline'}
                                            aria-label="Justificado"
                                            onClick={() => setEditingRespuestaValues(v => ({ ...v, asiste: null, estado: 'JUSTIFICADO' }))}><FaShieldAlt /></IconButton>
                                        </HStack>
                                      ) : (
                                        <>
                                          {r.estado === 'JUSTIFICADO' ? (
                                            <Badge colorPalette="blue"><Icon as={FaShieldAlt} mr={1} />Justificado</Badge>
                                          ) : (
                                            <>
                                              {r.asiste === null && <Badge colorPalette="gray"><Icon as={FaQuestion} /></Badge>}
                                              {r.asiste === true && <Badge colorPalette="green"><Icon as={FaCheck} /></Badge>}
                                              {r.asiste === false && <Badge colorPalette="red"><Icon as={FaTimes} /></Badge>}
                                            </>
                                          )}
                                        </>
                                      )}
                                    </Table.Cell>
                                    {/* Autobús */}
                                    {act.necesidadAutobus && (
                                      <Table.Cell>
                                        {isEditing ? (
                                          <HStack gap={1}>
                                            <IconButton
                                              size="xs"
                                              colorPalette={draft.necesitaAutobus === true ? 'blue' : 'gray'}
                                              variant={draft.necesitaAutobus === true ? 'solid' : 'outline'}
                                              aria-label="Necesita bus"
                                              onClick={() => setEditingRespuestaValues(v => ({ ...v, necesitaAutobus: true }))}><FaBus /></IconButton>
                                            <IconButton
                                              size="xs"
                                              colorPalette={draft.necesitaAutobus === false ? 'red' : 'gray'}
                                              variant={draft.necesitaAutobus === false ? 'solid' : 'outline'}
                                              aria-label="No necesita bus"
                                              onClick={() => setEditingRespuestaValues(v => ({ ...v, necesitaAutobus: false }))}><FaTimes /></IconButton>
                                          </HStack>
                                        ) : (
                                          r.necesitaAutobus === true
                                            ? <Icon as={FaBus} color="blue.500" />
                                            : r.necesitaAutobus === false
                                              ? <Icon as={FaTimes} color="gray.400" />
                                              : <Text color="gray.400" fontSize="xs">—</Text>
                                        )}
                                      </Table.Cell>
                                    )}
                                    {/* Acciones */}
                                    <Table.Cell>
                                      {isEditing ? (
                                        <HStack gap={1}>
                                          <Button
                                            size="xs"
                                            colorPalette="purple"
                                            onClick={() => saveEditRespuesta(r.id)}
                                            loading={savingRespuesta}
                                          >
                                            Guardar
                                          </Button>
                                          <Button size="xs" variant="ghost" onClick={cancelEditRespuesta}>
                                            Cancelar
                                          </Button>
                                        </HStack>
                                      ) : (
                                        <HStack gap={1}>
                                          <Tooltip content="Compartir encuesta individual para controlar la asistencia" showArrow>
                                            <IconButton
                                              size="sm"
                                              variant="outline"
                                              colorPalette="whatsapp"
                                              onClick={() => handleWhatsappEncuestaAsistencia(r)}
                                              aria-label="Compartir encuesta por WhatsApp"><FaWhatsapp /></IconButton>
                                          </Tooltip>
                                          {(!isReadOnly || encuesta?.estado === 'ABIERTA') && (
                                            <IconButton
                                              size="sm"
                                              variant="outline"
                                              colorPalette="purple"
                                              onClick={() => startEditRespuesta(r)}
                                              aria-label="Editar respuesta"
                                              title="Editar respuesta manualmente"><FaEdit /></IconButton>
                                          )}
                                        </HStack>
                                      )}
                                    </Table.Cell>
                                  </Table.Row>
                                );
                              })}
                            </Table.Body>
                          </Table.Root>
                        </Box>
                      </Card.Body>
                    </Card.Root>
                  </>
                )}
              </VStack>
            </TabPanel>

            {/* ===== REFUERZOS ===== */}
            <TabPanel value="3" px={0}>
              <VStack gap={4} align="stretch">
                {/* ── Necesidades (vacantes por no asistencia) ── */}
                <Card.Root bg={cardBg} shadow="md" rounded="xl">
                  <Card.Body>
                    <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                      <HStack gap={2}>
                        <Heading size="sm">Necesidades</Heading>
                        {vacantes?.vacantes && (
                          <Badge colorPalette={vacantes.vacantes.length > 0 ? 'red' : 'green'}>
                            {vacantes.vacantes.length > 0 ? `${vacantes.vacantes.length} plaza${vacantes.vacantes.length > 1 ? 's' : ''}` : 'Sin bajas'}
                          </Badge>
                        )}
                      </HStack>
                      <HStack gap={2}>
                        {/* Toggle lista / mosaico */}
                        <HStack gap={0} border="1px solid" borderColor="gray.200" rounded="md" overflow="hidden" _dark={{ borderColor: 'gray.600' }}>
                          <IconButton
                            size="xs"
                            variant={vacantesVista === 'lista' ? 'solid' : 'ghost'}
                            colorPalette={vacantesVista === 'lista' ? 'purple' : 'gray'}
                            aria-label="Vista lista"
                            rounded="none"
                            onClick={() => setVacantesVista('lista')}><FaList /></IconButton>
                          <IconButton
                            size="xs"
                            variant={vacantesVista === 'mosaico' ? 'solid' : 'ghost'}
                            colorPalette={vacantesVista === 'mosaico' ? 'purple' : 'gray'}
                            aria-label="Vista mosaico"
                            rounded="none"
                            onClick={() => setVacantesVista('mosaico')}><FaTh /></IconButton>
                        </HStack>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={loadVacantes}
                          loading={vacantesLoading}><FaSync />Actualizar
                                                  </Button>
                      </HStack>
                    </Flex>

                    {vacantesLoading ? (
                      <Flex justify="center" py={6}><Spinner color="purple.500" /></Flex>
                    ) : !vacantes ? (
                      <Alert.Root status="info" rounded="lg"><Alert.Indicator />Pulsa "Actualizar" para ver las plazas vacantes.</Alert.Root>
                    ) : vacantes.sinPlantilla ? (
                      <Alert.Root status="info" rounded="lg"><Alert.Indicator />Asigna una plantilla en la pestaña "Formación" para ver las necesidades.</Alert.Root>
                    ) : vacantes.vacantes.length === 0 ? (
                      <Alert.Root status="success" rounded="lg"><Alert.Indicator />No hay bajas: todos los músicos de la formación han confirmado asistencia o están pendientes.</Alert.Root>
                    ) : (() => {
                      // Mapa posicionId → refuerzo que la cubre
                      const cubiertasMap = {};
                      (act.refuerzos || []).forEach(r => {
                        const m = r.notas?.match(/^\[posicion:([^\]]+)\]/);
                        if (m) cubiertasMap[m[1]] = r;
                      });

                      return vacantesVista === 'mosaico' ? (
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} gap={3}>
                          {vacantes.vacantes.map((v) => {
                            const cubiertaPor = cubiertasMap[v.posicionId];
                            return (
                              <Box
                                key={v.posicionId}
                                border="2px dashed"
                                borderColor={cubiertaPor ? 'green.300' : 'red.300'}
                                rounded="xl" p={3}
                                bg={cubiertaPor ? 'green.50' : 'red.50'}
                                _dark={{ bg: cubiertaPor ? 'green.900' : 'red.900', borderColor: cubiertaPor ? 'green.600' : 'red.600' }}
                              >
                                <Badge colorPalette={FAMILIA_COLORS[v.instrumento?.familia] || 'gray'} fontSize="2xs" mb={1}>
                                  {FAMILIA_LABELS[v.instrumento?.familia] || 'Sin familia'}
                                </Badge>
                                <Text fontWeight="700" fontSize="sm" lineClamp={1}>
                                  {v.instrumento?.nombre || v.label || 'Plaza sin instrumento'}
                                </Text>
                                <Text fontSize="xs" color={cubiertaPor ? 'green.700' : 'red.600'} _dark={{ color: cubiertaPor ? 'green.300' : 'red.300' }} mt={1} lineClamp={1}>
                                  Baja: {v.musicoAusente ? `${v.musicoAusente.nombre} ${v.musicoAusente.apellidos || ''}`.trim() : '—'}
                                </Text>
                                {cubiertaPor ? (
                                  <HStack mt={2} gap={1}>
                                    <Icon as={FaCheck} color="green.500" boxSize={3} />
                                    <Text fontSize="xs" fontWeight="600" color="green.700" _dark={{ color: 'green.300' }} lineClamp={1}>
                                      Cubierta por {cubiertaPor.nombre}
                                    </Text>
                                  </HStack>
                                ) : !isReadOnly && (
                                  <Button
                                    size="xs"
                                    mt={2}
                                    w="full"
                                    colorPalette="purple"
                                    variant="outline"
                                    onClick={() => {
                                      setRefPosicionVacanteId(v.posicionId);
                                      setRefForm(prev => ({ ...prev, instrumentoId: v.instrumento?.id || '', notas: '' }));
                                      setRefModo('seleccionar');
                                      loadAgendaRefuerzos();
                                      onRefOpen();
                                    }}><FaPlus />Añadir refuerzo
                                                                  </Button>
                                )}
                              </Box>
                            );
                          })}
                        </SimpleGrid>
                      ) : (
                        <Table.Root size="sm" variant="simple">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>Instrumento</Table.ColumnHeader>
                              <Table.ColumnHeader>Músico ausente</Table.ColumnHeader>
                              <Table.ColumnHeader>Estado</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {vacantes.vacantes.map((v) => {
                              const cubiertaPor = cubiertasMap[v.posicionId];
                              return (
                                <Table.Row key={v.posicionId} bg={cubiertaPor ? 'green.50' : undefined} _dark={{ bg: cubiertaPor ? 'green.900' : undefined }}>
                                  <Table.Cell>
                                    <HStack gap={1}>
                                      <Badge colorPalette={FAMILIA_COLORS[v.instrumento?.familia] || 'gray'} fontSize="2xs">
                                        {FAMILIA_LABELS[v.instrumento?.familia] || '—'}
                                      </Badge>
                                      <Text fontSize="sm">{v.instrumento?.nombre || v.label || '—'}</Text>
                                    </HStack>
                                  </Table.Cell>
                                  <Table.Cell fontSize="sm" color="red.600" _dark={{ color: 'red.300' }}>
                                    {v.musicoAusente ? `${v.musicoAusente.nombre} ${v.musicoAusente.apellidos || ''}`.trim() : '—'}
                                  </Table.Cell>
                                  <Table.Cell>
                                    {cubiertaPor ? (
                                      <HStack gap={1}>
                                        <Icon as={FaCheck} color="green.500" boxSize={3} />
                                        <Text fontSize="xs" fontWeight="600" color="green.600" _dark={{ color: 'green.300' }} lineClamp={1}>
                                          {cubiertaPor.nombre}
                                        </Text>
                                      </HStack>
                                    ) : !isReadOnly ? (
                                      <Button
                                        size="xs"
                                        colorPalette="purple"
                                        variant="outline"
                                        onClick={() => {
                                          setRefPosicionVacanteId(v.posicionId);
                                          setRefForm(prev => ({ ...prev, instrumentoId: v.instrumento?.id || '', notas: '' }));
                                          setRefModo('seleccionar');
                                          loadAgendaRefuerzos();
                                          onRefOpen();
                                        }}><FaPlus />Añadir
                                                                              </Button>
                                    ) : <Text fontSize="xs" color="gray.400">Sin cubrir</Text>}
                                  </Table.Cell>
                                </Table.Row>
                              );
                            })}
                          </Table.Body>
                        </Table.Root>
                      );
                    })()}
                  </Card.Body>
                </Card.Root>
                {/* ── Agenda de refuerzos ── */}
                <Card.Root bg={cardBg} shadow="md" rounded="xl">
                  <Card.Body>
                    <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
                      <HStack flexWrap="wrap" gap={2}>
                        <Icon as={FaAddressBook} color="purple.400" />
                        <Heading size="sm">Refuerzos</Heading>
                        {(() => {
                          const confirmados  = (act.refuerzos || []).filter(r => r.confirmado).length;
                          const noDisp       = (act.refuerzos || []).filter(r => r.noDisponible).length;
                          const contactados  = (act.refuerzos || []).filter(r => !r.confirmado && !r.noDisponible && r.ultimoContactoEn).length;
                          return (
                            <>
                              {confirmados > 0 && <Badge colorPalette="green">{confirmados} confirmado{confirmados > 1 ? 's' : ''}</Badge>}
                              {contactados > 0 && <Badge colorPalette="blue">{contactados} pendiente{contactados > 1 ? 's' : ''}</Badge>}
                              {noDisp > 0      && <Badge colorPalette="red">{noDisp} no disponible{noDisp > 1 ? 's' : ''}</Badge>}
                            </>
                          );
                        })()}
                      </HStack>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={loadAgendaRefuerzos}
                        loading={agendaLoading}><FaSync />Actualizar
                                              </Button>
                    </Flex>

                    {agendaLoading ? (
                      <Flex justify="center" py={8}><Spinner color="purple.500" /></Flex>
                    ) : agendaRefuerzos.length === 0 ? (
                      <Alert.Root status="info" rounded="lg">
                        <Alert.Indicator />
                        No tienes refuerzos en tu agenda. Ve a Gestión → Contactos y añade tus refuerzos habituales.
                      </Alert.Root>
                    ) : (() => {
                      // Mapa contactoAgendaId → Refuerzo registrado para esta actuación
                      const refMap = new Map(
                        (act.refuerzos || []).filter(r => r.contactoAgendaId).map(r => [r.contactoAgendaId, r])
                      );
                      return (
                        <VStack gap={2} align="stretch">
                          {agendaRefuerzos.map(c => {
                            const ref           = refMap.get(c.id);
                            const isConfirmado  = ref?.confirmado === true;
                            const isNoDisp      = ref?.noDisponible === true;
                            // Contactado = tiene registro pero sin confirmar ni marcar no disponible
                            const isContactado  = !!ref && !isConfirmado && !isNoDisp;
                            const telefono      = c.telefono;
                            const instArr       = Array.isArray(c.instrumentos) ? c.instrumentos : [];
                            const waMsg         = encodeURIComponent(`Hola ${[c.nombre, c.apellidos].filter(Boolean).join(' ')}, te contactamos para la actuación del ${act.fecha ? new Date(act.fecha).toLocaleDateString('es-ES') : ''}.`);

                            // Colores según estado
                            const borderColor = isConfirmado ? 'green.300'
                              : isNoDisp ? 'red.200'
                              : isContactado ? 'blue.200'
                              : 'gray.200';
                            const bgColor = isConfirmado ? 'green.50'
                              : isNoDisp ? 'red.50'
                              : isContactado ? 'blue.50'
                              : 'white';
                            const bgDark = isConfirmado ? 'green.900'
                              : isNoDisp ? 'red.900'
                              : isContactado ? 'blue.900'
                              : 'gray.750';
                            const avatarBg = isConfirmado ? 'green.500'
                              : isNoDisp ? 'red.400'
                              : isContactado ? 'blue.400'
                              : 'purple.400';

                            return (
                              <Box
                                key={c.id}
                                p={3} rounded="xl" borderWidth="2px"
                                borderColor={borderColor} bg={bgColor}
                                _dark={{ borderColor: borderColor, bg: bgDark }}
                                transition="all 0.15s"
                                opacity={isNoDisp ? 0.75 : 1}
                              >
                                <Flex align="start" gap={3}>
                                  {/* Avatar con estado */}
                                  <Box position="relative" flexShrink={0}>
                                    <Avatar.Root size="sm" bg={avatarBg} color="white"><Avatar.Fallback name={(c.apellidos?.[0] || c.nombre?.[0] || '?').toUpperCase()} /></Avatar.Root>
                                    {isConfirmado && (
                                      <Box position="absolute" bottom="-1px" right="-1px"
                                        bg="green.500" rounded="full" w={3} h={3}
                                        display="flex" alignItems="center" justifyContent="center">
                                        <Icon as={FaCheck} color="white" boxSize={1.5} />
                                      </Box>
                                    )}
                                    {isNoDisp && (
                                      <Box position="absolute" bottom="-1px" right="-1px"
                                        bg="red.500" rounded="full" w={3} h={3}
                                        display="flex" alignItems="center" justifyContent="center">
                                        <Icon as={FaTimes} color="white" boxSize={1.5} />
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Info */}
                                  <Box flex="1" minW={0}>
                                    <HStack gap={2} flexWrap="wrap">
                                      <Text fontWeight="700" fontSize="sm" lineClamp={1}
                                        textDecoration={isNoDisp ? 'line-through' : 'none'}
                                        color={isNoDisp ? 'gray.400' : undefined}>
                                        {[c.nombre, c.apellidos].filter(Boolean).join(' ')}
                                      </Text>
                                      {instArr.map((inst, i) => (
                                        <Badge key={i} colorPalette="purple" fontSize="2xs" variant="subtle">
                                          {inst.nombre}{inst.voz ? ` · ${inst.voz}` : ''}
                                        </Badge>
                                      ))}
                                      {(c.localidad || c.provincia) && (
                                        <HStack gap={1}>
                                          <Icon as={FaMapMarkerAlt} boxSize={2.5} color="gray.400" />
                                          <Text fontSize="xs" color="gray.500">
                                            {[c.localidad, c.provincia].filter(Boolean).join(', ')}
                                          </Text>
                                        </HStack>
                                      )}
                                    </HStack>

                                    {/* Badges de estado */}
                                    <HStack gap={2} mt={1} flexWrap="wrap">
                                      {isConfirmado && <Badge colorPalette="green" fontSize="xs">✓ Confirmado</Badge>}
                                      {isNoDisp     && <Badge colorPalette="red"   fontSize="xs">✗ No disponible</Badge>}
                                      {isContactado && <Badge colorPalette="blue"  fontSize="xs">Contactado — pendiente</Badge>}
                                      {!ref         && <Badge colorPalette="gray"  fontSize="xs" variant="subtle">Sin contactar</Badge>}
                                      {ref?.ultimoContactoEn && (
                                        <HStack gap={1}>
                                          <Icon as={FaClock} boxSize={2.5} color="blue.400" />
                                          <Text fontSize="xs" color="blue.500">
                                            {new Date(ref.ultimoContactoEn).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                          </Text>
                                        </HStack>
                                      )}
                                    </HStack>

                                    {/* Baja asignada */}
                                    {ref?.notas && (
                                      <HStack gap={1} mt={0.5}>
                                        <Icon as={FaCheck} color="green.500" boxSize={2.5} />
                                        <Text fontSize="xs" color="green.600" _dark={{ color: 'green.300' }} fontStyle="italic" lineClamp={1}>
                                          {notasLegibles(ref.notas)}
                                        </Text>
                                      </HStack>
                                    )}

                                    {/* Selector de vacante */}
                                    {isConfirmado && !ref?.notas && vacantes?.vacantes?.length > 0 && !isReadOnly && (
                                      <NativeSelect.Root>
                                        <NativeSelect.Field
                                          size="xs"
                                          mt={2}
                                          maxW="280px"
                                          rounded="md"
                                          placeholder="Asignar a baja…"
                                          value=""
                                          onChange={e => {
                                            const v = vacantes.vacantes.find(x => x.posicionId === e.target.value);
                                            if (v) handleAsignarVacante(ref, v);
                                          }}>
                                          {vacantes.vacantes
                                            .filter(v => !(act.refuerzos || []).find(r =>
                                              r.notas?.match(new RegExp(`^\\[posicion:${v.posicionId}\\]`))
                                            ))
                                            .map(v => (
                                              <option key={v.posicionId} value={v.posicionId}>
                                                {v.instrumento?.nombre || '—'} · {v.musicoAusente ? `${v.musicoAusente.nombre} ${v.musicoAusente.apellidos || ''}`.trim() : '—'}
                                              </option>
                                            ))}
                                        </NativeSelect.Field>
                                        <NativeSelect.Indicator />
                                      </NativeSelect.Root>
                                    )}
                                  </Box>

                                  {/* Botones de acción (columna derecha) */}
                                  <VStack gap={2} flexShrink={0} align="end">
                                    {/* Llamar / WhatsApp — botones grandes y visibles */}
                                    {telefono ? (
                                      <HStack gap={2}>
                                        <Tooltip content={`Llamar a ${c.nombre}`} showArrow>
                                          <IconButton
                                            as="a"
                                            href={`tel:${telefono}`}
                                            size="md"
                                            colorPalette="blue"
                                            variant="solid"
                                            rounded="full"
                                            shadow="sm"
                                            aria-label="Llamar"
                                            _hover={{ transform: 'scale(1.1)', shadow: 'md' }}
                                            transition="all 0.15s"
                                            onClick={() => handleContactarDesdeAgenda(c, ref)}><FaPhone /></IconButton>
                                        </Tooltip>
                                        <Tooltip content="WhatsApp — escribir mensaje" showArrow>
                                          <IconButton
                                            size="md"
                                            colorPalette="whatsapp"
                                            variant="solid"
                                            rounded="full"
                                            shadow="sm"
                                            aria-label="WhatsApp"
                                            _hover={{ transform: 'scale(1.1)', shadow: 'md' }}
                                            transition="all 0.15s"
                                            onClick={() => openWaModal(c, ref)}><FaWhatsapp /></IconButton>
                                        </Tooltip>
                                      </HStack>
                                    ) : !ref && !isReadOnly && (
                                      <Tooltip content="Marcar como contactado (sin abrir app)" showArrow>
                                        <Button
                                          size="sm"
                                          colorPalette="blue"
                                          variant="outline"
                                          rounded="full"
                                          onClick={() => handleMarcarContactado(c, ref)}><FaClock />Contactado
                                                                                  </Button>
                                      </Tooltip>
                                    )}

                                    {/* Confirmar / No disponible / Reactivar */}
                                    {!isReadOnly && (
                                      <HStack gap={1} justify="flex-end">
                                        {!isNoDisp && (
                                          <Tooltip content={isConfirmado ? 'Quitar confirmación' : 'Confirmar disponibilidad'} showArrow>
                                            <Button
                                              size="xs"
                                              colorPalette={isConfirmado ? 'green' : 'gray'}
                                              variant={isConfirmado ? 'solid' : 'outline'}
                                              rounded="full"
                                              onClick={() => handleConfirmarRefuerzoAgenda(c, ref)}><FaCheck />{isConfirmado ? 'Confirmado' : 'Disponible'}</Button>
                                          </Tooltip>
                                        )}
                                        {!isConfirmado && (
                                          <Tooltip content={isNoDisp ? 'Reactivar' : 'No disponible'} showArrow>
                                            <Button
                                              size="xs"
                                              colorPalette={isNoDisp ? 'gray' : 'red'}
                                              variant={isNoDisp ? 'outline' : 'ghost'}
                                              rounded="full"
                                              onClick={() => handleMarcarNoDisponible(c, ref)}>{isNoDisp ? undefined : <FaTimes />}{isNoDisp ? 'Reactivar' : 'No disponible'}</Button>
                                          </Tooltip>
                                        )}
                                        {ref && (
                                          <Tooltip content="Quitar de esta actuación" showArrow>
                                            <IconButton
                                              size="xs"
                                              colorPalette="red"
                                              variant="ghost"
                                              aria-label="Quitar"
                                              rounded="full"
                                              onClick={() => handleDeleteRefuerzo(ref.id)}><FaTrash /></IconButton>
                                          </Tooltip>
                                        )}
                                      </HStack>
                                    )}
                                  </VStack>
                                </Flex>
                              </Box>
                            );
                          })}
                        </VStack>
                      );
                    })()}</Card.Body>
                </Card.Root>
              </VStack>
            </TabPanel>

            {/* ===== FORMACION ===== */}
            <TabPanel value="4" px={0}>
              <VStack gap={4} align="stretch">
                {sinMusicosEnBanda && (
                  <Alert.Root
                    status="warning"
                    variant='subtle'
                    borderRadius="lg"
                    align="flex-start"
                    borderStartWidth='3px'
                    borderStartColor='colorPalette.solid'>
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title fontSize="sm" mb={1}>
                        Añade músicos antes de las formaciones
                      </Alert.Title>
                      <Alert.Description fontSize="sm" display="block">
                        <Text mb={2}>
                          Todavía no hay <strong>ningún músico</strong> en <strong>Mis músicos</strong>. Las plantillas de
                          formación se montan colocando a cada músico en la cuadrícula, así que primero debes dar de alta
                          al menos a los integrantes de la banda.
                        </Text>
                        <List.Root as='ol' gap={2} pl={1} mb={3}>
                          <List.Item>
                            Ve a <strong>Gestión → Mis músicos</strong> y crea o importa los músicos con sus datos e
                            instrumentos.
                          </List.Item>
                          <List.Item>
                            Después, en <strong>Gestión → Formaciones</strong>, podrás definir las disposiciones
                            predeterminadas (calle, concierto, etc.).
                          </List.Item>
                          <List.Item>
                            Por último, vuelve aquí y asigna la plantilla que corresponda a esta actuación.
                          </List.Item>
                        </List.Root>
                        <Button size="sm" colorPalette="orange" onClick={() => navigate('/banda/musicos')}>
                          Ir a Mis músicos
                        </Button>
                      </Alert.Description>
                    </Box>
                  </Alert.Root>
                )}

                {!sinMusicosEnBanda && sinPlantillasFormacion && (
                  <Alert.Root
                    status="info"
                    variant='subtle'
                    borderRadius="lg"
                    align="flex-start"
                    borderStartWidth='3px'
                    borderStartColor='colorPalette.solid'>
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title fontSize="sm" mb={1}>
                        Crea primero tus formaciones en Gestión
                      </Alert.Title>
                      <Alert.Description fontSize="sm" display="block">
                        <Text mb={2}>
                          Aún no tienes <strong>plantillas de formación</strong>. El flujo recomendado es definir antes
                          tus disposiciones habituales (por ejemplo para <strong>calle / procesión</strong> o{' '}
                          <strong>concierto / teatro</strong>) con los músicos que tengas dados de alta en{' '}
                          <strong>Mis músicos</strong>.
                        </Text>
                        <List.Root as='ol' gap={2} pl={1} mb={3}>
                          <List.Item>
                            Ve a <strong>Gestión → Formaciones</strong> y crea una o más plantillas predeterminadas.
                          </List.Item>
                          <List.Item>
                            Asigna en la cuadrícula los músicos e instrumentos que correspondan (desde el censo de{' '}
                            <strong>Mis músicos</strong>).
                          </List.Item>
                          <List.Item>
                            Vuelve a esta actuación y elige aquí la plantilla que quieras usar.
                          </List.Item>
                        </List.Root>
                        <Button
                          size="sm"
                          colorPalette="purple"
                          onClick={() => navigate('/banda/formaciones')}
                        >
                          Ir a Formaciones
                        </Button>
                      </Alert.Description>
                    </Box>
                  </Alert.Root>
                )}

                {/* Selector de plantilla */}
                <Card.Root bg={cardBg} shadow="md" rounded="xl">
                  <Card.Body>
                    <HStack justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                      <VStack align="start" gap={0}>
                        <Heading size="sm">Plantilla de formación</Heading>
                        <Text fontSize="xs" color="gray.500">
                          Selecciona la formación que usarás en esta actuación
                        </Text>
                      </VStack>
                    </HStack>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={act.plantillaId || ''}
                        onChange={(e) => handleSelectPlantilla(e.target.value)}
                        disabled={savingPlantilla || isReadOnly || sinMusicosEnBanda || sinPlantillasFormacion}
                        placeholder={
                          sinMusicosEnBanda
                            ? 'Añade músicos en Mis músicos primero'
                            : sinPlantillasFormacion
                              ? 'No hay plantillas: créalas en Formaciones'
                              : 'Sin formación asignada'
                        }>
                        {(plantillas || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Card.Body>
                </Card.Root>

                {/* Visualización de la formación */}
                {formacionLoading ? (
                  <Flex justify="center" py={10}><Spinner color="purple.500" /></Flex>
                ) : act.plantillaId && formacionData ? (
                  <Card.Root bg={cardBg} shadow="md" rounded="xl">
                    <Card.Body>
                      <HStack mb={4} justify="space-between" wrap="wrap" gap={2}>
                        <VStack align="start" gap={0}>
                          <Heading size="sm">{formacionData.nombre}</Heading>
                          <Text fontSize="xs" color="gray.500">
                            {formacionData.modo === 'LIBRE' ? 'Modo libre' : `${formacionData.filas} filas × ${formacionData.columnas} columnas`}
                          </Text>
                        </VStack>
                        {/* Resumen de asistencia entre los músicos de la formación */}
                        {(() => {
                          const RE_POS = /^\[posicion:([^\]]+)\]/;
                          const cubiertoIds = new Set(
                            (act.refuerzos || [])
                              .map(r => r.notas && RE_POS.exec(r.notas)?.[1])
                              .filter(Boolean)
                          );
                          const conMusico = formacionData.posiciones.filter((p) => p.musico);
                          const conf    = conMusico.filter((p) => p.asistencia === 'confirmado').length;
                          const noAsi   = conMusico.filter((p) => p.asistencia === 'no_asiste' && !cubiertoIds.has(p.id)).length;
                          const cub     = conMusico.filter((p) => p.asistencia === 'no_asiste' && cubiertoIds.has(p.id)).length;
                          const pend    = conMusico.filter((p) => p.asistencia === 'pendiente').length;
                          return (
                            <HStack gap={2} flexWrap="wrap">
                              <Badge colorPalette="green"  px={2}>{conf} confirmado{conf !== 1 ? 's' : ''}</Badge>
                              <Badge colorPalette="gray"   px={2}>{pend} pendiente{pend !== 1 ? 's' : ''}</Badge>
                              {noAsi > 0 && <Badge colorPalette="red"    px={2}>{noAsi} no asiste{noAsi !== 1 ? 'n' : ''}</Badge>}
                              {cub   > 0 && <Badge colorPalette="orange" px={2}>{cub} baja{cub !== 1 ? 's' : ''} cubierta{cub !== 1 ? 's' : ''}</Badge>}
                            </HStack>
                          );
                        })()}
                      </HStack>
                      <FormacionViewer
                        plantilla={formacionData}
                        posiciones={formacionData.posiciones}
                        refuerzos={act.refuerzos}
                      />
                    </Card.Body>
                  </Card.Root>
                ) : act.plantillaId && !formacionLoading ? (
                  <Alert.Root status="info" rounded="lg">
                    <Alert.Indicator />
                    No se pudo cargar la formación. Puede que haya sido eliminada.
                  </Alert.Root>
                ) : null}
              </VStack>
            </TabPanel>

            {/* ===== REPERTORIO ===== */}
            <TabPanel value="5" px={0}>
              <VStack align="stretch" gap={4}>
                <Card.Root bg={cardBg} shadow="md" rounded="xl">
                  <Card.Body>
                    <Heading size="sm" mb={2}>
                      Repertorio compartido (partituras Cadencya)
                    </Heading>
                    <Text fontSize="xs" color="gray.600" mb={2}>
                      {isReadOnly
                        ? 'Puedes incluir o no el enlace público y la contraseña del repertorio compartido en el PDF (histórico).'
                        : 'Puedes incluir o no el enlace público y la contraseña del repertorio compartido en el PDF y en el mensaje de WhatsApp de esta actuación (la contraseña solo se añade si la has mostrado aquí).'}
                    </Text>
                    <Field.Root display="flex" alignItems="center" justifyContent="space-between" gap={3} mb={3}>
                      <Field.Label htmlFor="incluir-rep-export" mb={0} fontSize="sm" fontWeight="600" flex={1}>
                        {isReadOnly ? 'Incluir enlace y contraseña en PDF' : 'Incluir enlace y contraseña en PDF y WhatsApp'}
                      </Field.Label>
                      <Switch
                        id="incluir-rep-export"
                        colorPalette="purple"
                        checked={incluirRepertorioCompartidoEnExport}
                        onChange={(e) => setIncluirRepertorioCompartidoEnExport(e.target.checked)}
                      />
                    </Field.Root>
                    {repertorioShare?.shareUrl ? (
                      <VStack align="stretch" gap={2}>
                        <HStack flexWrap="wrap" gap={2}>
                          <Link
                            href={repertorioShare.shareUrl}
                            fontSize="sm"
                            color="purple.600"
                            fontWeight="600"
                            target='_blank'
                            rel='noopener noreferrer'>
                            {repertorioShare.shareUrl} <Icon as={FaExternalLinkAlt} />
                          </Link>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(repertorioShare.shareUrl);
                              toast({ title: 'Enlace copiado', status: 'success', duration: 1500 });
                            }}><FaCopy />Copiar enlace
                                                      </Button>
                        </HStack>
                        {sharePwdPlain ? (
                          <Alert.Root status="success" variant="subtle" borderRadius="md">
                            <Alert.Indicator />
                            <Box>
                              <Text fontSize="sm" fontWeight="600">
                                Contraseña del enlace
                                {incluirRepertorioCompartidoEnExport
                                  ? (isReadOnly ? ' (se incluirá en PDF)' : ' (se incluirá en PDF y WhatsApp)')
                                  : (isReadOnly ? ' (no se añadirá al PDF)' : ' (no se añadirá a PDF ni WhatsApp: desactivaste la opción de arriba)')}
                              </Text>
                              <Text fontSize="sm" fontFamily="mono" mt={1}>
                                {sharePwdPlain}
                              </Text>
                            </Box>
                          </Alert.Root>
                        ) : repertorioShare.canRevealSharePassword && !isReadOnly ? (
                          <Button
                            size="sm"
                            variant="outline"
                            colorPalette="purple"
                            onClick={onRevealPwdOpen}><FaEye />Mostrar contraseña del enlace
                                                      </Button>
                        ) : repertorioShare.hasPassword ? (
                          <Text fontSize="xs" color="gray.600">
                            Esta clave se configuró antes de poder recuperarla aquí. Cambia una vez la contraseña del
                            enlace en Repertorio (partituras); después podrás consultarla con tu contraseña de cuenta.
                          </Text>
                        ) : null}
                      </VStack>
                    ) : repertorioShare?.hasPassword ? (
                      <Text fontSize="sm" color="gray.600">
                        El repertorio compartido está activo; el enlace público se genera al definir la contraseña de
                        acceso en Repertorio (partituras).
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.600">
                        Aún no hay enlace de repertorio compartido. Actívalo en la sección Repertorio (partituras) de tu
                        perfil de banda.
                      </Text>
                    )}
                  </Card.Body>
                </Card.Root>

                <Card.Root bg={cardBg} shadow="md" rounded="xl">
                  <Card.Body>
                    <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
                      <Heading size="sm">Programa / repertorio de la actuación</Heading>
                      {!isReadOnly ? (
                        <HStack gap={2} flexWrap="wrap" justify="flex-end">
                          <Button
                            size="sm"
                            colorPalette="purple"
                            variant="solid"
                            onClick={openModalCarpetas}><FaFolder />Importar carpeta
                                                      </Button>
                          <Button
                            size="sm"
                            colorPalette="purple"
                            variant="outline"
                            onClick={openModalImportObra}><FaMusic />Importar obra
                                                      </Button>
                          <Button
                            size="sm"
                            colorPalette="red"
                            variant="outline"
                            onClick={onClearRepertorioOpen}
                            disabled={!(act?.repertorio || []).length}><FaTrash />Vaciar repertorio
                                                      </Button>
                        </HStack>
                      ) : null}
                    </Flex>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      {isReadOnly
                        ? 'Programa tal como quedó guardado en la actuación.'
                        : 'Cada pieza es una línea. Las obras importadas de una carpeta muestran su nombre como etiqueta. Usa el icono de agarre para arrastrar y cambiar el orden.'}
                    </Text>
                    {(act.repertorio || []).length > 0 && (
                      isReadOnly ? (
                        <VStack align="stretch" gap={3} mb={4}>
                          {act.repertorio.map((r, i) => {
                            const isObra = isObraCarpetaLine(r);
                            return (
                              <Box
                                key={r.id || `rep-ro-${i}`}
                                py={2}
                                px={3}
                                rounded="lg"
                                borderWidth="1px"
                                borderColor="gray.100"
                                bg="gray.50"
                              >
                                <HStack align="flex-start" gap={2} flexWrap="wrap">
                                  <Text fontSize="sm" fontWeight="600" lineClamp={3} flex={1} minW={0}>
                                    {r.titulo}
                                  </Text>
                                  {isObra && r.nombreCarpeta ? (
                                    <Badge colorPalette="purple" flexShrink={0} fontSize="10px" borderRadius="md">
                                      {r.nombreCarpeta}
                                    </Badge>
                                  ) : null}
                                </HStack>
                                {r.autor ? (
                                  <Text fontSize="xs" color="gray.600" mt={1}>
                                    {r.autor}
                                  </Text>
                                ) : null}
                                {!isObra && r.enlace ? (
                                  <Link
                                    href={r.enlace}
                                    fontSize="xs"
                                    color="purple.500"
                                    display="block"
                                    mt={1}
                                    target='_blank'
                                    rel='noopener noreferrer'>
                                    {r.enlace}
                                  </Link>
                                ) : null}
                              </Box>
                            );
                          })}
                        </VStack>
                      ) : (
                        <DndContext
                          sensors={repSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleRepertorioDragEnd}
                        >
                          <SortableContext items={repSortableIds} strategy={verticalListSortingStrategy}>
                            <VStack align="stretch" gap={3} mb={4}>
                              {act.repertorio.map((r, i) => (
                                <SortableRepertorioRow
                                  key={r.id || `rep-row-${i}`}
                                  item={r}
                                  onRemove={handleRemoveRepertorio}
                                  readOnly={false}
                                />
                              ))}
                            </VStack>
                          </SortableContext>
                        </DndContext>
                      )
                    )}
                    {!isReadOnly ? (
                      <>
                        <Separator my={3} />
                        <Text fontSize="xs" fontWeight="600" color="gray.500" mb={2}>
                          Añadir pieza manual (autor y enlace opcionales)
                        </Text>
                        <HStack flexWrap="wrap" align="flex-end">
                          <Input
                            size="sm"
                            placeholder="Título de la pieza"
                            value={repItem.titulo}
                            onChange={(e) => setRepItem((r) => ({ ...r, titulo: e.target.value }))}
                            maxW={{ md: '200px' }}
                            flex={1}
                            minW={0}
                          />
                          <Input
                            size="sm"
                            placeholder="Autor (opcional)"
                            value={repItem.autor}
                            onChange={(e) => setRepItem((r) => ({ ...r, autor: e.target.value }))}
                            maxW={{ md: '160px' }}
                            flex={1}
                            minW={0}
                          />
                          <Input
                            size="sm"
                            placeholder="Enlace (opcional)"
                            value={repItem.enlace}
                            onChange={(e) => setRepItem((r) => ({ ...r, enlace: e.target.value }))}
                            flex={1}
                            minW={0}
                          />
                          <Button
                            size="sm"
                            colorPalette="purple"
                            onClick={handleAddRepertorio}
                            flexShrink={0}><FaPlus />Añadir
                                                      </Button>
                        </HStack>
                      </>
                    ) : null}
                    {act.enlacePartituras && (
                      <Box mt={4}>
                        <Text fontSize="xs" fontWeight="600" color="gray.500">
                          Enlace general a partituras (campo clásico):
                        </Text>
                        <Link
                          href={act.enlacePartituras}
                          fontSize="sm"
                          color="purple.500"
                          target='_blank'
                          rel='noopener noreferrer'>
                          {act.enlacePartituras} <Icon as={FaExternalLinkAlt} mx={1} />
                        </Link>
                      </Box>
                    )}
                  </Card.Body>
                </Card.Root>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs.Root>
      </VStack>

      {/* Modal: cartel a tamaño real */}
      <Dialog.Root open={isCartelOpen} placement='center' size='xl' onOpenChange={e => {
        if (!e.open) {
          onCartelClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.900" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" shadow="none" mx={2}>
              <Dialog.CloseTrigger color="white" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.800' }} zIndex={10} />
              <Dialog.Body p={0} display="flex" alignItems="center" justifyContent="center">
                <Image
                  src={act?.cartelUrl}
                  maxW="full"
                  maxH="90vh"
                  objectFit="contain"
                  rounded="xl"
                  shadow="2xl"
                  onClick={onCartelClose}
                  cursor="zoom-out"
                />
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal: contraseña de cuenta para revelar clave del enlace de repertorio */}
      <Dialog.Root open={isRevealPwdOpen} placement='center' size='md' onOpenChange={e => {
        if (!e.open) {
          closeRevealPwdModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Mostrar contraseña del enlace</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Alert.Root
                  status="info"
                  variant='subtle'
                  borderRadius="md"
                  mb={4}
                  fontSize="sm"
                  borderStartWidth='3px'
                  borderStartColor='colorPalette.solid'>
                  <Alert.Indicator />
                  Introduce tu contraseña de cuenta Cadencya (no la reutilices como contraseña del enlace público).
                </Alert.Root>
                <Field.Root>
                  <Field.Label fontSize="sm">Contraseña de tu cuenta</Field.Label>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    value={revealAccountPwd}
                    onChange={(e) => setRevealAccountPwd(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRevealSharePwd();
                      }
                    }}
                  />
                </Field.Root>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={closeRevealPwdModal}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={handleRevealSharePwd} loading={revealLoading}>
                  Mostrar contraseña
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal carpetas partituras */}
      <Dialog.Root open={isCarpetasOpen} size='lg' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onCarpetasClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Añadir carpetas del repertorio</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Cada obra de la carpeta se añadirá como una pieza en el programa, con una etiqueta con el nombre de la
                  carpeta. El orden inicial será el de tu repertorio de partituras; luego puedes reordenar arrastrando en la
                  actuación. Puedes añadir varias carpetas.
                </Text>
                {carpetasLoading ? (
                  <Flex justify="center" py={10}>
                    <Spinner color="purple.500" />
                  </Flex>
                ) : carpetasPartituras.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No hay carpetas creadas. Créalas en Repertorio (partituras) de tu perfil de banda.
                  </Text>
                ) : (
                  <IsolateCheckboxesFromField>
                  <VStack align="stretch" gap={2} maxH="50vh" overflowY="auto">
                    {carpetasPartituras.map((c) => {
                      const ya = act ? carpetaIdsEnRepertorio(act.repertorio).has(c.id) : false;
                      const checked = carpetaIdsSeleccion.includes(c.id);
                      return (
                        <Checkbox.Root
                          key={c.id}
                          disabled={ya}
                          onCheckedChange={() => !ya && toggleCarpetaSeleccion(c.id)}
                          checked={checked}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                            <Text as="span" fontWeight="600">
                              {c.nombre}
                            </Text>
                            {ya ? (
                              <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                                (ya añadida)
                              </Text>
                            ) : (
                              <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                                ({(c.obras || []).length} obra{(c.obras || []).length === 1 ? '' : 's'})
                              </Text>
                            )}
                          </Checkbox.Label></Checkbox.Root>
                      );
                    })}
                  </VStack>
                  </IsolateCheckboxesFromField>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onCarpetasClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={handleImportarCarpetas}
                  disabled={!carpetaIdsSeleccion.length || carpetasLoading}
                >
                  Importar {carpetaIdsSeleccion.length ? `(${carpetaIdsSeleccion.length})` : ''}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal: importar obra individual del archivo */}
      <Dialog.Root open={isImportObraOpen} size='lg' scrollBehavior="inside" placement='center' onOpenChange={e => {
        if (!e.open) {
          onImportObraClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Importar obra del archivo</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Selecciona una obra del Archivo Musical para añadirla al programa de esta actuación.
                </Text>
                <Input
                  size="sm"
                  placeholder="Buscar obra…"
                  value={obraArchivoQuery}
                  onChange={(e) => setObraArchivoQuery(e.target.value)}
                  mb={3}
                />
                {obrasArchivoLoading ? (
                  <Flex justify="center" py={10}>
                    <Spinner color="purple.500" />
                  </Flex>
                ) : (
                  <VStack align="stretch" gap={2} maxH="50vh" overflowY="auto">
                    {obrasArchivo
                      .filter((o) => {
                        const q = obraArchivoQuery.trim().toLowerCase();
                        if (!q) return true;
                        const t = String(o?.titulo || '').toLowerCase();
                        const a = String(o?.autor || '').toLowerCase();
                        return t.includes(q) || a.includes(q);
                      })
                      .map((o) => {
                        const isSel = String(selectedObraArchivoId) === String(o.id);
                        const sub = [o.autor, o.estado].filter(Boolean).join(' · ');
                        return (
                          <Button
                            key={o.id}
                            size="sm"
                            variant={isSel ? 'solid' : 'outline'}
                            colorPalette={isSel ? 'purple' : 'gray'}
                            justifyContent="flex-start"
                            h="auto"
                            py={2.5}
                            px={3}
                            onClick={() => setSelectedObraArchivoId(o.id)}
                          >
                            <Box minW={0} flex={1}>
                              <Text textAlign="left" fontWeight="700" lineClamp={1}>
                                {o.titulo || 'Sin título'}
                              </Text>
                              {sub ? (
                                <Text textAlign="left" fontSize="xs" opacity={0.8} lineClamp={1}>
                                  {sub}
                                </Text>
                              ) : null}
                            </Box>
                          </Button>
                        );
                      })}
                    {!obrasArchivo.length ? (
                      <Text fontSize="sm" color="gray.500">
                        No hay obras en el archivo.
                      </Text>
                    ) : null}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onImportObraClose}>
                  Cancelar
                </Button>
                <Button colorPalette="purple" onClick={handleImportarObraArchivo} disabled={!selectedObraArchivoId || obrasArchivoLoading}>
                  Importar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Confirmación: vaciar repertorio */}
      <Dialog.Root
        open={isClearRepertorioOpen}
        initialFocusEl={() => cancelClearRepertorioRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onClearRepertorioClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header fontSize="lg" fontWeight="bold">
                Vaciar repertorio
              </Dialog.Header>
              <Dialog.Body>
                ¿Seguro que quieres eliminar <strong>todo</strong> el repertorio de esta actuación? Esta acción no se puede deshacer.
              </Dialog.Body>
              <Dialog.Footer>
                <Button ref={cancelClearRepertorioRef} onClick={onClearRepertorioClose} variant="ghost">
                  Cancelar
                </Button>
                <Button colorPalette="red" onClick={handleClearRepertorio} ml={3}>
                  Vaciar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal Refuerzo — selector de agenda o nuevo */}
      <Dialog.Root open={isRefOpen} size='lg' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open)
          { setRefPosicionVacanteId(''); onRefClose(); }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Añadir refuerzo</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {/* Selector de modo */}
                <HStack mb={4} gap={2}>
                  <Button
                    size="sm"
                    flex="1"
                    variant={refModo === 'seleccionar' ? 'solid' : 'outline'}
                    colorPalette="purple"
                    onClick={() => { setRefModo('seleccionar'); setRefContactoSeleccionado(null); }}><FaAddressBook />De mis contactos
                                  </Button>
                  <Button
                    size="sm"
                    flex="1"
                    variant={refModo === 'nuevo' ? 'solid' : 'outline'}
                    colorPalette="gray"
                    onClick={() => { setRefModo('nuevo'); setRefContactoSeleccionado(null); }}><FaPlus />Nuevo / manual
                                  </Button>
                </HStack>

                {refModo === 'seleccionar' ? (
                  <VStack gap={3} align="stretch">
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none"><FaSearch color="gray" size={11} /></InputLeftElement>
                      <Input
                        placeholder="Buscar en mis refuerzos…"
                        value={refBusqueda}
                        onChange={e => setRefBusqueda(e.target.value)}
                        rounded="lg"
                      />
                    </InputGroup>
                    {agendaLoading ? (
                      <Flex justify="center" py={6}><Spinner size="sm" color="purple.500" /></Flex>
                    ) : agendaRefuerzos.length === 0 ? (
                      <Alert.Root status="info" rounded="lg" size="sm">
                        <Alert.Indicator />
                        No tienes refuerzos en tu agenda. Usa el modo "Nuevo / manual" o añade contactos en Gestión → Contactos.
                      </Alert.Root>
                    ) : agendaRefuerzos.every(c => {
                        const ref = (act.refuerzos || []).find(r => r.contactoAgendaId === c.id);
                        // Se considera "no seleccionable" si está no disponible O ya asignado a baja
                        return ref?.noDisponible || ref?.notas?.match(/^\[posicion:[^\]]+\]/);
                      }) ? (
                      <Alert.Root status="success" rounded="lg" size="sm">
                        <Alert.Indicator />
                        Todos los refuerzos disponibles ya están asignados.
                      </Alert.Root>
                    ) : (
                      <VStack gap={2} align="stretch" maxH="320px" overflowY="auto" pr={1}>
                        {agendaRefuerzos
                          .filter(c => {
                            const ref = (act.refuerzos || []).find(r => r.contactoAgendaId === c.id);
                            // Excluir si ya está asignado a una baja concreta
                            if (ref?.notas?.match(/^\[posicion:[^\]]+\]/)) return false;
                            // Excluir si marcado como no disponible
                            if (ref?.noDisponible) return false;
                            const q = refBusqueda.toLowerCase();
                            return !q || [c.nombre, c.apellidos, c.instrumento?.nombre, c.localidad].filter(Boolean).some(v => v.toLowerCase().includes(q));
                          })
                          .map(c => {
                            const isSelected   = refContactoSeleccionado?.id === c.id;
                            const existingRef  = (act.refuerzos || []).find(r => r.contactoAgendaId === c.id);
                            const yaConfirmado = existingRef?.confirmado === true;
                            const cInstArr     = Array.isArray(c.instrumentos) ? c.instrumentos : [];
                            return (
                              <Box
                                key={c.id}
                                p={3} rounded="lg" cursor="pointer"
                                borderWidth="2px"
                                borderColor={isSelected ? 'purple.400' : yaConfirmado ? 'green.200' : 'gray.200'}
                                bg={isSelected ? 'purple.50' : yaConfirmado ? 'green.50' : 'white'}
                                _dark={{ bg: isSelected ? 'purple.900' : yaConfirmado ? 'green.900' : 'gray.700' }}
                                _hover={{ borderColor: 'purple.300', bg: 'purple.50' }}
                                onClick={() => setRefContactoSeleccionado(isSelected ? null : c)}
                                transition="all 0.15s"
                              >
                                <HStack gap={3}>
                                  <Avatar.Root size="sm" bg={yaConfirmado ? 'green.500' : 'purple.400'} color="white"><Avatar.Fallback name={(c.apellidos?.[0] || c.nombre?.[0] || '?').toUpperCase()} /></Avatar.Root>
                                  <Box flex="1" minW={0}>
                                    <HStack gap={2}>
                                      <Text fontWeight="600" fontSize="sm" lineClamp={1}>
                                        {[c.nombre, c.apellidos].filter(Boolean).join(' ')}
                                      </Text>
                                      {yaConfirmado && (
                                        <Badge colorPalette="green" fontSize="2xs">✓ Disponible</Badge>
                                      )}
                                    </HStack>
                                    <HStack gap={2} flexWrap="wrap">
                                      {cInstArr.map((inst, i) => (
                                        <Badge key={i} colorPalette="purple" fontSize="2xs" variant="subtle">
                                          {inst.nombre}{inst.voz ? ` · ${inst.voz}` : ''}
                                        </Badge>
                                      ))}
                                      {c.localidad && <Text fontSize="xs" color="gray.500">{c.localidad}</Text>}
                                    </HStack>
                                  </Box>
                                  <VStack gap={0} align="end" flexShrink={0}>
                                    {c.telefono && <HStack gap={1}><Icon as={FaPhone} boxSize={2.5} color="gray.400" /><Text fontSize="xs" color="gray.500">{c.telefono}</Text></HStack>}
                                    {isSelected && <Icon as={FaCheck} color="purple.500" boxSize={4} />}
                                  </VStack>
                                </HStack>
                              </Box>
                            );
                          })
                        }
                      </VStack>
                    )}
                    {refContactoSeleccionado && (
                      <>
                        <Separator />
                        <Field.Root>
                          <Field.Label fontSize="sm">Notas (opcional)</Field.Label>
                          <Textarea size="sm" rows={2} value={refForm.notas} onChange={e => setRefForm(f => ({ ...f, notas: e.target.value }))} placeholder="Condiciones específicas para esta actuación…" />
                        </Field.Root>
                        <Field.Root display="flex" alignItems="center">
                          <Field.Label fontSize="sm" mb={0}>Confirmado</Field.Label>
                          <Switch checked={refForm.confirmado} onValueChange={e => setRefForm(f => ({ ...f, confirmado: e.target.checked }))} colorPalette="green" />
                        </Field.Root>
                      </>
                    )}
                  </VStack>
                ) : (
                  <VStack gap={3}>
                    <Alert.Root status="info" rounded="lg" size="sm">
                      <Alert.Indicator />
                      Este refuerzo se añadirá solo a esta actuación. Para guardarlo en tu agenda ve a Gestión → Contactos.
                    </Alert.Root>
                    <Field.Root required>
                      <Field.Label fontSize="sm">Nombre</Field.Label>
                      <Input size="sm" value={refForm.nombre} onChange={e => setRefForm(f => ({ ...f, nombre: e.target.value }))} />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label fontSize="sm">Instrumento</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          size="sm"
                          value={refForm.instrumentoId}
                          onChange={e => setRefForm(f => ({ ...f, instrumentoId: e.target.value }))}
                          placeholder="Sin asignar">
                          {instrumentos.map(i => <option key={i.id} value={i.id}>{i.nombre}</option>)}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>
                    <SimpleGrid columns={2} gap={3} w="100%">
                      <Field.Root><Field.Label fontSize="sm">Teléfono</Field.Label><Input size="sm" type="tel" value={refForm.telefono} onChange={e => setRefForm(f => ({ ...f, telefono: e.target.value }))} /></Field.Root>
                      <Field.Root><Field.Label fontSize="sm">Email</Field.Label><Input size="sm" type="email" value={refForm.email} onChange={e => setRefForm(f => ({ ...f, email: e.target.value }))} /></Field.Root>
                    </SimpleGrid>
                    <Field.Root>
                      <Field.Label fontSize="sm">Notas</Field.Label>
                      <Textarea size="sm" value={refForm.notas} onChange={e => setRefForm(f => ({ ...f, notas: e.target.value }))} rows={2} />
                    </Field.Root>
                    <Field.Root display="flex" alignItems="center">
                      <Field.Label fontSize="sm" mb={0}>Confirmado</Field.Label>
                      <Switch checked={refForm.confirmado} onValueChange={e => setRefForm(f => ({ ...f, confirmado: e.target.checked }))} colorPalette="green" />
                    </Field.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onRefClose}>Cancelar</Button>
                <Button
                  colorPalette="purple"
                  onClick={handleAddRefuerzo}
                  disabled={refModo === 'seleccionar' ? !refContactoSeleccionado : !refForm.nombre.trim()}
                >
                  Añadir
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Modal de WhatsApp ── */}
      {/* ── AlertDialog: confirmar reapertura de encuesta ── */}
      <Dialog.Root
        open={isReopenOpen}
        initialFocusEl={() => reabrirRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onReopenClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content rounded="2xl">
              <Dialog.Header fontSize="lg" fontWeight="800">
                ¿Reabrir encuesta?
              </Dialog.Header>
              <Dialog.Body>
                <Text mb={2}>Quienes aún no hayan respondido podrán hacerlo. <strong>Quien ya votó no puede cambiar su respuesta.</strong></Text>
                <Text fontSize="sm" color="gray.500">
                  La actuación volverá al estado <strong>Encuesta abierta</strong>.
                </Text>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button ref={reabrirRef} variant="ghost" onClick={onReopenClose}>Cancelar</Button>
                <Button colorPalette="yellow" onClick={handleReopenConfirm}><Icon as={FaSync} />Sí, reabrir
                                  </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── AlertDialog: cerrar encuesta con doble confirmación ── */}
      <Dialog.Root
        open={isCloseEncuestaOpen}
        initialFocusEl={() => cerrarRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onCloseEncuestaClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop backdropFilter="blur(6px)" />
          <Dialog.Positioner>
            <Dialog.Content rounded="2xl">
              <Dialog.Header fontSize="lg" fontWeight="900">
                Cerrar encuesta
              </Dialog.Header>
              <Dialog.Body>
                <Text mb={2}>
                  Una vez cerrada, <strong>no se podrán editar</strong> las respuestas (ni marcar justificados, ni corregir asistencias).
                </Text>
                <Text fontSize="sm" color="gray.500" mb={4}>
                  Para confirmar, escribe <strong>{CLOSE_WORD}</strong> dos veces.
                </Text>
                <VStack align="stretch" gap={2}>
                  <Input
                    placeholder={CLOSE_WORD}
                    value={closeConfirmA}
                    onChange={(e) => setCloseConfirmA(e.target.value)}
                    autoFocus
                  />
                  <Input
                    placeholder={`${CLOSE_WORD} (otra vez)`}
                    value={closeConfirmB}
                    onChange={(e) => setCloseConfirmB(e.target.value)}
                  />
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button
                  ref={cerrarRef}
                  variant="ghost"
                  onClick={() => {
                    setCloseConfirmA('');
                    setCloseConfirmB('');
                    onCloseEncuestaClose();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleCloseEncuestaConfirm}
                  disabled={closeConfirmA.trim().toUpperCase() !== CLOSE_WORD || closeConfirmB.trim().toUpperCase() !== CLOSE_WORD}
                >
                  Sí, cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Modal de configuración de PDF ── */}
      <Dialog.Root open={isPdfOpen} size='md' placement='center' onOpenChange={e => {
        if (!e.open) {
          onPdfClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content rounded="2xl" overflow="hidden">
              {/* Cabecera */}
              <Box bg="purple.600" px={6} py={4} _dark={{ bg: 'purple.800' }}>
                <HStack gap={3}>
                  <Icon as={FaFilePdf} boxSize={5} color="white" />
                  <Box>
                    <Text fontWeight="800" color="white" fontSize="md">Exportar PDF</Text>
                    <Text fontSize="xs" color="purple.200">Selecciona las secciones a incluir</Text>
                  </Box>
                </HStack>
              </Box>

              <Dialog.Body pt={5} pb={3}>
                <IsolateCheckboxesFromField>
                <VStack align="stretch" gap={4}>
                  {/* Grupo: Ficha principal */}
                  <Box>
                    <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="1px" color="purple.500" mb={2}>
                      Siempre incluido
                    </Text>
                    <VStack align="stretch" gap={2} pl={1}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" fontWeight="500">Cabecera con nombre y fecha</Text>
                        <Icon as={FaCheck} color="green.500" boxSize={3} />
                      </HStack>
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="1px" color="purple.500" mb={2}>
                      Información general
                    </Text>
                    <VStack align="stretch" gap={2} pl={1}>
                      {[
                        { key: 'infoGeneral', label: 'Datos de la actuación', sub: 'Fecha, horario, ubicaciones, uniforme…' },
                        { key: 'autobus',     label: 'Autobús', sub: 'Hora de salida y punto de recogida', show: act?.necesidadAutobus },
                        { key: 'cartel',      label: 'Cartel / imagen', sub: 'Imagen promocional de la actuación', show: !!act?.cartelUrl },
                        { key: 'observaciones', label: 'Observaciones', sub: 'Notas adicionales del organizador', show: !!act?.observaciones },
                      ].filter(s => s.show !== false).map(({ key, label, sub }) => (
                        <Checkbox.Root
                          key={key}
                          onCheckedChange={details => setPdfSecciones(p => ({ ...p, [key]: details.checked === true }))}
                          colorPalette="purple"
                          checked={pdfSecciones[key]}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <Box>
                            <Text fontSize="sm" fontWeight="500">{label}</Text>
                            {sub && <Text fontSize="xs" color="gray.500">{sub}</Text>}
                          </Box>
                        </Checkbox.Label></Checkbox.Root>
                      ))}
                    </VStack>
                  </Box>

                  <Box>
                    <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="1px" color="purple.500" mb={2}>
                      Repertorio y partituras
                    </Text>
                    <VStack align="stretch" gap={2} pl={1}>
                      {[
                        { key: 'repertorio', label: 'Repertorio', sub: 'Lista de obras programadas', show: (act?.repertorio?.length ?? 0) > 0 },
                        { key: 'partiturasCompartidas', label: 'Partituras (Cadencya)', sub: 'Enlace al repertorio compartido' },
                        { key: 'enlaceManual', label: 'Enlace de partituras manual', show: !!act?.enlacePartituras },
                      ].filter(s => s.show !== false).map(({ key, label, sub }) => (
                        <Checkbox.Root
                          key={key}
                          onCheckedChange={details => setPdfSecciones(p => ({ ...p, [key]: details.checked === true }))}
                          colorPalette="purple"
                          checked={pdfSecciones[key]}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <Box>
                            <Text fontSize="sm" fontWeight="500">{label}</Text>
                            {sub && <Text fontSize="xs" color="gray.500">{sub}</Text>}
                          </Box>
                        </Checkbox.Label></Checkbox.Root>
                      ))}
                    </VStack>
                  </Box>

                  {act?.plantillaId && (
                    <Box>
                      <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="1px" color="purple.500" mb={2}>
                        Formación
                      </Text>
                      <VStack align="stretch" gap={2} pl={1}>
                        <Checkbox.Root
                          onCheckedChange={details => setPdfSecciones(p => ({ ...p, formacion: details.checked === true }))}
                          colorPalette="purple"
                          checked={pdfSecciones.formacion}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <Box>
                            <Text fontSize="sm" fontWeight="500">Distribución de la formación</Text>
                            <Text fontSize="xs" color="gray.500">Página aparte con la disposición en el espacio y el estado de asistencia</Text>
                          </Box>
                        </Checkbox.Label></Checkbox.Root>
                      </VStack>
                    </Box>
                  )}

                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="1px" color="orange.500">
                        Datos de asistencia
                      </Text>
                      <Text fontSize="10px" color="orange.400">(opcionales)</Text>
                    </HStack>
                    <VStack align="stretch" gap={2} pl={1}>
                      {[
                        { key: 'musicosConfirmados', label: 'Músicos confirmados', sub: `${act?.encuesta?.respuestas?.filter(r => r.asiste === true).length ?? 0} confirmados` },
                        { key: 'noAsisten',          label: 'Músicos que no asisten', sub: `${act?.encuesta?.respuestas?.filter(r => r.asiste === false).length ?? 0} bajas` },
                        { key: 'refuerzos',          label: 'Refuerzos', sub: `${act?.refuerzos?.length ?? 0} refuerzos` },
                      ].map(({ key, label, sub }) => (
                        <Checkbox.Root
                          key={key}
                          onCheckedChange={details => setPdfSecciones(p => ({ ...p, [key]: details.checked === true }))}
                          colorPalette="orange"
                          checked={pdfSecciones[key]}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <Box>
                            <Text fontSize="sm" fontWeight="500">{label}</Text>
                            {sub && <Text fontSize="xs" color="gray.500">{sub}</Text>}
                          </Box>
                        </Checkbox.Label></Checkbox.Root>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
                </IsolateCheckboxesFromField>
              </Dialog.Body>

              <Dialog.Footer gap={2} borderTop="1px solid" borderColor="gray.100" _dark={{ borderColor: 'gray.700' }}>
                <Button variant="ghost" size="sm" onClick={onPdfClose}>Cancelar</Button>
                <Button
                  colorPalette="purple"
                  size="sm"
                  rounded="full"
                  shadow="sm"
                  onClick={() => handleExportPDF(pdfSecciones)}><FaFilePdf />Generar PDF
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Cartel imagen para redes (feed / story) ── */}
      <Dialog.Root open={isRrssCartelOpen} size='lg' placement='center' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          closeRrssCartelModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content rounded="2xl" overflow="hidden" position="relative">
              <Dialog.CloseTrigger top={3} right={3} color="white" zIndex={2} _hover={{ bg: 'whiteAlpha.200' }} />
              <Box bg="purple.600" px={6} py={4} _dark={{ bg: 'purple.800' }}>
                <HStack gap={3}>
                  <Icon as={FaInstagram} boxSize={5} color="white" />
                  <Box>
                    <Text fontWeight="800" color="white" fontSize="md">Cartel para RRSS</Text>
                    <Text fontSize="xs" color="purple.200">
                      {rrssCartelStep === 'checks'
                        ? 'Elige el formato y marca la información'
                        : 'Personaliza colores y fondo'}
                    </Text>
                  </Box>
                </HStack>
                {act?.nombre ? <Text fontSize="sm" color="purple.100" mt={2} lineClamp={2}>{act.nombre}</Text> : null}
              </Box>

              {rrssCartelStep === 'formato' ? (
                <Dialog.Body pt={6} pb={4}>
                  <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }} mb={5}>
                    Feed: cuadrado para publicaciones. Story / Short: vertical 9:16 para Instagram, YouTube Shorts, etc.
                  </Text>
                  <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                    <Box
                      as="button"
                      type="button"
                      p={5}
                      border="2px solid"
                      borderColor={rrssCartelFormato === 'feed' ? 'purple.500' : 'gray.200'}
                      bg={rrssCartelFormato === 'feed' ? 'purple.50' : 'transparent'}
                      rounded="xl"
                      w="full"
                      textAlign="left"
                      transition="all 0.15s"
                      _hover={{ borderColor: 'purple.400', shadow: 'md' }}
                      onClick={() => setRrssCartelFormato('feed')}
                    >
                      <HStack gap={3} mb={2}>
                        <Flex bg="purple.100" p={2} rounded="lg" align="center" justify="center">
                          <Icon as={FaTh} color="purple.600" boxSize={6} />
                        </Flex>
                        <Text fontWeight="700" fontSize="md">{RRSS_CARTEL_FORMATOS.feed.title}</Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.600">{RRSS_CARTEL_FORMATOS.feed.subtitle}</Text>
                    </Box>
                    <Box
                      as="button"
                      type="button"
                      p={5}
                      border="2px solid"
                      borderColor={rrssCartelFormato === 'story' ? 'purple.500' : 'gray.200'}
                      bg={rrssCartelFormato === 'story' ? 'purple.50' : 'transparent'}
                      rounded="xl"
                      w="full"
                      textAlign="left"
                      transition="all 0.15s"
                      _hover={{ borderColor: 'purple.400', shadow: 'md' }}
                      onClick={() => setRrssCartelFormato('story')}
                    >
                      <HStack gap={3} mb={2}>
                        <Flex bg="purple.100" p={2} rounded="lg" align="center" justify="center">
                          <Icon as={FaMobileAlt} color="purple.600" boxSize={6} />
                        </Flex>
                        <Text fontWeight="700" fontSize="md">{RRSS_CARTEL_FORMATOS.story.title}</Text>
                      </HStack>
                      <Text fontSize="xs" color="gray.600">{RRSS_CARTEL_FORMATOS.story.subtitle}</Text>
                    </Box>
                  </SimpleGrid>
                </Dialog.Body>
              ) : rrssCartelStep === 'secciones' ? (
                <Dialog.Body pt={5} pb={3}>
                  <IsolateCheckboxesFromField>
                  <VStack align="stretch" gap={3} pl={1}>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, titulo: details.checked === true }))}
                      colorPalette="purple"
                      checked={rrssCartelOpts.titulo}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Nombre de la actuación</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, fecha: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.fecha}
                      checked={rrssCartelOpts.fecha}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Fecha</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, horas: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!(act?.horaInicio || act?.horaFin)}
                      checked={rrssCartelOpts.horas}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Horario (inicio / fin)</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, ubicacion: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.ubicacion?.trim()}
                      checked={rrssCartelOpts.ubicacion}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Ubicación (inicio)</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, ubicacionLlegada: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.ubicacionFin?.trim()}
                      checked={rrssCartelOpts.ubicacionLlegada}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Ubicación de llegada</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, tipoActuacion: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.tipo}
                      checked={rrssCartelOpts.tipoActuacion}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Tipo de actuación</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, repertorio: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!(act?.repertorio?.length > 0)}
                      checked={rrssCartelOpts.repertorio}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Repertorio</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, observaciones: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.observaciones?.trim()}
                      checked={rrssCartelOpts.observaciones}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Observaciones</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, cartelFondo: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.cartelUrl}
                      checked={rrssCartelOpts.cartelFondo}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Box>
                        <Text fontSize="sm" fontWeight="600">Cartel como cabecera</Text>
                        <Text fontSize="xs" color="gray.500">Imagen de la actuación arriba del texto</Text>
                      </Box>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, logoBanda: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.banda?.avatar}
                      checked={rrssCartelOpts.logoBanda}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Logo / avatar de la banda</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, nombreBanda: details.checked === true }))}
                      colorPalette="purple"
                      disabled={!act?.banda?.nombre}
                      checked={rrssCartelOpts.nombreBanda}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm" fontWeight="600">Nombre de la banda</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <Separator />
                    <Checkbox.Root
                      onCheckedChange={(details) => setRrssCartelOpts((p) => ({ ...p, footerCadencya: details.checked === true }))}
                      colorPalette="purple"
                      checked={rrssCartelOpts.footerCadencya}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Box>
                        <Text fontSize="sm" fontWeight="600">Marca Cadencya (recomendado)</Text>
                        <Text fontSize="xs" color="gray.500">Pie con logo y color corporativo, como en los correos</Text>
                      </Box>
                    </Checkbox.Label></Checkbox.Root>
                  </VStack>
                  </IsolateCheckboxesFromField>
                </Dialog.Body>
              ) : rrssCartelStep === 'colores' ? (
                <Dialog.Body pt={5} pb={3}>
                  <VStack align="stretch" gap={4}>
                    <Box>
                      <Text fontSize="xs" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                        Colores
                      </Text>
                      <SimpleGrid columns={{ base: 1, sm: 3 }} gap={3}>
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.600" mb={1}>Fondo</Text>
                          <Input
                            type="color"
                            value={rrssCartelOpts.bgColor || '#FFFFFF'}
                            onChange={(e) => setRrssCartelOpts((p) => ({ ...p, bgColor: e.target.value }))}
                            p={0}
                            h="40px"
                          />
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.600" mb={1}>Texto</Text>
                          <Input
                            type="color"
                            value={rrssCartelOpts.textColor || '#0F172A'}
                            onChange={(e) => setRrssCartelOpts((p) => ({ ...p, textColor: e.target.value }))}
                            p={0}
                            h="40px"
                          />
                        </Box>
                        <Box>
                          <Text fontSize="xs" fontWeight="700" color="gray.600" mb={1}>Acento</Text>
                          <Input
                            type="color"
                            value={rrssCartelOpts.accentColor || '#4C1D95'}
                            onChange={(e) => setRrssCartelOpts((p) => ({ ...p, accentColor: e.target.value }))}
                            p={0}
                            h="40px"
                          />
                        </Box>
                      </SimpleGrid>
                    </Box>

                    <Box>
                      <Text fontSize="xs" fontWeight="800" color="gray.600" textTransform="uppercase" letterSpacing="0.08em" mb={2}>
                        Fondo con cartel
                      </Text>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm" fontWeight="600" color="gray.700">Opacidad del cartel</Text>
                        <Badge colorPalette="purple" variant="subtle">
                          {Math.round((Number(rrssCartelOpts.cartelOpacity ?? 0.75) * 100))}%
                        </Badge>
                      </HStack>
                      <SliderRoot
                        value={Math.round((Number(rrssCartelOpts.cartelOpacity ?? 0.75) * 100))}
                        min={0}
                        max={100}
                        step={5}
                        colorPalette="purple"
                        onValueChange={(v) => setRrssCartelOpts((p) => ({ ...p, cartelOpacity: Math.max(0, Math.min(1, v / 100)) }))}
                        disabled={!rrssCartelOpts.cartelFondo || !act?.cartelUrl}
                      >
                        <SliderTrack>
                          <SliderFilledTrack />
                        </SliderTrack>
                        <SliderThumb />
                      </SliderRoot>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Solo aplica si activas “Cartel como cabecera” y la actuación tiene imagen/cartel.
                      </Text>
                    </Box>
                  </VStack>
                </Dialog.Body>
              ) : (
                <Dialog.Body pt={5} pb={3}>
                  {rrssCartelPreviewUrl ? (
                    <VStack gap={3} align="stretch">
                      <Box rounded="xl" overflow="hidden" borderWidth="1px" borderColor="gray.200">
                        <Image src={rrssCartelPreviewUrl} alt="Previsualización cartel RRSS" w="full" />
                      </Box>
                      <Text fontSize="xs" color="gray.500" textAlign="center">
                        Esta es la imagen final. Si vuelves atrás y cambias secciones o colores, genera la previsualización de nuevo.
                      </Text>
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      Aún no hay previsualización. Pulsa “Generar previsualización”.
                    </Text>
                  )}
                </Dialog.Body>
              )}

              <Dialog.Footer gap={2} borderTop="1px solid" borderColor="gray.100" _dark={{ borderColor: 'gray.700' }} flexWrap="wrap">
                {rrssCartelStep !== 'formato' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (rrssCartelStep === 'secciones') setRrssCartelStep('formato');
                      else if (rrssCartelStep === 'colores') setRrssCartelStep('secciones');
                      else setRrssCartelStep('colores');
                    }}
                  >
                    Atrás
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={closeRrssCartelModal}>
                    Cancelar
                  </Button>
                )}

                {rrssCartelStep === 'formato' && (
                  <Button colorPalette="purple" size="sm" rounded="full" onClick={() => setRrssCartelStep('secciones')}>
                    Siguiente: secciones
                  </Button>
                )}
                {rrssCartelStep === 'secciones' && (
                  <Button colorPalette="purple" size="sm" rounded="full" onClick={() => setRrssCartelStep('colores')}>
                    Siguiente: colores
                  </Button>
                )}
                {rrssCartelStep === 'colores' && (
                  <Button
                    colorPalette="purple"
                    size="sm"
                    rounded="full"
                    onClick={handleRrssCartelGenerarPreview}
                    loading={rrssCartelGen}
                    loadingText="Generando…"><FaEye />Generar previsualización
                                  </Button>
                )}
                {rrssCartelStep === 'preview' && (
                  <Button
                    colorPalette="purple"
                    size="sm"
                    rounded="full"
                    onClick={handleRrssCartelGenerar}
                    loading={rrssCartelGen}
                    loadingText="Descargando…"><FaImage />Descargar PNG
                                  </Button>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Modal de compartir actuación (WhatsApp / PDF) ── */}
      <Dialog.Root open={isShareWaOpen} size='lg' placement='center' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          closeShareWaModal();
        }
      }}>
        <Portal>

          <Dialog.Backdrop backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content
              rounded="2xl"
              maxH="90vh"
              display="flex"
              flexDirection="column"
              overflow="hidden">
              {/* Dynamic header */}
              {shareWaStep === 'formato' && (
                <Box bg="purple.600" px={6} py={4}>
                  <HStack gap={3}>
                    <Icon as={FaWhatsapp} boxSize={5} color="white" />
                    <Text fontWeight="800" color="white" fontSize="md">Compartir actuaci&oacute;n</Text>
                  </HStack>
                  {act?.nombre && <Text fontSize="sm" color="purple.200" mt={1}>{act.nombre}</Text>}
                </Box>
              )}
              {shareWaStep === 'secciones' && (
                <Box bg={shareWaFormato === 'texto' ? 'whatsapp.600' : 'purple.600'} px={6} py={4}>
                  <HStack gap={3}>
                    <Icon as={shareWaFormato === 'texto' ? FaWhatsapp : FaFilePdf} boxSize={5} color="white" />
                    <Text fontWeight="800" color="white" fontSize="md">Seleccionar secciones</Text>
                  </HStack>
                  <Text fontSize="sm" color={shareWaFormato === 'texto' ? 'whatsapp.100' : 'purple.200'} mt={1}>
                    {shareWaFormato === 'texto' ? 'Mensaje de texto formateado' : 'Informe PDF'}
                  </Text>
                </Box>
              )}
              {shareWaStep === 'preview' && (
                <Box bg="whatsapp.600" px={6} py={4}>
                  <HStack gap={3}>
                    <Icon as={FaWhatsapp} boxSize={5} color="white" />
                    <Text fontWeight="800" color="white" fontSize="md">Vista previa del mensaje</Text>
                  </HStack>
                  <Text fontSize="sm" color="whatsapp.100" mt={1}>Revisa y edita antes de copiar</Text>
                </Box>
              )}

              {/* Step: formato */}
              {shareWaStep === 'formato' && (
                <>
                  <Dialog.Body pt={6} pb={4}>
                    <Text fontSize="sm" color="gray.500" mb={5}>
                      Elige c&oacute;mo quieres compartir la informaci&oacute;n de esta actuaci&oacute;n:
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} gap={4}>
                      <Box
                        as="button"
                        p={5}
                        border="2px solid"
                        borderColor="gray.200"
                        rounded="xl"
                        w="full"
                        textAlign="center"
                        transition="all 0.15s"
                        _hover={{ borderColor: 'whatsapp.400', bg: 'whatsapp.50', transform: 'translateY(-2px)', shadow: 'md' }}
                        onClick={() => handleShareWaFormatoSelect('texto')}
                      >
                        <VStack gap={3}>
                          <Flex bg="whatsapp.100" p={3} rounded="full" align="center" justify="center">
                            <Icon as={FaWhatsapp} boxSize={7} color="whatsapp.600" />
                          </Flex>
                          <VStack gap={1}>
                            <Text fontWeight="700" fontSize="md">Texto formateado</Text>
                            <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                              Mensaje estructurado con emojis y formato profesional para WhatsApp
                            </Text>
                          </VStack>
                        </VStack>
                      </Box>
                      <Box
                        as="button"
                        p={5}
                        border="2px solid"
                        borderColor="gray.200"
                        rounded="xl"
                        w="full"
                        textAlign="center"
                        transition="all 0.15s"
                        _hover={{ borderColor: 'purple.400', bg: 'purple.50', transform: 'translateY(-2px)', shadow: 'md' }}
                        onClick={() => handleShareWaFormatoSelect('pdf')}
                      >
                        <VStack gap={3}>
                          <Flex bg="purple.100" p={3} rounded="full" align="center" justify="center">
                            <Icon as={FaFilePdf} boxSize={7} color="purple.600" />
                          </Flex>
                          <VStack gap={1}>
                            <Text fontWeight="700" fontSize="md">Informe PDF</Text>
                            <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                              Genera el informe completo en formato PDF para compartir
                            </Text>
                          </VStack>
                        </VStack>
                      </Box>
                    </SimpleGrid>
                  </Dialog.Body>
                  <Dialog.Footer>
                    <Button variant="ghost" size="sm" onClick={closeShareWaModal}>Cancelar</Button>
                  </Dialog.Footer>
                </>
              )}

              {/* Step: secciones */}
              {shareWaStep === 'secciones' && (
                <>
                  <Dialog.Body pt={5} pb={4}>
                    <Text fontSize="sm" color="gray.500" mb={4}>
                      Elige qu&eacute; informaci&oacute;n incluir en {shareWaFormato === 'texto' ? 'el mensaje' : 'el PDF'}:
                    </Text>
                    <IsolateCheckboxesFromField>
                    <VStack align="stretch" gap={3}>
                      <Checkbox.Root
                        onCheckedChange={details => setShareWaSecciones(s => ({ ...s, infoBasica: details.checked === true }))}
                        colorPalette="purple"
                        checked={shareWaSecciones.infoBasica}
                      ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                        <HStack gap={2}>
                          <Icon as={FaCalendarAlt} color="gray.400" boxSize={3} />
                          <Text fontSize="sm">Informaci&oacute;n b&aacute;sica (fecha, lugar, uniforme&hellip;)</Text>
                        </HStack>
                      </Checkbox.Label></Checkbox.Root>
                      <Checkbox.Root
                        onCheckedChange={details => setShareWaSecciones(s => ({ ...s, horarios: details.checked === true }))}
                        colorPalette="purple"
                        checked={shareWaSecciones.horarios}
                      ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                        <HStack gap={2}>
                          <Icon as={FaClock} color="gray.400" boxSize={3} />
                          <Text fontSize="sm">Horarios (concentraci&oacute;n, salida, comienzo, fin)</Text>
                        </HStack>
                      </Checkbox.Label></Checkbox.Root>
                      {Boolean(act?.necesidadAutobus) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, autobus: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.autobus}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaBus} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">Informaci&oacute;n del autob&uacute;s</Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      {Boolean(act?.cartelUrl) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, cartel: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.cartel}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaImage} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">
                              Cartel / imagen promocional
                              {shareWaFormato === 'texto' && (
                                <Badge ml={2} colorPalette="purple" fontSize="9px" verticalAlign="middle">incluye imagen</Badge>
                              )}
                            </Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      {Boolean(act?.repertorio?.length) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, repertorio: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.repertorio}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaMusic} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">Repertorio musical</Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      {Boolean(repertorioShare?.shareUrl || act?.enlacePartituras) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, partiturasEnlace: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.partiturasEnlace}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaLink} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">
                              Enlace a partituras
                              {sharePwdPlain && (
                                <Badge ml={2} colorPalette="green" fontSize="9px" verticalAlign="middle">con contraseña</Badge>
                              )}
                            </Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      {Boolean(act?.encuesta) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, asistencia: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.asistencia}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaUsers} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">Resumen de asistencia</Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      {Boolean(act?.plantillaId) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, formacion: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.formacion}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaThList} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">
                              Distribuci&oacute;n de la formaci&oacute;n
                              {shareWaFormato === 'texto' && (
                                <Badge ml={2} colorPalette="purple" fontSize="9px" verticalAlign="middle">incluye imagen</Badge>
                              )}
                            </Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      {Boolean(act?.refuerzos?.length) && (
                        <Checkbox.Root
                          onCheckedChange={details => setShareWaSecciones(s => ({ ...s, refuerzos: details.checked === true }))}
                          colorPalette="purple"
                          checked={shareWaSecciones.refuerzos}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                          <HStack gap={2}>
                            <Icon as={FaUserPlus} color="gray.400" boxSize={3} />
                            <Text fontSize="sm">Refuerzos</Text>
                          </HStack>
                        </Checkbox.Label></Checkbox.Root>
                      )}
                      <Checkbox.Root
                        onCheckedChange={details => setShareWaSecciones(s => ({ ...s, observaciones: details.checked === true }))}
                        colorPalette="purple"
                        checked={shareWaSecciones.observaciones}
                      ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                        <HStack gap={2}>
                          <Icon as={FaEdit} color="gray.400" boxSize={3} />
                          <Text fontSize="sm">Observaciones</Text>
                        </HStack>
                      </Checkbox.Label></Checkbox.Root>
                    </VStack>
                    </IsolateCheckboxesFromField>
                  </Dialog.Body>
                  <Dialog.Footer gap={2}>
                    <Button variant="ghost" size="sm" onClick={() => setShareWaStep('formato')}>
                      Atr&aacute;s
                    </Button>
                    <Button
                      colorPalette={shareWaFormato === 'pdf' ? 'purple' : 'green'}
                      size="sm"
                      rounded="full"
                      loading={shareWaGenerando}
                      loadingText="Preparando\u2026"
                      onClick={handleShareWaSiguiente}><Icon as={shareWaFormato === 'texto' ? FaWhatsapp : FaFilePdf} />{shareWaFormato === 'texto' ? 'Vista previa' : 'Generar PDF'}</Button>
                  </Dialog.Footer>
                </>
              )}

              {/* Step: preview (texto only) */}
              {shareWaStep === 'preview' && (
                <>
                  <Dialog.Body pt={4} pb={3} flex="1" minH={0} overflowY="auto">
                    {(shareWaCartelImgUrl || shareWaFormacionImgUrl) && (
                      <SimpleGrid columns={{ base: 1, sm: 2 }} gap={3} mb={4}>
                        {shareWaCartelImgUrl && (
                          <Box border="1px solid" borderColor="purple.200" rounded="lg" overflow="hidden" bg="purple.50">
                            <HStack px={2} py={1} justify="space-between" bg="purple.100">
                              <Text fontSize="2xs" fontWeight="700" color="purple.700" textTransform="uppercase">
                                Cartel
                              </Text>
                              <Tooltip content="Copiar cartel" showArrow>
                                <IconButton
                                  size="xs"
                                  variant="ghost"
                                  colorPalette="purple"
                                  aria-label="Copiar cartel"
                                  onClick={async () => {
                                    try {
                                      await copyPreviewImgToClipboard({
                                        imgRef: shareWaCartelImgRef,
                                        blob: shareWaCartelBlob,
                                        url: shareWaCartelImgUrl,
                                      });
                                      toast({ title: 'Cartel copiado', status: 'success', duration: 2000 });
                                    } catch (err) {
                                      console.warn('copy cartel failed:', err);
                                      toast({ title: 'No se pudo copiar el cartel', status: 'warning', duration: 2000 });
                                    }
                                  }}><FaCopy /></IconButton>
                              </Tooltip>
                            </HStack>
                            <Flex h="88px" align="center" justify="center" p={2}>
                              <Box
                                as="img"
                                ref={shareWaCartelImgRef}
                                src={shareWaCartelImgUrl}
                                alt="Cartel"
                                maxH="80px"
                                maxW="100%"
                                objectFit="contain"
                                display="block"
                              />
                            </Flex>
                          </Box>
                        )}
                        {shareWaFormacionImgUrl && (
                          <Box border="1px solid" borderColor="purple.200" rounded="lg" overflow="hidden" bg="purple.50">
                            <HStack px={2} py={1} justify="space-between" bg="purple.100">
                              <Text fontSize="2xs" fontWeight="700" color="purple.700" textTransform="uppercase">
                                Formaci&oacute;n
                              </Text>
                              <HStack gap={1}>
                                <Tooltip content="Copiar formación" showArrow>
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="purple"
                                    aria-label="Copiar formación"
                                    onClick={async () => {
                                      try {
                                        await copyPreviewImgToClipboard({
                                          imgRef: shareWaFormacionImgRef,
                                          blob: shareWaFormacionBlob,
                                          url: shareWaFormacionImgUrl,
                                        });
                                        toast({ title: 'Imagen copiada', status: 'success', duration: 2000 });
                                      } catch (err) {
                                        console.warn('copy formacion failed:', err);
                                        toast({ title: 'No se pudo copiar la imagen', status: 'warning', duration: 2000 });
                                      }
                                    }}><FaCopy /></IconButton>
                                </Tooltip>
                                <Tooltip content="Descargar PNG" showArrow>
                                  <IconButton
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="purple"
                                    aria-label="Descargar formación"
                                    onClick={() => {
                                      const a = document.createElement('a');
                                      a.href = shareWaFormacionImgUrl;
                                      a.download = 'formacion-cadencya.png';
                                      a.click();
                                    }}><FaImage /></IconButton>
                                </Tooltip>
                              </HStack>
                            </HStack>
                            <Flex h="88px" align="center" justify="center" p={2}>
                              <Box
                                as="img"
                                ref={shareWaFormacionImgRef}
                                src={shareWaFormacionImgUrl}
                                alt="Formación"
                                maxH="80px"
                                maxW="100%"
                                objectFit="contain"
                                display="block"
                              />
                            </Flex>
                          </Box>
                        )}
                      </SimpleGrid>
                    )}
                    <HStack mb={2} justify="space-between" align="center">
                      <Text fontSize="xs" fontWeight="700" color="gray.500" letterSpacing="wider" textTransform="uppercase">
                        Mensaje
                      </Text>
                      <Tooltip content="Copiar solo el texto" showArrow>
                        <IconButton
                          size="xs"
                          variant="outline"
                          colorPalette="purple"
                          aria-label="Copiar mensaje"
                          onClick={() => {
                            navigator.clipboard.writeText(shareWaMensaje)
                              .then(() => toast({ title: 'Texto copiado', status: 'success', duration: 2000 }))
                              .catch(() => toast({ title: 'No se pudo copiar', status: 'warning', duration: 2000 }));
                          }}><FaCopy /></IconButton>
                      </Tooltip>
                    </HStack>
                    <Textarea
                      value={shareWaMensaje}
                      onChange={e => { setShareWaMensaje(e.target.value); setShareWaCopied(false); setShareWaCopyNotice(null); }}
                      rows={7}
                      resize="vertical"
                      fontSize="sm"
                      rounded="xl"
                      bg="gray.50"
                      _dark={{ bg: 'gray.700' }}
                      borderColor="gray.200"
                      maxH="180px"
                      _focus={{ borderColor: 'whatsapp.400', boxShadow: '0 0 0 1px var(--chakra-colors-whatsapp-400)' }}
                    />
                    {shareWaCopyNotice && (
                      <Alert.Root status={shareWaCopyNotice.status} variant="subtle" rounded="lg" mt={3} py={2}>
                        <Alert.Indicator boxSize={4} />
                        <Box>
                          <Alert.Title fontSize="sm">{shareWaCopyNotice.title}</Alert.Title>
                          <Alert.Description fontSize="xs">{shareWaCopyNotice.description}</Alert.Description>
                        </Box>
                      </Alert.Root>
                    )}
                    {(shareWaCartelImgUrl || shareWaFormacionImgUrl) && (
                      <Text fontSize="xs" color="gray.500" mt={3}>
                        WhatsApp se abre con el texto. El cartel y la formación se pegan o adjuntan en el chat.
                      </Text>
                    )}
                  </Dialog.Body>
                  <Dialog.Footer
                    gap={2}
                    flexWrap="wrap"
                    flexShrink={0}
                    borderTop="1px solid"
                    borderColor="gray.100"
                    _dark={{ borderColor: 'gray.700' }}
                    bg={cardBg}
                  >
                    <Button variant="ghost" size="sm" onClick={() => {
                      if (shareWaCartelImgUrl) URL.revokeObjectURL(shareWaCartelImgUrl);
                      setShareWaCartelImgUrl(null);
                      setShareWaCartelBlob(null);
                      setShareWaFormacionImgUrl(null);
                      setShareWaFormacionBlob(null);
                      setShareWaCopied(false);
                      setShareWaCopyNotice(null);
                      setShareWaStep('secciones');
                    }}>
                      Atr&aacute;s
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      rounded="full"
                      disabled={!shareWaMensaje.trim()}
                      onClick={handleShareWaCopiar}><FaCopy />{shareWaCopied ? 'Copiado' : 'Copiar'}</Button>
                    <Button
                      colorPalette="green"
                      size="sm"
                      rounded="full"
                      shadow="sm"
                      disabled={!shareWaMensaje.trim()}
                      onClick={() => openWhatsappChat(null, shareWaMensaje)}
                    ><FaWhatsapp />Abrir WhatsApp</Button>
                  </Dialog.Footer>
                </>
              )}
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Modal de WhatsApp ── */}
      <Dialog.Root open={isWaOpen} size='md' placement='center' onOpenChange={e => {
        if (!e.open) {
          onWaClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop backdropFilter="blur(4px)" />
          <Dialog.Positioner>
            <Dialog.Content rounded="2xl" overflow="hidden">
              {/* Cabecera verde WhatsApp */}
              <Box bg="whatsapp.500" px={6} py={4} _dark={{ bg: 'whatsapp.700' }}>
                <HStack gap={3}>
                  <Icon as={FaWhatsapp} boxSize={6} color="white" />
                  <Box>
                    <Text fontWeight="800" color="white" fontSize="md">
                      Mensaje de WhatsApp
                    </Text>
                    {waTarget?.contacto && (
                      <Text fontSize="sm" color="whatsapp.100">
                        Para: {[waTarget.contacto.nombre, waTarget.contacto.apellidos].filter(Boolean).join(' ')}
                        {waTarget.contacto.telefono ? ` · ${waTarget.contacto.telefono}` : ''}
                      </Text>
                    )}
                  </Box>
                </HStack>
              </Box>

              <Dialog.Body pt={5} pb={2}>
                <Text fontSize="xs" color="gray.500" mb={2}>
                  Edita el mensaje antes de enviarlo. Se abrirá WhatsApp con este texto pre-rellenado.
                </Text>
                <Textarea
                  value={waMensaje}
                  onChange={e => setWaMensaje(e.target.value)}
                  rows={9}
                  resize="vertical"
                  fontSize="sm"
                  rounded="xl"
                  bg="gray.50"
                  _dark={{ bg: 'gray.700' }}
                  borderColor="gray.200"
                  _focus={{ borderColor: 'whatsapp.400', boxShadow: '0 0 0 1px var(--chakra-colors-whatsapp-400)' }}
                />
              </Dialog.Body>

              <Dialog.Footer gap={2}>
                <Button variant="ghost" size="sm" onClick={onWaClose}>Cancelar</Button>
                <Button
                  colorPalette="whatsapp"
                  size="sm"
                  rounded="full"
                  shadow="sm"
                  disabled={!waMensaje.trim()}
                  onClick={handleEnviarWa}><FaWhatsapp />Abrir WhatsApp
                              </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <PasarListaModal
        isOpen={isPasarListaOpen}
        onClose={onPasarListaClose}
        title="Pasar lista"
        items={(encuesta?.respuestas || []).map((r) => ({
          id: r.id,
          name: `${r.musico?.nombre || ''} ${r.musico?.apellidos || ''}`.trim() || 'Músico',
          subtitle: formatMusicoInstrumentosText(r.musico) || undefined,
          foto: r.musico?.foto || null,
          estado: respuestaEstadoEffective(r),
        }))}
        statuses={PASAR_LISTA_ACTUACION_STATUSES}
        onMark={handlePasarListaMark}
      />
    </Box>
  );
}
